class FluentType {
  /**
   * Create a `FluentType` instance.
   *
   * @param value The JavaScript value to wrap.
   */
  constructor(value) {
    this.value = value;
  }
  /**
   * Unwrap the raw value stored by this `FluentType`.
   */
  valueOf() {
    return this.value;
  }
}
class FluentNone extends FluentType {
  /**
   * Create an instance of `FluentNone` with an optional fallback value.
   * @param value The fallback value of this `FluentNone`.
   */
  constructor(value = "???") {
    super(value);
  }
  /**
   * Format this `FluentNone` to the fallback string.
   */
  toString(scope) {
    return `{${this.value}}`;
  }
}
class FluentNumber extends FluentType {
  /**
   * Create an instance of `FluentNumber` with options to the
   * `Intl.NumberFormat` constructor.
   *
   * @param value The number value of this `FluentNumber`.
   * @param opts Options which will be passed to `Intl.NumberFormat`.
   */
  constructor(value, opts = {}) {
    super(value);
    this.opts = opts;
  }
  /**
   * Format this `FluentNumber` to a string.
   */
  toString(scope) {
    if (scope) {
      try {
        const nf = scope.memoizeIntlObject(Intl.NumberFormat, this.opts);
        return nf.format(this.value);
      } catch (err) {
        scope.reportError(err);
      }
    }
    return this.value.toString(10);
  }
}
class FluentDateTime extends FluentType {
  static supportsValue(value) {
    if (typeof value === "number")
      return true;
    if (value instanceof Date)
      return true;
    if (value instanceof FluentType)
      return FluentDateTime.supportsValue(value.valueOf());
    if ("Temporal" in globalThis) {
      const _Temporal = globalThis.Temporal;
      if (value instanceof _Temporal.Instant || value instanceof _Temporal.PlainDateTime || value instanceof _Temporal.PlainDate || value instanceof _Temporal.PlainMonthDay || value instanceof _Temporal.PlainTime || value instanceof _Temporal.PlainYearMonth) {
        return true;
      }
    }
    return false;
  }
  /**
   * Create an instance of `FluentDateTime` with options to the
   * `Intl.DateTimeFormat` constructor.
   *
   * @param value The number value of this `FluentDateTime`, in milliseconds.
   * @param opts Options which will be passed to `Intl.DateTimeFormat`.
   */
  constructor(value, opts = {}) {
    if (value instanceof FluentDateTime) {
      opts = { ...value.opts, ...opts };
      value = value.value;
    } else if (value instanceof FluentType) {
      value = value.valueOf();
    }
    if (typeof value === "object" && "calendarId" in value && opts.calendar === void 0) {
      opts = { ...opts, calendar: value.calendarId };
    }
    super(value);
    this.opts = opts;
  }
  [Symbol.toPrimitive](hint) {
    return hint === "string" ? this.toString() : this.toNumber();
  }
  /**
   * Convert this `FluentDateTime` to a number.
   * Note that this isn't always possible due to the nature of Temporal objects.
   * In such cases, a TypeError will be thrown.
   */
  toNumber() {
    const value = this.value;
    if (typeof value === "number")
      return value;
    if (value instanceof Date)
      return value.getTime();
    if ("epochMilliseconds" in value) {
      return value.epochMilliseconds;
    }
    if ("toZonedDateTime" in value) {
      return value.toZonedDateTime("UTC").epochMilliseconds;
    }
    throw new TypeError("Unwrapping a non-number value as a number");
  }
  /**
   * Format this `FluentDateTime` to a string.
   */
  toString(scope) {
    if (scope) {
      try {
        const dtf = scope.memoizeIntlObject(Intl.DateTimeFormat, this.opts);
        return dtf.format(this.value);
      } catch (err) {
        scope.reportError(err);
      }
    }
    if (typeof this.value === "number" || this.value instanceof Date) {
      return new Date(this.value).toISOString();
    }
    return this.value.toString();
  }
}
const MAX_PLACEABLES = 100;
const FSI = "⁨";
const PDI = "⁩";
function match(scope, selector, key) {
  if (key === selector) {
    return true;
  }
  if (key instanceof FluentNumber && selector instanceof FluentNumber && key.value === selector.value) {
    return true;
  }
  if (selector instanceof FluentNumber && typeof key === "string") {
    let category = scope.memoizeIntlObject(Intl.PluralRules, selector.opts).select(selector.value);
    if (key === category) {
      return true;
    }
  }
  return false;
}
function getDefault(scope, variants, star) {
  if (variants[star]) {
    return resolvePattern(scope, variants[star].value);
  }
  scope.reportError(new RangeError("No default"));
  return new FluentNone();
}
function getArguments(scope, args) {
  const positional = [];
  const named = /* @__PURE__ */ Object.create(null);
  for (const arg of args) {
    if (arg.type === "narg") {
      named[arg.name] = resolveExpression(scope, arg.value);
    } else {
      positional.push(resolveExpression(scope, arg));
    }
  }
  return { positional, named };
}
function resolveExpression(scope, expr) {
  switch (expr.type) {
    case "str":
      return expr.value;
    case "num":
      return new FluentNumber(expr.value, {
        minimumFractionDigits: expr.precision
      });
    case "var":
      return resolveVariableReference(scope, expr);
    case "mesg":
      return resolveMessageReference(scope, expr);
    case "term":
      return resolveTermReference(scope, expr);
    case "func":
      return resolveFunctionReference(scope, expr);
    case "select":
      return resolveSelectExpression(scope, expr);
    default:
      return new FluentNone();
  }
}
function resolveVariableReference(scope, { name }) {
  let arg;
  if (scope.params) {
    if (Object.prototype.hasOwnProperty.call(scope.params, name)) {
      arg = scope.params[name];
    } else {
      return new FluentNone(`$${name}`);
    }
  } else if (scope.args && Object.prototype.hasOwnProperty.call(scope.args, name)) {
    arg = scope.args[name];
  } else {
    scope.reportError(new ReferenceError(`Unknown variable: $${name}`));
    return new FluentNone(`$${name}`);
  }
  if (arg instanceof FluentType) {
    return arg;
  }
  switch (typeof arg) {
    case "string":
      return arg;
    case "number":
      return new FluentNumber(arg);
    case "object":
      if (FluentDateTime.supportsValue(arg)) {
        return new FluentDateTime(arg);
      }
    // eslint-disable-next-line no-fallthrough
    default:
      scope.reportError(new TypeError(`Variable type not supported: $${name}, ${typeof arg}`));
      return new FluentNone(`$${name}`);
  }
}
function resolveMessageReference(scope, { name, attr }) {
  const message = scope.bundle._messages.get(name);
  if (!message) {
    scope.reportError(new ReferenceError(`Unknown message: ${name}`));
    return new FluentNone(name);
  }
  if (attr) {
    const attribute = message.attributes[attr];
    if (attribute) {
      return resolvePattern(scope, attribute);
    }
    scope.reportError(new ReferenceError(`Unknown attribute: ${attr}`));
    return new FluentNone(`${name}.${attr}`);
  }
  if (message.value) {
    return resolvePattern(scope, message.value);
  }
  scope.reportError(new ReferenceError(`No value: ${name}`));
  return new FluentNone(name);
}
function resolveTermReference(scope, { name, attr, args }) {
  const id = `-${name}`;
  const term = scope.bundle._terms.get(id);
  if (!term) {
    scope.reportError(new ReferenceError(`Unknown term: ${id}`));
    return new FluentNone(id);
  }
  if (attr) {
    const attribute = term.attributes[attr];
    if (attribute) {
      scope.params = getArguments(scope, args).named;
      const resolved2 = resolvePattern(scope, attribute);
      scope.params = null;
      return resolved2;
    }
    scope.reportError(new ReferenceError(`Unknown attribute: ${attr}`));
    return new FluentNone(`${id}.${attr}`);
  }
  scope.params = getArguments(scope, args).named;
  const resolved = resolvePattern(scope, term.value);
  scope.params = null;
  return resolved;
}
function resolveFunctionReference(scope, { name, args }) {
  let func = scope.bundle._functions[name];
  if (!func) {
    scope.reportError(new ReferenceError(`Unknown function: ${name}()`));
    return new FluentNone(`${name}()`);
  }
  if (typeof func !== "function") {
    scope.reportError(new TypeError(`Function ${name}() is not callable`));
    return new FluentNone(`${name}()`);
  }
  try {
    let resolved = getArguments(scope, args);
    return func(resolved.positional, resolved.named);
  } catch (err) {
    scope.reportError(err);
    return new FluentNone(`${name}()`);
  }
}
function resolveSelectExpression(scope, { selector, variants, star }) {
  let sel = resolveExpression(scope, selector);
  if (sel instanceof FluentNone) {
    return getDefault(scope, variants, star);
  }
  for (const variant of variants) {
    const key = resolveExpression(scope, variant.key);
    if (match(scope, sel, key)) {
      return resolvePattern(scope, variant.value);
    }
  }
  return getDefault(scope, variants, star);
}
function resolveComplexPattern(scope, ptn) {
  if (scope.dirty.has(ptn)) {
    scope.reportError(new RangeError("Cyclic reference"));
    return new FluentNone();
  }
  scope.dirty.add(ptn);
  const result = [];
  const useIsolating = scope.bundle._useIsolating && ptn.length > 1;
  for (const elem of ptn) {
    if (typeof elem === "string") {
      result.push(scope.bundle._transform(elem));
      continue;
    }
    scope.placeables++;
    if (scope.placeables > MAX_PLACEABLES) {
      scope.dirty.delete(ptn);
      throw new RangeError(`Too many placeables expanded: ${scope.placeables}, max allowed is ${MAX_PLACEABLES}`);
    }
    if (useIsolating) {
      result.push(FSI);
    }
    result.push(resolveExpression(scope, elem).toString(scope));
    if (useIsolating) {
      result.push(PDI);
    }
  }
  scope.dirty.delete(ptn);
  return result.join("");
}
function resolvePattern(scope, value) {
  if (typeof value === "string") {
    return scope.bundle._transform(value);
  }
  return resolveComplexPattern(scope, value);
}
class Scope {
  constructor(bundle, errors, args) {
    this.dirty = /* @__PURE__ */ new WeakSet();
    this.params = null;
    this.placeables = 0;
    this.bundle = bundle;
    this.errors = errors;
    this.args = args;
  }
  reportError(error) {
    if (!this.errors || !(error instanceof Error)) {
      throw error;
    }
    this.errors.push(error);
  }
  memoizeIntlObject(ctor, opts) {
    let cache2 = this.bundle._intls.get(ctor);
    if (!cache2) {
      cache2 = {};
      this.bundle._intls.set(ctor, cache2);
    }
    let id = JSON.stringify(opts);
    if (!cache2[id]) {
      cache2[id] = new ctor(this.bundle.locales, opts);
    }
    return cache2[id];
  }
}
function values(opts, allowed) {
  const unwrapped = /* @__PURE__ */ Object.create(null);
  for (const [name, opt] of Object.entries(opts)) {
    if (allowed.includes(name)) {
      unwrapped[name] = opt.valueOf();
    }
  }
  return unwrapped;
}
const NUMBER_ALLOWED = [
  "unitDisplay",
  "currencyDisplay",
  "useGrouping",
  "minimumIntegerDigits",
  "minimumFractionDigits",
  "maximumFractionDigits",
  "minimumSignificantDigits",
  "maximumSignificantDigits"
];
function NUMBER(args, opts) {
  let arg = args[0];
  if (arg instanceof FluentNone) {
    return new FluentNone(`NUMBER(${arg.valueOf()})`);
  }
  if (arg instanceof FluentNumber) {
    return new FluentNumber(arg.valueOf(), {
      ...arg.opts,
      ...values(opts, NUMBER_ALLOWED)
    });
  }
  if (arg instanceof FluentDateTime) {
    return new FluentNumber(arg.toNumber(), {
      ...values(opts, NUMBER_ALLOWED)
    });
  }
  throw new TypeError("Invalid argument to NUMBER");
}
const DATETIME_ALLOWED = [
  "dateStyle",
  "timeStyle",
  "fractionalSecondDigits",
  "dayPeriod",
  "hour12",
  "weekday",
  "era",
  "year",
  "month",
  "day",
  "hour",
  "minute",
  "second",
  "timeZoneName"
];
function DATETIME(args, opts) {
  let arg = args[0];
  if (arg instanceof FluentNone) {
    return new FluentNone(`DATETIME(${arg.valueOf()})`);
  }
  if (arg instanceof FluentDateTime || arg instanceof FluentNumber) {
    return new FluentDateTime(arg, values(opts, DATETIME_ALLOWED));
  }
  throw new TypeError("Invalid argument to DATETIME");
}
const cache = /* @__PURE__ */ new Map();
function getMemoizerForLocale(locales) {
  const stringLocale = Array.isArray(locales) ? locales.join(" ") : locales;
  let memoizer = cache.get(stringLocale);
  if (memoizer === void 0) {
    memoizer = /* @__PURE__ */ new Map();
    cache.set(stringLocale, memoizer);
  }
  return memoizer;
}
class FluentBundle {
  /**
   * Create an instance of `FluentBundle`.
   *
   * @example
   * ```js
   * let bundle = new FluentBundle(["en-US", "en"]);
   *
   * let bundle = new FluentBundle(locales, {useIsolating: false});
   *
   * let bundle = new FluentBundle(locales, {
   *   useIsolating: true,
   *   functions: {
   *     NODE_ENV: () => process.env.NODE_ENV
   *   }
   * });
   * ```
   *
   * @param locales - Used to instantiate `Intl` formatters used by translations.
   * @param options - Optional configuration for the bundle.
   */
  constructor(locales, { functions, useIsolating = true, transform = (v) => v } = {}) {
    this._terms = /* @__PURE__ */ new Map();
    this._messages = /* @__PURE__ */ new Map();
    this.locales = Array.isArray(locales) ? locales : [locales];
    this._functions = {
      NUMBER,
      DATETIME,
      ...functions
    };
    this._useIsolating = useIsolating;
    this._transform = transform;
    this._intls = getMemoizerForLocale(locales);
  }
  /**
   * Check if a message is present in the bundle.
   *
   * @param id - The identifier of the message to check.
   */
  hasMessage(id) {
    return this._messages.has(id);
  }
  /**
   * Return a raw unformatted message object from the bundle.
   *
   * Raw messages are `{value, attributes}` shapes containing translation units
   * called `Patterns`. `Patterns` are implementation-specific; they should be
   * treated as black boxes and formatted with `FluentBundle.formatPattern`.
   *
   * @param id - The identifier of the message to check.
   */
  getMessage(id) {
    return this._messages.get(id);
  }
  /**
   * Add a translation resource to the bundle.
   *
   * @example
   * ```js
   * let res = new FluentResource("foo = Foo");
   * bundle.addResource(res);
   * bundle.getMessage("foo");
   * // → {value: .., attributes: {..}}
   * ```
   *
   * @param res
   * @param options
   */
  addResource(res, { allowOverrides = false } = {}) {
    const errors = [];
    for (let i = 0; i < res.body.length; i++) {
      let entry = res.body[i];
      if (entry.id.startsWith("-")) {
        if (allowOverrides === false && this._terms.has(entry.id)) {
          errors.push(new Error(`Attempt to override an existing term: "${entry.id}"`));
          continue;
        }
        this._terms.set(entry.id, entry);
      } else {
        if (allowOverrides === false && this._messages.has(entry.id)) {
          errors.push(new Error(`Attempt to override an existing message: "${entry.id}"`));
          continue;
        }
        this._messages.set(entry.id, entry);
      }
    }
    return errors;
  }
  /**
   * Format a `Pattern` to a string.
   *
   * Format a raw `Pattern` into a string. `args` will be used to resolve
   * references to variables passed as arguments to the translation.
   *
   * In case of errors `formatPattern` will try to salvage as much of the
   * translation as possible and will still return a string. For performance
   * reasons, the encountered errors are not returned but instead are appended
   * to the `errors` array passed as the third argument.
   *
   * If `errors` is omitted, the first encountered error will be thrown.
   *
   * @example
   * ```js
   * let errors = [];
   * bundle.addResource(
   *     new FluentResource("hello = Hello, {$name}!"));
   *
   * let hello = bundle.getMessage("hello");
   * if (hello.value) {
   *     bundle.formatPattern(hello.value, {name: "Jane"}, errors);
   *     // Returns "Hello, Jane!" and `errors` is empty.
   *
   *     bundle.formatPattern(hello.value, undefined, errors);
   *     // Returns "Hello, {$name}!" and `errors` is now:
   *     // [<ReferenceError: Unknown variable: name>]
   * }
   * ```
   */
  formatPattern(pattern, args = null, errors = null) {
    if (typeof pattern === "string") {
      return this._transform(pattern);
    }
    let scope = new Scope(this, errors, args);
    try {
      let value = resolveComplexPattern(scope, pattern);
      return value.toString(scope);
    } catch (err) {
      if (scope.errors && err instanceof Error) {
        scope.errors.push(err);
        return new FluentNone().toString(scope);
      }
      throw err;
    }
  }
}
const RE_MESSAGE_START = /^(-?[a-zA-Z][\w-]*) *= */gm;
const RE_ATTRIBUTE_START = /\.([a-zA-Z][\w-]*) *= */y;
const RE_VARIANT_START = /\*?\[/y;
const RE_NUMBER_LITERAL = /(-?[0-9]+(?:\.([0-9]+))?)/y;
const RE_IDENTIFIER = /([a-zA-Z][\w-]*)/y;
const RE_REFERENCE = /([$-])?([a-zA-Z][\w-]*)(?:\.([a-zA-Z][\w-]*))?/y;
const RE_FUNCTION_NAME = /^[A-Z][A-Z0-9_-]*$/;
const RE_TEXT_RUN = /([^{}\n\r]+)/y;
const RE_STRING_RUN = /([^\\"\n\r]*)/y;
const RE_STRING_ESCAPE = /\\([\\"])/y;
const RE_UNICODE_ESCAPE = /\\u([a-fA-F0-9]{4})|\\U([a-fA-F0-9]{6})/y;
const RE_LEADING_NEWLINES = /^\n+/;
const RE_TRAILING_SPACES = / +$/;
const RE_BLANK_LINES = / *\r?\n/g;
const RE_INDENT = /( *)$/;
const TOKEN_BRACE_OPEN = /{\s*/y;
const TOKEN_BRACE_CLOSE = /\s*}/y;
const TOKEN_BRACKET_OPEN = /\[\s*/y;
const TOKEN_BRACKET_CLOSE = /\s*] */y;
const TOKEN_PAREN_OPEN = /\s*\(\s*/y;
const TOKEN_ARROW = /\s*->\s*/y;
const TOKEN_COLON = /\s*:\s*/y;
const TOKEN_COMMA = /\s*,?\s*/y;
const TOKEN_BLANK = /\s+/y;
class FluentResource {
  constructor(source) {
    this.body = [];
    RE_MESSAGE_START.lastIndex = 0;
    let cursor = 0;
    while (true) {
      let next = RE_MESSAGE_START.exec(source);
      if (next === null) {
        break;
      }
      cursor = RE_MESSAGE_START.lastIndex;
      try {
        this.body.push(parseMessage(next[1]));
      } catch (err) {
        if (err instanceof SyntaxError) {
          continue;
        }
        throw err;
      }
    }
    function test(re) {
      re.lastIndex = cursor;
      return re.test(source);
    }
    function consumeChar(char, errorClass) {
      if (source[cursor] === char) {
        cursor++;
        return true;
      }
      if (errorClass) {
        throw new errorClass(`Expected ${char}`);
      }
      return false;
    }
    function consumeToken(re, errorClass) {
      if (test(re)) {
        cursor = re.lastIndex;
        return true;
      }
      if (errorClass) {
        throw new errorClass(`Expected ${re.toString()}`);
      }
      return false;
    }
    function match2(re) {
      re.lastIndex = cursor;
      let result = re.exec(source);
      if (result === null) {
        throw new SyntaxError(`Expected ${re.toString()}`);
      }
      cursor = re.lastIndex;
      return result;
    }
    function match1(re) {
      return match2(re)[1];
    }
    function parseMessage(id) {
      let value = parsePattern();
      let attributes = parseAttributes();
      if (value === null && Object.keys(attributes).length === 0) {
        throw new SyntaxError("Expected message value or attributes");
      }
      return { id, value, attributes };
    }
    function parseAttributes() {
      let attrs = /* @__PURE__ */ Object.create(null);
      while (test(RE_ATTRIBUTE_START)) {
        let name = match1(RE_ATTRIBUTE_START);
        let value = parsePattern();
        if (value === null) {
          throw new SyntaxError("Expected attribute value");
        }
        attrs[name] = value;
      }
      return attrs;
    }
    function parsePattern() {
      let first;
      if (test(RE_TEXT_RUN)) {
        first = match1(RE_TEXT_RUN);
      }
      if (source[cursor] === "{" || source[cursor] === "}") {
        return parsePatternElements(first ? [first] : [], Infinity);
      }
      let indent = parseIndent();
      if (indent) {
        if (first) {
          return parsePatternElements([first, indent], indent.length);
        }
        indent.value = trim(indent.value, RE_LEADING_NEWLINES);
        return parsePatternElements([indent], indent.length);
      }
      if (first) {
        return trim(first, RE_TRAILING_SPACES);
      }
      return null;
    }
    function parsePatternElements(elements = [], commonIndent) {
      while (true) {
        if (test(RE_TEXT_RUN)) {
          elements.push(match1(RE_TEXT_RUN));
          continue;
        }
        if (source[cursor] === "{") {
          elements.push(parsePlaceable());
          continue;
        }
        if (source[cursor] === "}") {
          throw new SyntaxError("Unbalanced closing brace");
        }
        let indent = parseIndent();
        if (indent) {
          elements.push(indent);
          commonIndent = Math.min(commonIndent, indent.length);
          continue;
        }
        break;
      }
      let lastIndex = elements.length - 1;
      let lastElement = elements[lastIndex];
      if (typeof lastElement === "string") {
        elements[lastIndex] = trim(lastElement, RE_TRAILING_SPACES);
      }
      let baked = [];
      for (let element of elements) {
        if (element instanceof Indent) {
          element = element.value.slice(0, element.value.length - commonIndent);
        }
        if (element) {
          baked.push(element);
        }
      }
      return baked;
    }
    function parsePlaceable() {
      consumeToken(TOKEN_BRACE_OPEN, SyntaxError);
      let selector = parseInlineExpression();
      if (consumeToken(TOKEN_BRACE_CLOSE)) {
        return selector;
      }
      if (consumeToken(TOKEN_ARROW)) {
        let variants = parseVariants();
        consumeToken(TOKEN_BRACE_CLOSE, SyntaxError);
        return {
          type: "select",
          selector,
          ...variants
        };
      }
      throw new SyntaxError("Unclosed placeable");
    }
    function parseInlineExpression() {
      if (source[cursor] === "{") {
        return parsePlaceable();
      }
      if (test(RE_REFERENCE)) {
        let [, sigil, name, attr = null] = match2(RE_REFERENCE);
        if (sigil === "$") {
          return { type: "var", name };
        }
        if (consumeToken(TOKEN_PAREN_OPEN)) {
          let args = parseArguments();
          if (sigil === "-") {
            return { type: "term", name, attr, args };
          }
          if (RE_FUNCTION_NAME.test(name)) {
            return { type: "func", name, args };
          }
          throw new SyntaxError("Function names must be all upper-case");
        }
        if (sigil === "-") {
          return {
            type: "term",
            name,
            attr,
            args: []
          };
        }
        return { type: "mesg", name, attr };
      }
      return parseLiteral();
    }
    function parseArguments() {
      let args = [];
      while (true) {
        switch (source[cursor]) {
          case ")":
            cursor++;
            return args;
          case void 0:
            throw new SyntaxError("Unclosed argument list");
        }
        args.push(parseArgument());
        consumeToken(TOKEN_COMMA);
      }
    }
    function parseArgument() {
      let expr = parseInlineExpression();
      if (expr.type !== "mesg") {
        return expr;
      }
      if (consumeToken(TOKEN_COLON)) {
        return {
          type: "narg",
          name: expr.name,
          value: parseLiteral()
        };
      }
      return expr;
    }
    function parseVariants() {
      let variants = [];
      let count = 0;
      let star;
      while (test(RE_VARIANT_START)) {
        if (consumeChar("*")) {
          star = count;
        }
        let key = parseVariantKey();
        let value = parsePattern();
        if (value === null) {
          throw new SyntaxError("Expected variant value");
        }
        variants[count++] = { key, value };
      }
      if (count === 0) {
        return null;
      }
      if (star === void 0) {
        throw new SyntaxError("Expected default variant");
      }
      return { variants, star };
    }
    function parseVariantKey() {
      consumeToken(TOKEN_BRACKET_OPEN, SyntaxError);
      let key;
      if (test(RE_NUMBER_LITERAL)) {
        key = parseNumberLiteral();
      } else {
        key = {
          type: "str",
          value: match1(RE_IDENTIFIER)
        };
      }
      consumeToken(TOKEN_BRACKET_CLOSE, SyntaxError);
      return key;
    }
    function parseLiteral() {
      if (test(RE_NUMBER_LITERAL)) {
        return parseNumberLiteral();
      }
      if (source[cursor] === '"') {
        return parseStringLiteral();
      }
      throw new SyntaxError("Invalid expression");
    }
    function parseNumberLiteral() {
      let [, value, fraction = ""] = match2(RE_NUMBER_LITERAL);
      let precision = fraction.length;
      return {
        type: "num",
        value: parseFloat(value),
        precision
      };
    }
    function parseStringLiteral() {
      consumeChar('"', SyntaxError);
      let value = "";
      while (true) {
        value += match1(RE_STRING_RUN);
        if (source[cursor] === "\\") {
          value += parseEscapeSequence();
          continue;
        }
        if (consumeChar('"')) {
          return { type: "str", value };
        }
        throw new SyntaxError("Unclosed string literal");
      }
    }
    function parseEscapeSequence() {
      if (test(RE_STRING_ESCAPE)) {
        return match1(RE_STRING_ESCAPE);
      }
      if (test(RE_UNICODE_ESCAPE)) {
        let [, codepoint4, codepoint6] = match2(RE_UNICODE_ESCAPE);
        let codepoint = parseInt(codepoint4 || codepoint6, 16);
        return codepoint <= 55295 || 57344 <= codepoint ? (
          // It's a Unicode scalar value.
          String.fromCodePoint(codepoint)
        ) : (
          // Lonely surrogates can cause trouble when the parsing result is
          // saved using UTF-8. Use U+FFFD REPLACEMENT CHARACTER instead.
          "�"
        );
      }
      throw new SyntaxError("Unknown escape sequence");
    }
    function parseIndent() {
      let start = cursor;
      consumeToken(TOKEN_BLANK);
      switch (source[cursor]) {
        case ".":
        case "[":
        case "*":
        case "}":
        case void 0:
          return false;
        case "{":
          return makeIndent(source.slice(start, cursor));
      }
      if (source[cursor - 1] === " ") {
        return makeIndent(source.slice(start, cursor));
      }
      return false;
    }
    function trim(text, re) {
      return text.replace(re, "");
    }
    function makeIndent(blank) {
      let value = blank.replace(RE_BLANK_LINES, "\n");
      let length = RE_INDENT.exec(blank)[1].length;
      return new Indent(value, length);
    }
  }
}
class Indent {
  constructor(value, length) {
    this.value = value;
    this.length = length;
  }
}
const chrome = "# Viewer chrome: buttons, panel headers, and other UI the reader interacts\n# with. Rendered on the main thread and selected by `uiLocale`.\n#\n# Message ids are lower-kebab-case Fluent identifiers, optionally with a\n# single `.attribute` suffix (`submit-button`, `answer-status.correct`).\n#\n# This catalog is the source of truth for every other locale: `lint:i18n`\n# rejects a translation that defines a key missing here. Run\n# `npm run codegen -w @doenet/i18n` after editing.\n\n\n## Answer submission — the check-work button and the status it reports.\n\nanswer-checking = Checking...\nanswer-submitting = Submitting...\n\n# Announced to a screen reader while the submission is in flight. Separate\n# from the button's own text, which is abbreviated.\nanswer-checking-status = Checking answer\nanswer-submitting-status = Submitting answer\n\nanswer-correct = Correct\nanswer-incorrect = Incorrect\n\n# Shown instead of a correctness verdict when the activity withholds\n# correctness: the response was recorded, nothing is claimed about it.\nanswer-response-saved = Response Saved\n\n# Partial credit. `-credit` is used when repeated attempts reduce the credit\n# available, `-correct` when they do not, and `-short` on a button too narrow\n# for a word.\nanswer-percent-credit = { $percent }% Credit\nanswer-percent-correct = { $percent }% Correct\nanswer-percent-short = { $percent } %\n\nmax-credit-available = Max credit available: { $percent }%\n\n# Fluent formats `{ $count }` with `Intl.NumberFormat`, so a four-digit\n# attempt count renders as \"1,000\" where the hand-built string said \"1000\".\n# That is the one place English output is not byte-identical to what this\n# replaced, and grouping is the locale-correct rendering, so it stands.\nattempts-remaining =\n    { $count ->\n        [0] no attempts remaining\n        [one] { $count } attempt remaining\n       *[other] { $count } attempts remaining\n    }\n\n# Appended to an input's accessible name once its response has been graded,\n# so a screen reader reports the verdict along with the field.\nvalidation-correct = (Correct)\nvalidation-incorrect = (Incorrect)\nvalidation-partially-correct = (Partially correct)\n\n# Tooltip on the badge that reports how many responses have been submitted to\n# one answer, shown only to a host that asked for it. `$answerId` is the\n# answer's authored name and is never translated.\nanswer-show-responses =\n    { $count ->\n        [one] Show { $count } response to { $answerId }\n       *[other] Show { $count } responses to { $answerId }\n    }\n\n\n## Disclosure panels\n\nfeedback-heading = Feedback\n\n# Follows a disclosure panel's own heading — \"Solution (click to open)\" — and\n# is shared by `<solution>`, `<hint>`, and a collapsible `<section>`. The whole\n# parenthetical is one message: where the word for open or close falls inside\n# it is the translator's business.\ncollapsible-click-to-open = (click to open)\ncollapsible-click-to-close = (click to close)\n\n# Placeholder inside a panel that has been opened before its contents have\n# arrived from the core. Shared by `<solution>` and a collapsible `<section>`.\ncollapsible-initializing = Initializing...\n\n# Tooltip on a footnote marker, naming what activating it will do.\nfootnote-show = Show footnote\nfootnote-hide = Hide footnote\n\n# Tooltip on the ⓘ affordance that reveals an input's description.\ndescription-more-information = more information\n\n\n## Controls\n\nslider-previous = Prev\nslider-next = Next\n\nkeyboard-open = Open Keyboard\nkeyboard-close = Close Keyboard\n\n# Accessible name of the button that drops one selected choice from an inline\n# `<choiceInput selectMultiple>`. The button itself is drawn as a bare cross,\n# so `$choice` — the choice's own text, which is never translated — is the\n# only thing that says which chip it belongs to.\nchoice-input-remove-choice = Remove { $choice }\n\n# Accessible names of a matrix input's size controls, whose visible labels are\n# the symbols `r-` `r+` `c-` `c+`.\nmatrix-remove-row = Remove row\nmatrix-add-row = Add row\nmatrix-remove-column = Remove column\nmatrix-add-column = Add column\n\n# Modes and actions of the subset-of-reals input's control strip. The button\n# that selects all of the reals is the symbol `R`, not a word, and stays in\n# place.\nsubset-add-remove-points = Add/Remove points\nsubset-toggle-points-intervals = Toggle points and intervals\nsubset-move-points = Move Points\nsubset-clear = Clear\n\n# Buttons that edit an orbital diagram: rows hold boxes, boxes hold up to\n# three spin arrows.\norbital-add-row = Add Row\norbital-remove-row = Remove Row\norbital-add-box = Add Box\norbital-remove-box = Remove Box\norbital-add-up-arrow = Add Up Arrow\norbital-add-down-arrow = Add Down Arrow\norbital-remove-arrow = Remove Arrow\n\n# Accessible name of the text field naming one row of an orbital diagram,\n# counting from 1.\norbital-row-label = Label for row { $row }\n\n# Labels the answer column of a pretzel exercise's grid.\npretzel-answer = Answer\n\n# Caption above the table a `<summaryStatistics>` renders. `$column` is the\n# authored name of the data column being summarized and is never translated.\n# The table's own headings (`mean`, `stdev`, `quartile1`, …) are the statistic\n# ids an author references, not prose, and stay in place.\nsummary-statistics-caption = Summary statistics of { $column }\n\n\n## Math input\n\n# Accessible name of the popover that previews the typed expression, and of\n# the rendered expression inside it.\nmath-input-preview-region = math expression preview\nmath-input-preview = Preview\nmath-input-invalid-expression = Invalid expression:\n\n\n## Document status\n\n# Shown while the core is still starting up and nothing can be rendered yet.\nviewer-initializing = Initializing...\n\n\n## Errors\n\n# Prefixes an error message wherever one is shown in place of content: an\n# `<error>` the core reported, the error boundary's fallback, and the\n# placeholder left where a renderer chunk failed to load.\nerror-heading = Error\n\n# Follows the error's own message inside that box, saying where in the source\n# the error was found. $span selects the sentence — one line or a range of\n# them — as a key rather than a phrase, so a translation is free to reorder the\n# whole of it. $startLine and $endLine are line numbers, and arrive as text\n# rather than as numbers so that line 1234 is not grouped as \"1,234\": a line\n# number is an identifier, not a quantity.\nerror-found-at =\n    { $span ->\n        [line] Found on line { $startLine }.\n       *[lines] Found on lines { $startLine }–{ $endLine }.\n    }\n\n# Banner above a document that compiled with at least one error in it.\ndocument-contains-errors = This document contains errors!\n\n# Headings of the tooltip the editor shows over a squiggle, naming what kind\n# of diagnostic it is. `-information` and `-hint` are styled identically but\n# are two different words; `-hint` is unreachable today, since nothing Doenet\n# raises is an LSP hint, and is here so the set is complete.\ndiagnostic-heading-error = Error\ndiagnostic-heading-warning = Warning\ndiagnostic-heading-information = Info\ndiagnostic-heading-hint = Hint\n\n# Headings of the same tooltip over an accessibility diagnostic, which says\n# what kind of problem it is rather than how severe. Level 1 is a failure\n# against the WCAG AA success criteria; level 2 is Doenet's own advice, which\n# no standard requires.\naccessibility-heading-level-1 = WCAG AA Accessibility Violation\naccessibility-heading-level-2 = Accessibility alert\n\n# Shown in place of the document when a renderer threw and the error boundary\n# caught it.\nsomething-went-wrong = Something went wrong.\n\n# Shown in place of a single renderer whose code chunk never arrived.\nrenderer-load-failed = a renderer failed to load. Please reload the page.\n\n# Shown in place of the document when the core worker could not be started\n# after retries, rather than leaving the pane blank.\ncore-start-failed = This document could not be started. Please reload the page.\n\n# Replaces `core-start-failed` when several documents were starting at the\n# same time — counted across every same-origin tab, so not necessarily all on\n# the page the reader sees. Naming the contention keeps the reader from\n# concluding the service is broken.\ncore-start-failed-busy = This document could not be started. Several documents were starting at once, which can take longer on a slower device. Reloading the page may help once the other documents have finished.\n";
const content = '# Worker-generated content: style descriptions ("thick red line"), boolean\n# words, and other prose the core computes into the document. Selected by\n# `documentLocale`, which follows the content\'s language rather than the\n# reader\'s UI language.\n#\n# Message ids are lower-kebab-case Fluent identifiers, optionally with a\n# single `.attribute` suffix (`color.blue`, `noun.line-segment`).\n\n\n## Style vocabulary\n##\n## The words the style pipeline derives from a component\'s numeric and\n## enumerated style values. A word an author writes directly — `lineColorWord`,\n## `markerStyleWord`, and their siblings — passes through untranslated: the\n## author chose those words, and rewriting them would be a surprise. So does a\n## CSS named color asked for by name ("rebeccapurple"), which\n## `resolveColorWord` deliberately preserves.\n##\n## Every adjective here is handed `$gender`, the grammatical gender of the noun\n## it describes (see `noun-gender`), and `$role`, the syntactic position the\n## phrase it belongs to is going into. English has no agreement and ignores\n## both; a language that inflects selects on them.\n##\n## `$role` is one of:\n##\n##   standalone          the phrase stands on its own, which is where all but\n##                       the three below are\n##   border-clause       inside `style-border-clause`, behind its preposition\n##   background-clause   the background colour inside `style-text`, behind its\n##                       preposition\n##   text-clause         the text colour beside it, inside `style-text`\n##\n## The last three exist because those words are rendered in two places each —\n## once alone, as `borderStyleDescription`, `backgroundColor` and `textColor`\n## report them, and once embedded in a clause. A language that inflects for\n## case needs a different form in each, and no amount of `$gender` can say\n## which is wanted. Which case a position governs is the catalog\'s business:\n## `border-clause` is a dative in German, an instrumental in Russian and\n## Polish, and nothing at all in a language that does not inflect.\n\n# The canonical color families a color value resolves to.\ncolor =\n    .black = black\n    .white = white\n    .gray = gray\n    .red = red\n    .orange = orange\n    .yellow = yellow\n    .green = green\n    .cyan = cyan\n    .blue = blue\n    .purple = purple\n    .pink = pink\n    .brown = brown\n\n# Stroke widths. Only the extremes are named — a middling width is described by\n# its color alone.\nline-width =\n    .thick = thick\n    .thin = thin\n\n# Dash patterns. A solid stroke is described by its color alone.\nline-style =\n    .dashed = dashed\n    .dotted = dotted\n\n# Patterns a shape\'s interior can be filled with. A solid fill is described by\n# its color alone.\nfill-style =\n    .horizontal = horizontal lines\n    .vertical = vertical lines\n    .diagonal = diagonal lines\n    .backdiagonal = reverse diagonal lines\n    .dots = dots\n    .diamonds = diamonds\n\n# The things being described. The shapes a point can be drawn as ("square",\n# "cross") are nouns too: a point\'s description names its marker shape rather\n# than always saying "point".\nnoun =\n    .line = line\n    .line-segment = line segment\n    .ray = ray\n    .vector = vector\n    .curve = curve\n    .function = function\n    .slope-field = slope field\n    .vector-field = vector field\n    .parabola = parabola\n    .polyline = polyline\n    .polygon = polygon\n    .triangle = triangle\n    .rectangle = rectangle\n    .circle = circle\n    .region = region\n    .point = point\n    .square = square\n    .diamond = diamond\n    .cross = cross\n    .plus = plus\n\n# A regular polygon names its side count, so it is a message of its own rather\n# than one of `noun`\'s attributes.\n#\n# `$part` splits the noun where a language needs it split: `head` is the word\n# the adjectives attach to, `tail` a complement that follows them. English\n# folds the side count into the head and has no tail; Spanish says "polígono\n# regular" and puts "de 5 lados" after the adjectives, so that they stay beside\n# the noun they agree with. `style-with-noun` and `style-filled-with-noun`\n# place the two halves.\n#\n# `$numSides` is a real number, so it is formatted by the locale\'s own rules —\n# a 1000-gon reads "1,000-sided" here and "de 1000 lados" in Spanish. That is\n# the number-formatting policy in the README, and the one place a description\n# is not character-for-character what the pre-catalog code produced.\nnoun-regular-polygon =\n    { $part ->\n        [tail] { "" }\n       *[head] { $numSides }-sided regular polygon\n    }\n\n# The grammatical gender of the noun being described, passed to every adjective\n# describing it so that translations can agree. English has no grammatical\n# gender, so every noun answers the same and the answer goes unused.\n#\n# `$noun` is one of `noun`\'s attribute names, `regular-polygon` for the shape\n# `noun-regular-polygon` names, or the head of a phrase the description builds\n# without naming it as a noun: `border`, `fill`, `text`, or `background`. A\n# word this message does not list falls to its default gender — which is also\n# what an author\'s own `markerStyleWord` gets, since the catalog has never seen\n# it.\nnoun-gender = neuter\n\n\n## Style composition\n##\n## `$parts` names which pieces the style actually supplies, so that a\n## translation can order and inflect each combination on its own terms instead\n## of substituting into a fixed English frame. An absent piece is a different\n## branch, never an empty placeable.\n\n# The adjectives describing a stroke: its width, its dash pattern, and its\n# color. Also describes a shape\'s border, where the color is dropped when it\n# matches the fill it surrounds.\nstyle-stroke =\n    { $parts ->\n        [width-style-color] { $width } { $lineStyle } { $color }\n        [width-color] { $width } { $color }\n        [style-color] { $lineStyle } { $color }\n        [width-style] { $width } { $lineStyle }\n        [width] { $width }\n        [style] { $lineStyle }\n       *[color] { $color }\n    }\n\n# A style description followed by what it describes: "thick red line".\n#\n# `$nounTail` is the noun\'s trailing complement, for the nouns whose\n# translation splits around the adjectives (see `noun-regular-polygon`).\n# English has none today, so it only ever selects `noun` for itself — the other\n# variant is still what a partly-translated locale falls back to, and dropping\n# it would drop that locale\'s side count.\n#\n# The phrase it builds is never governed by anything, so its `$role` is always\n# `standalone`.\nstyle-with-noun =\n    { $parts ->\n        [noun-tail] { $description } { $noun } { $nounTail }\n       *[noun] { $description } { $noun }\n    }\n\n# The word marking a shape as filled.\n#\n# A key of its own, looked up by the code and handed to the messages below as\n# `$filled`, rather than literal text inside them: a language that inflects it\n# has to agree it with the shape. Referencing it from those messages would not\n# do — a term reference (`{ -filled }`) gets an empty scope and never sees\n# `$gender`, and a message reference resolves only inside its own bundle, so a\n# locale that translated `style-filled` but not this word would render the\n# reference literally instead of falling back to English.\n#\n# Said only of the shape itself, so its `$role` is always `standalone`.\nstyle-filled-word = filled\n\n# A filled shape, and the pattern its interior is drawn with, if any.\nstyle-filled =\n    { $parts ->\n        [pattern] { $filled } { $color } with { $pattern }\n       *[plain] { $filled } { $color }\n    }\n\n# The same, naming the shape: "filled blue circle with diamonds".\n#\n# The `-tail` variants carry the noun\'s trailing complement, as\n# `style-with-noun` does.\nstyle-filled-with-noun =\n    { $parts ->\n        [pattern] { $filled } { $color } { $noun } with { $pattern }\n        [plain-tail] { $filled } { $color } { $noun } { $nounTail }\n        [pattern-tail] { $filled } { $color } { $noun } { $nounTail } with { $pattern }\n       *[plain] { $filled } { $color } { $noun }\n    }\n\n# The border clause appended to a filled shape: "with a thick red border".\n#\n# `$parts` carries two distinctions English cares about: whether a fill pattern\n# was already mentioned, which makes this a further clause ("and") rather than\n# the first ("with"), and whether the surrounding description named the shape,\n# which is where English wants an article.\nstyle-border-clause =\n    { $parts ->\n        [with-article] with a { $border } border\n        [and] and { $border } border\n        [and-article] and a { $border } border\n       *[with] with { $border } border\n    }\n\n# How a shape\'s interior is filled, on its own: "blue diamonds".\nstyle-fill =\n    { $parts ->\n        [pattern] { $color } { $pattern }\n       *[plain] { $color }\n    }\n\nstyle-unfilled = unfilled\n\n# How a piece of text is styled: its color, and the background behind it.\n#\n# The only composition message with no `$role`: its two words sit in two\n# different positions, so they arrive already inflected for `text-clause` and\n# `background-clause` respectively and no one token would describe them both.\nstyle-text =\n    { $parts ->\n        [background] { $color } with a { $background } background\n       *[plain] { $color }\n    }\n\n# What `backgroundColor` answers when nothing is drawn behind the text.\nstyle-background-none = none\n\n\n## Boolean words\n##\n## What a `<boolean>` or `<booleanInput>` *displays*.\n##\n## Not what it parses or serializes. `true` and `false` are DoenetML syntax —\n## an author writes them in the source, `<award>` compares against them, and\n## saved state stores them — so they stay English everywhere, in every\n## language, exactly as `<` stays `<`. Only the word the reader sees moves.\n##\n## The consequence is that a value has two spellings in a translated document,\n## and both have to be accepted where one is read back in: see\n## `booleanFromWord` in the worker.\n\nboolean-true = true\nboolean-false = false\n\n\n## Answer buttons\n##\n## The default text on an answer\'s submit button. Only the *default* is\n## translated: an author who writes `submitLabel="Ready?"` gets "Ready?" in\n## every locale, because they chose those words for their document and a\n## translation of it is not Doenet\'s to make.\n\n# Shown when the answer will report whether the response was right.\nanswer-submit-label = Check Work\n\n# Shown when it will not — the reader submits, and is told nothing more.\nanswer-submit-label-no-correctness = Submit Response\n\n\n## Sectional blocks\n##\n## The word a sectional block calls itself, which the reader sees in its\n## heading and on a `<solution>`\'s reveal control. Keyed by the element the\n## author writes, so `<subsection>` and `<subsubsection>` share `.section` —\n## they are all called a section.\n##\n## An author who writes `renameTo` has named the block themselves, and that\n## name passes through in every language.\n\nsection-name =\n    .activity = Activity\n    .aside = Aside\n    .cascade = Cascade\n    .definition = Definition\n    .example = Example\n    .exercise = Exercise\n    .exercises = Exercises\n    .given-answer = Answer\n    .note = Note\n    .objectives = Objectives\n    .paragraphs = Paragraphs\n    .part = Part\n    .problem = Problem\n    .problems = Problems\n    .proof = Proof\n    .question = Question\n    .section = Section\n    .solution = Solution\n    .task = Task\n    .theorem = Theorem\n\n# The heading a section builds for itself: "Example 2", "Section 1.3: Limits".\n#\n# `$parts` names which pieces are present and whether a `<title>` follows, so\n# that a translation orders and punctuates each combination on its own terms\n# rather than substituting into an English frame. English puts the word first\n# and separates a title with a colon — or with a period when the heading is a\n# bare number — and none of that is a given.\n#\n# The `-title` variants end in the separator, because what follows them is the\n# title child, rendered separately. Fluent trims trailing whitespace, so the\n# separator is written as a string literal to keep its space.\n#\n# `$sectionNumber` arrives as text ("2", "1.3") rather than as a number: it is\n# an identifier made of counters, and it is not the catalog\'s to reformat.\nsection-title-prefix =\n    { $parts ->\n        [name] { $sectionName }\n        [number] { $sectionNumber }\n        [name-title] { $sectionName }{ ": " }\n        [number-title] { $sectionNumber }{ ". " }\n        [name-number-title] { $sectionName } { $sectionNumber }{ ": " }\n       *[name-number] { $sectionName } { $sectionNumber }\n    }\n\n# The heading a `<hint>` shows when the author gave it no `<title>`.\nhint-title = Hint\n\n\n## Tables and figures\n##\n## The name a `<table>` or `<figure>` gives itself, which the renderer sets in\n## bold at the head of the title or caption: "Table 2", "Figure 3". An\n## unnumbered one is named by the bare word.\n##\n## The word and the number are one message rather than a word the code\n## concatenates a number onto, so that a language which orders or punctuates\n## them differently can say so.\n##\n## `$enumeration` arrives as text rather than as a number: it identifies the\n## table, so the thousandth one is "Table 1000" and not "Table 1,000".\n##\n## The `-title` and `-caption` branches end in the separator joining the name\n## to the authored title or caption, which the renderer used to write as a\n## literal `": "` of its own. That child is arbitrary marked-up content\n## rendered after this string rather than an argument passed into it, so where\n## it sits is the one thing a translation cannot reorder — the same shape\n## `section-title-prefix` has.\n\ntable-name =\n    { $parts ->\n        [numbered] Table { $enumeration }\n        [numbered-title] Table { $enumeration }{ ": " }\n        [unnumbered-title] Table{ ": " }\n       *[unnumbered] Table\n    }\n\nfigure-name =\n    { $parts ->\n        [numbered] Figure { $enumeration }\n        [numbered-caption] Figure { $enumeration }{ ": " }\n        [unnumbered-caption] Figure{ ": " }\n       *[unnumbered] Figure\n    }\n\n\n## Paginator controls\n##\n## The strip of controls that steps through a `<paginator>`\'s pages. All three\n## words are author-overridable attributes, so only the default is translated.\n\npaginator-previous = Previous\npaginator-next = Next\npaginator-page = Page\n\n# The status between the two buttons: "Page 3 of 5".\n#\n# Composed in the worker rather than in the renderer so that the whole of it is\n# in one language: `$pageLabel` is the `pageLabel` attribute, which an author\n# may have written themselves, and the words joining it to the counts have to\n# sit beside it in the same tongue.\n#\n# The two counts arrive as text, for the same reason `$enumeration` does above.\npaginator-page-status = { $pageLabel } { $currentPage } of { $numPages }\n\n\n## Piecewise functions\n##\n## `<piecewiseFunction>` renders each branch\'s domain as mathematics and writes\n## these three words around it: "if 1 < x < 2 or 4 < x < 5", and "otherwise"\n## for the branch that catches what the others leave. The inequalities are\n## notation and stay as they are; these are prose, and are read aloud as prose.\n##\n## `or` is the only conjunction the core composes into content. Everything else\n## that reads as "a, b, and c" is a diagnostic, and those are joined by\n## `Intl.ListFormat` at the moment the reader\'s language is known rather than\n## by a catalog word (see `DiagnosticListArg`).\n\npiecewise-condition-or = or\n\n# Introduces the domain a branch applies on; the mathematics follows it.\npiecewise-condition-if = if\n\n# The last branch, which applies wherever none of the earlier ones do.\npiecewise-condition-otherwise = otherwise\n\n\n## Chemistry\n##\n## The names the chemistry components generate. Symbols (`H`, `Fe`), formulas,\n## and anything an author\'s `<award>` compares against by value stay as they\n## are; only what is displayed as prose is here.\n##\n## Element names are keyed by symbol, which is the identifier the atom database\n## is read through and is itself never translated.\n##\n## An element\'s periodic group, its phase at STP and its metal category are not\n## here. They are enumerated values an author\'s `<award>` compares against —\n## `$atom.groupName = Noble Gas` — and translating one would break that\n## comparison the moment a document declared a language (#1577).\n\nelement-name =\n    .h = Hydrogen\n    .he = Helium\n    .li = Lithium\n    .be = Beryllium\n    .b = Boron\n    .c = Carbon\n    .n = Nitrogen\n    .o = Oxygen\n    .f = Fluorine\n    .ne = Neon\n    .na = Sodium\n    .mg = Magnesium\n    .al = Aluminum\n    .si = Silicon\n    .p = Phosphorus\n    .s = Sulfur\n    .cl = Chlorine\n    .ar = Argon\n    .k = Potassium\n    .ca = Calcium\n    .sc = Scandium\n    .ti = Titanium\n    .v = Vanadium\n    .cr = Chromium\n    .mn = Manganese\n    .fe = Iron\n    .co = Cobalt\n    .ni = Nickel\n    .cu = Copper\n    .zn = Zinc\n    .ga = Gallium\n    .ge = Germanium\n    .as = Arsenic\n    .se = Selenium\n    .br = Bromine\n    .kr = Krypton\n    .rb = Rubidium\n    .sr = Strontium\n    .y = Yttrium\n    .zr = Zirconium\n    .nb = Niobium\n    .mo = Molybdenum\n    .tc = Technetium\n    .ru = Ruthenium\n    .rh = Rhodium\n    .pd = Palladium\n    .ag = Silver\n    .cd = Cadmium\n    .in = Indium\n    .sn = Tin\n    .sb = Antimony\n    .te = Tellurium\n    .i = Iodine\n    .xe = Xenon\n    .cs = Cesium\n    .ba = Barium\n    .la = Lanthanum\n    .ce = Cerium\n    .pr = Praseodymium\n    .nd = Neodymium\n    .pm = Promethium\n    .sm = Samarium\n    .eu = Europium\n    .gd = Gadolinium\n    .tb = Terbium\n    .dy = Dysprosium\n    .ho = Holmium\n    .er = Erbium\n    .tm = Thulium\n    .yb = Ytterbium\n    .lu = Lutetium\n    .hf = Hafnium\n    .ta = Tantalum\n    .w = Tungsten\n    .re = Rhenium\n    .os = Osmium\n    .ir = Iridium\n    .pt = Platinum\n    .au = Gold\n    .hg = Mercury\n    .tl = Thallium\n    .pb = Lead\n    .bi = Bismuth\n    .po = Polonium\n    .at = Astatine\n    .rn = Radon\n    .fr = Francium\n    .ra = Radium\n    .ac = Actinium\n    .th = Thorium\n    .pa = Protactinium\n    .u = Uranium\n    .np = Neptunium\n    .pu = Plutonium\n    .am = Americium\n    .cm = Curium\n    .bk = Berkelium\n    .cf = Californium\n    .es = Einsteinium\n    .fm = Fermium\n    .md = Mendelevium\n    .no = Nobelium\n    .lr = Lawrencium\n    .rf = Rutherfordium\n    .db = Dubnium\n    .sg = Seaborgium\n    .bh = Bohrium\n    .hs = Hassium\n    .mt = Meitnerium\n    .ds = Darmstadtium\n    .rg = Roentgenium\n    .cn = Copernicium\n    .nh = Nihonium\n    .fl = Flerovium\n    .mc = Moscovium\n    .lv = Livermorium\n    .ts = Tennessine\n    .og = Oganesson\n\n# The name an anion takes, for the elements that have one.\n#\n# English derives these — strip a trailing "ine", add "ide", with a small table\n# for the words that rule does not fit — but that is English morphology, not a\n# fact about anions. So each language supplies the names it uses. An element\n# with no entry here is called by its own name, which is also what the English\n# rule does for everything it does not match.\nelement-anion-name =\n    .h = Hydride\n    .c = Carbide\n    .n = Nitride\n    .o = Oxide\n    .f = Fluoride\n    .p = Phosphide\n    .s = Sulfide\n    .cl = Chloride\n    .br = Bromide\n    .i = Iodide\n    .at = Astatide\n    .ts = Tennesside\n\n# A transition metal\'s ion, named with its oxidation state: "Iron (II)".\n#\n# The Roman numeral is IUPAC notation and is the same in every language. Where\n# it sits and how it is punctuated is not, which is why this is a message and\n# not a suffix the code appends.\nion-name-oxidation-state = { $name } ({ $numeral })\n\n# Shown in place of a chemical symbol or formula that names nothing. Rendered\n# into mathematics as well as into text, so the brackets around it are added by\n# the code — inside `\\text{}` for the LaTeX form — and are not part of the\n# message.\nchemistry-invalid-symbol = Invalid Chemical Symbol\nchemistry-invalid-ionic-compound = Invalid Ionic Compound\n';
const diagnostics = '# Errors and warnings surfaced to the reader or author. Produced by the worker\n# but addressed to whoever is looking at the screen, so these are selected by\n# `uiLocale`, not `documentLocale`.\n#\n# Message ids are lower-kebab-case Fluent identifiers, optionally with a\n# single `.attribute` suffix (`invalid-attribute-value`).\n#\n# Reached by stable diagnostic code rather than by a literal `t("key")` call:\n# `DIAGNOSTIC_CODES` in `src/diagnostics.ts` maps `doenet-w0001` to the id\n# below, and `lint:i18n` treats that registry as the call site. Adding a\n# message here without registering a code for it fails the lint as an orphan.\n#\n# Translators: `through`, `endpoint`, `midpointOffset`, `numDimensions` and the\n# like are DoenetML attribute names. They are part of the language, not prose,\n# and must be left in English exactly as written.\n\n## `<lineSegment>`\n\n# $attributes is a list of attribute names; $attributesCount is its length.\nline-segment-attributes-ignored-with-endpoints =\n    { $attributesCount ->\n        [one] { $attributes } is ignored when two endpoints are specified\n       *[other] { $attributes } are ignored when two endpoints are specified\n    }\n\n# $attributes is a list of attribute names; $attributesCount is its length.\nline-segment-attributes-ignored-with-endpoint-and-midpoint =\n    { $attributesCount ->\n        [one] { $attributes } is ignored when an endpoint and a midpoint are both specified\n       *[other] { $attributes } are ignored when an endpoint and a midpoint are both specified\n    }\n\nline-segment-midpoint-offset-without-midpoint = midpointOffset has no effect without a midpoint\n\n## `<line>`\n\nline-points-undetermined-dimensions = Line through points of undetermined dimensions.\n\nline-points-too-few-dimensions = Line must be through points of at least two dimensions.\n\n# $variables is a bare enumeration of variable names, not an "and" list.\nline-points-depend-on-variables = Line is through points that depend on variables: { $variables }.\n\nline-equation-invalid-format = Invalid format for equation of line in variables { $variable1 } and { $variable2 }.\n\n## `<ray>`\n\nray-overprescribed-through = Ray is prescribed by through, endpoint, and direction.  Ignoring specified through.\n\nray-dimension-mismatch = numDimensions mismatch in ray.\n\n## `<vector>`\n\nvector-overprescribed-head = Vector is prescribed by head, tail, and displacement.  Ignoring specified head.\n\nvector-dimension-mismatch = numDimensions mismatch in vector.\n\n## Attracting and constraining\n\n# $component is the DoenetML tag of the child that was named, e.g. "polygon".\nattract-to-without-nearest-point = Cannot attract to a `<{ $component }>` as it doesn\'t have a nearestPoint state variable.\n\nconstrain-to-without-nearest-point = Cannot constrain to a `<{ $component }>` as it doesn\'t have a nearestPoint state variable.\n\nconstrain-to-interior-without-nearest-point = Cannot constrain to interior of a `<{ $component }>` as it doesn\'t have a nearestPoint state variable.\n\n## `<choiceInput>`\n\n# Translators: `labelPosition` is an attribute name and stays in English.\nchoice-input-label-position-ignored = labelPosition is ignored for non-inline choiceInput\n\n## Ordering children by index\n##\n## These name the component in prose rather than as a tag, matching how the\n## messages have always read. The component names stay in English; the nouns\n## around them are prose and should be translated.\n\nchoice-input-indices-count-mismatch = Ignoring indices specified for choiceInput as number of indices doesn\'t match number of choice children.\n\npretzel-indices-count-mismatch = Ignoring indices specified for problem as number of indices doesn\'t match number of problem children.\n\nshuffle-indices-count-mismatch = Ignoring indices specified for shuffle as number of indices doesn\'t match number of components.\n\n# $component is `choiceInput`, `pretzel` or `shuffle` — a DoenetML component\n# name, so it stays in English.\nindices-ignored-out-of-range = Ignoring indices specified for { $component } as some indices out of range.\n\npretzel-indices-repeated = Ignoring indices specified for pretzel as some indices are repeated.\n\npretzel-circuit-first-index = Ignoring indices specified for pretzel in circuit mode as the first index must be 1.\n\n## `<shuffle>` and `<sort>`\n\n# $component is `shuffle` or `sort`. These two components accept the same\n# children and fail the same ways, so they share their messages.\nstring-children-need-type = For `<{ $component }>` to work with string children, a `type` attribute must be specified.\n\n# $type is what the author wrote; math, text, number and boolean are attribute\n# values and stay in English.\ninvalid-type-defaulting-to-math = Invalid type { $type } for { $component } component. Must be one of math, text, number, or boolean. Defaulting to math.\n\n# $value is the string child that could not be used.\nstring-not-valid-component-to-arrange = String "{ $value }" is not a valid component to { $component }. Ignoring.\n\n## Types and variables\n\ninvalid-type-defaulting-to-number = Invalid type { $type }, setting type to number.\n\ninvalid-variable-value = Invalid value of a variable: `{ $value }`\n\n## Variants\n\n# $index is what the author wrote, reproduced verbatim rather than as a number:\n# it reached this message precisely because it was not one.\nvariant-index-must-be-number = Variant index { $index } must be a number\n\nvariant-index-must-be-integer = Variant index { $index } must be an integer\n\n## `<sideBySide>`\n\n# $component is `sideBySide` or `sbsGroup`.\nside-by-side-absolute-widths = `<{ $component }>` is not implemented for absolute measurements. Setting widths to relative.\n\nside-by-side-absolute-margins = `<{ $component }>` is not implemented for absolute measurements. Setting margins to relative.\n\nside-by-side-no-block-child = Invalid `<{ $component }>`: it must have at least one block child.\n\n## `<label>`\n\n# Translators: `for` is an attribute name and stays in English.\nlabel-for-ignored-on-graphical = The `for` attribute on graphical `<label>` is ignored.\n\nlabel-for-must-resolve-to-one = The `for` attribute on `<label>` must resolve to exactly one component.\n\nlabel-for-unresolved = The `for` attribute on `<label>` could not be resolved to a component.\n\nlabel-for-answer-with-authored-inputs = The `for` attribute on `<label>` references an `<answer>` with explicitly authored inputs; reference the input directly.\n\nlabel-for-answer-without-input = The `for` attribute on `<label>` references an `<answer>` without an input to label.\n\nlabel-for-must-reference-input-or-answer = The `for` attribute on `<label>` must reference an input or an answer.\n\n## Accessibility\n\n# $component is a DoenetML tag, e.g. "graph" or "image".\naccessibility-short-description-or-decorative = For accessibility, `<{ $component }>` must either have a short description or be specified as decorative.\n\naccessibility-video-short-description = For accessibility, `<video>` must have a short description.\n\naccessibility-input-short-description-or-label = For accessibility, `<{ $component }>` must have a short description or a label.\n\n# The companion to the message above, for the input an `<answer>` creates on the\n# author\'s behalf. Two messages rather than one with the subject passed in: the\n# subject is a phrase here, not a name, and a phrase handed over as an argument\n# would never reach a translator.\naccessibility-answer-input-short-description-or-label = For accessibility, an `<answer>` creating an input must have a short description or a label.\n\naccessibility-short-description-contains-math = Short descriptions should not contain math components such as `<{ $component }>`. Spell out any math with words.\n\n# $colorName is an attribute name and stays in English. $ratio and $threshold\n# are contrast ratios; $mode says which theme the shortfall was measured in,\n# and is `dark` or `light`.\naccessibility-section-title-insufficient-contrast =\n    { $mode ->\n        [dark] { $colorName } has insufficient contrast for the section heading text (dark mode) ({ NUMBER($ratio, minimumFractionDigits: 2, maximumFractionDigits: 2) }:1; requires at least { $threshold }:1).\n       *[other] { $colorName } has insufficient contrast for the section heading text ({ NUMBER($ratio, minimumFractionDigits: 2, maximumFractionDigits: 2) }:1; requires at least { $threshold }:1).\n    }\n\n## `<circle>`\n\n# $count is the number of through points.\ncircle-through-points-non-numerical = Haven\'t implemented `<circle>` through { $count } points in case where the points don\'t have numerical values.\n\ncircle-too-many-through-points = Cannot calculate circle through more than 3 points.\n\ncircle-overprescribed-radius-center-points = Cannot calculate circle with specified radius, center and through points.\n\ncircle-center-with-multiple-points = Cannot calculate circle with specified center through more than 1 point.\n\n# $distance and $radius arrive as strings, not numbers: $radius is the author\'s\n# own value echoed back for diagnosis, and formatting it as a quantity would\n# round a radius of 0.0001 away to 0.\ncircle-radius-too-small = Cannot calculate circle: given that the distance between the two points is { $distance }, the specified radius { $radius } is too small.\n\ncircle-radius-with-many-points = Cannot create circle through more than two points with a specified radius.\n\ncircle-invalid-center-or-through-points = Invalid center or through points of circle.\n\ncircle-radius-center-with-multiple-points = Cannot calculate radius of circle with specified center through more than 1 point.\n\ncircle-change-radius-non-numerical = Cannot change radius of circle with non-numerical through points\n\ncircle-radius-with-points-non-numerical = Cannot create circle through more than one point with specified radius when don\'t have numerical values.\n\ncircle-change-center-non-numerical = Haven\'t implemented changing center of circle through points with non numerical values.\n\n## `<function>`\n\n# Two independent counts in one sentence, so the variants multiply out. A\n# select\'s variants each need their own line, so the inner one spans lines too;\n# that is safe because newlines inside a placeable never reach the rendered\n# value. Only text continuing onto a further line would.\nfunction-domain-insufficient-dimensions =\n    { $intervals ->\n        [one] Insufficient dimensions for domain for function. Domain has { $intervals } interval but the function has { $inputs ->\n            [one] { $inputs } input\n           *[other] { $inputs } inputs\n        }.\n       *[other] Insufficient dimensions for domain for function. Domain has { $intervals } intervals but the function has { $inputs ->\n            [one] { $inputs } input\n           *[other] { $inputs } inputs\n        }.\n    }\n\nfunction-domain-invalid-format = Invalid format for domain for function.\n\n# $type is what was being read off the point. It selects the wording rather\n# than being substituted into it: "maximum", "slope" and the rest are English\n# nouns, and a noun handed over as an argument would never reach a translator.\n# The catch-all reproduces the pre-catalog behavior for a value not listed here.\nfunction-ignoring-non-numerical =\n    { $type ->\n        [maximum] Ignoring non-numerical maximum of function.\n        [minimum] Ignoring non-numerical minimum of function.\n        [extremum] Ignoring non-numerical extremum of function.\n        [point] Ignoring non-numerical point of function.\n        [slope] Ignoring non-numerical slope of function.\n       *[other] Ignoring non-numerical { $type } of function.\n    }\n\nfunction-ignoring-empty =\n    { $type ->\n        [maximum] Ignoring empty maximum of function.\n        [minimum] Ignoring empty minimum of function.\n        [extremum] Ignoring empty extremum of function.\n        [point] Ignoring empty point of function.\n       *[other] Ignoring empty { $type } of function.\n    }\n\nfunction-points-too-close = Function contains two points with locations too close together. Can\'t define function.\n\nfunction-iterates-input-output-mismatch =\n    { $inputs ->\n        [one] Function iterates are possible only if the number of inputs of the function is equal to the number of outputs. This function has { $inputs } input and { $outputs ->\n            [one] { $outputs } output\n           *[other] { $outputs } outputs\n        }.\n       *[other] Function iterates are possible only if the number of inputs of the function is equal to the number of outputs. This function has { $inputs } inputs and { $outputs ->\n            [one] { $outputs } output\n           *[other] { $outputs } outputs\n        }.\n    }\n\n## `<sequence>`\n\nsequence-invalid-length = Invalid length of sequence.  Must be a non-negative integer.\n\n# $type is a sequence type: number, letters, or math.\nsequence-invalid-step = Invalid step of sequence.  Must be a number for sequence of type { $type }.\n\n# $attribute is `from` or `to` — an attribute name, so it stays in English.\nsequence-invalid-endpoint-number = Invalid "{ $attribute }" of number sequence.  Must be a number.\n\nsequence-invalid-endpoint-letters = Invalid "{ $attribute }" of letters sequence.  Must be a letter combination.\n\nsequence-invalid-endpoint = Invalid "{ $attribute }" of sequence.\n\nselect-from-sequence-coprime-not-numbers = coprime ignored since not selecting numbers\n\nselect-from-sequence-coprime-with-exclude-combinations = coprime ignored since excludeCombinations specified\n\n## Resolving a `target`\n##\n## Raised by the components that take a `target` attribute. They resolve it\n## through the same code and fail the same two ways, so they share these two\n## messages rather than spelling each one out per component: $source is the tag\n## of the component that raised it, part of the DoenetML language, so it stays\n## in English.\n\ntarget-not-found = Invalid target for `<{ $source }>`: cannot find target.\n\n# $property is the state variable that was looked for; $component is the tag it\n# was looked for on.\ntarget-state-variable-not-found = Invalid target for `<{ $source }>`: cannot find a state variable named "{ $property }" on a `<{ $component }>`.\n\n## `<odeSystem>`\n\node-system-variables-match-independent = Variables of `<odeSystem>` must be different than independent variable.\n\node-system-duplicate-variable-names = Can\'t define ODE RHS functions with duplicate dependent variable names.\n\node-system-rhs-function-error = Cannot define ODE RHS function.  Error creating mathjs function.\n\n## `<angle>`, `<parabola>`, and `<intersection>`\n\n# $count is how many line children were found.\nangle-too-many-lines = Cannot define an angle between { $count } lines\n\nangle-invalid-through-point = Invalid point in through of `<angle>`\n\nparabola-vertex-too-many-points = Haven\'t implemented parabola with vertex through more than 1 point.\n\nparabola-too-many-points = Haven\'t implemented parabola through more than 3 points.\n\nintersection-too-many-items = Haven\'t implemented intersection for more than two items\n\n## Other math components\n\nionic-compound-not-two-ions = Have not implemented ionic compound for anything other than two ions.\n\nionic-compound-needs-cation-and-anion = Ionic compound implemented only for one cation and one anion.\n\n# $equation is the equation as the author wrote it.\nsolve-equations-cannot-evaluate = Cannot solve equation as could not evaluate equation: { $equation }\n\n# Translators: `operandNumber` is an attribute name and stays in English.\nmath-operators-operand-number-required = Must specify a operandNumber when extracting a math operand.\n\neigen-decomposition-failed = Could not calculate eigenvalues of matrix\n\n## `<matchesPattern>`\n\n# Translators: `parameters` is an attribute name and stays in English.\n# $parameters lists the parameters as the author wrote them;\n# $parametersCount is how many there were.\nmatches-pattern-parameter-not-in-pattern =\n    { $parametersCount ->\n        [one] `<matchesPattern>`: the parameter { $parameters } does not occur in the pattern, so it will always match a blank.\n       *[other] `<matchesPattern>`: the parameters { $parameters } do not occur in the pattern, so they will always match a blank.\n    }\n\n## `<graph>`\n\n# Translators: grid is an attribute name and none, medium and dense are its\n# values; all four stay in English, as does the example. $grid is the value the\n# author wrote, reproduced verbatim — it reached this message precisely because\n# it was not one of the forms listed.\ngraph-grid-invalid = `<graph>`: cannot interpret grid="{ $grid }". It must be none, medium, dense, or two positive numbers separated by a space, such as grid="1 0.5". No grid is drawn.\n\n## `<slopeField>` and `<vectorField>`\n\n# $component is the field\'s tag and $alternative the sibling field component\'s,\n# or `none` when the function would not suit it either; both are DoenetML tags\n# and stay in English, as do the example expressions. $expected is how many\n# outputs the component needs, $found how many the given function has. What a\n# field draws a mark from is the function\'s outputs, so the wrong number of\n# them means there is nothing to draw — and the author almost always wanted the\n# other component.\nfield-function-wrong-num-outputs =\n    `<{ $component }>` needs a function with { $expected ->\n        [one] one output, the slope y\' at each point, such as `y - x`\n       *[other] two outputs, the vector at each point, such as `(y, -x)`\n    }, but the function it was given has { $found ->\n        [one] { $found } output\n       *[other] { $found } outputs\n    }. { $alternative ->\n        [none] Nothing is drawn.\n       *[other] `<{ $alternative }>` is the component for that function. Nothing is drawn.\n    }\n\n# Translators: retired. `function` is an attribute name and stays in English.\n# It warned that a field had been given its function both as an attribute and\n# inside the component; the attribute no longer exists, so the two can no\n# longer disagree. Kept because diagnostic codes are never reused.\nfield-function-attribute-ignored-with-child = The `function` attribute is ignored because the function is also given inside the component; the one inside is used. Give the function only one of the two ways.\n\n# Translators: `variables` and `function` are a DoenetML attribute and tag and\n# stay in English, as does $component, the field\'s own tag. The attribute names\n# the inputs of an expression written directly inside the component; when there\n# is no such expression there is nothing for it to name, and $reason says which\n# of the two ways that happened.\nfield-variables-ignored =\n    `<{ $component }>`: the `variables` attribute names the variables of an expression written directly inside the component. { $reason ->\n        [function-child] The function here is given as a `<function>` child, which names its own variables, so `variables` is ignored.\n       *[no-expression] No such expression is given here, so `variables` is ignored.\n    }\n\n## PreFigure renderer\n\n# Translators: xLabelPosition, yLabelPosition and their values are attribute\n# names and stay in English, as does the renderer\'s name.\nprefigure-x-label-position-unsupported = `<graph>`: xLabelPosition="left" is not supported in prefigure renderer; using right-position behavior.\n\nprefigure-y-label-position-unsupported = `<graph>`: yLabelPosition="bottom" is not supported in prefigure renderer; using top-position behavior.\n\nprefigure-invalid-axis-bounds = `<graph>`: invalid axis bounds for prefigure conversion; using default bbox (-10,-10,10,10).\n\nprefigure-invalid-width = `<graph>`: invalid width for prefigure conversion; using default diagram width 425.\n\nprefigure-invalid-aspect-ratio = `<graph>`: invalid aspectRatio for prefigure conversion; using default aspect ratio 1.\n\n# Translators: the renderer\'s name, prefigure, stays in English. "grid" here is\n# the coordinate grid itself rather than the attribute that asks for one, so it\n# is prose and is translated.\nprefigure-grid-spacing-too-fine = `<graph>`: the grid spacing is too fine for the axis limits; the grid is omitted in the prefigure renderer.\n\nprefigure-annotations-not-rendered = `<graph>`: annotations will not be rendered when not using the PreFigure renderer.\n\nmultiple-annotations-children = Multiple `<annotations>` children found in `<graph>`; all but the last one are ignored.\n\n## Referring to other components\n##\n## `<updateValue>`\'s own "cannot find target" messages are not here: it\n## resolves a target the same way `<animateFromSequence>` does and fails the\n## same ways, so the two share `target-not-found` and\n## `target-state-variable-not-found` above.\n\ncopy-unrecognized-component-type = Cannot extend or copy an unrecognized component type: { $type }.\n\ncopy-prop-not-found = Could not find prop { $property } on a component of type { $component }\n\ncollect-no-source = No source found for collect.\n\ncollect-invalid-component-type = Cannot collect components of type `<{ $component }>` as it is an invalid component type.\n\n# $reference is the reference exactly as the author wrote it, `$` and all —\n# the `$p.styleDescription[1]` of `<text extend="$p.styleDescription[1]" />`.\n# An index only means something applied to an array, and the thing named here\n# is not one. The reference is quoted back rather than explained because the\n# text in front of the author is the only part of this they can act on: the\n# state variable and component index the core knows about are its own business\n# and go to the console instead.\nreference-index-unavailable = Cannot reference index `{ $reference }`\n\n## `<callAction>`\n\n# $action is the `actionName` the author asked for, part of the DoenetML\n# language, so it stays in English. $reference is the `target` as written.\ncomponent-action-unavailable = Cannot call { $action } on component `{ $reference }`\n\n## `<dataFrame>`\n\n# $componentIdx is an internal index, passed as a string so it is not grouped\n# like a quantity; the odd spacing before the colon is reproduced from the\n# original message.\ndata-frame-inconsistent-row-lengths = Data has invalid shape.  Rows has inconsistent lengths. Found in componentIdx :{ $componentIdx }\n\ndata-frame-duplicate-column-names = Data has duplicate column names.  Found in componentIdx :{ $componentIdx }\n\ndata-frame-missing-column-name = Data is missing a column name.  Found in componentIdx :{ $componentIdx }\n\n## `<answer>` and scoring\n\nanswer-award-depends-on-own-response = An award for this answer is based on the answer tag\'s own submitted response, which will lead to unexpected behavior.\n\n# Translators: maxNumAttempts and sectionWideCheckWork are attribute names.\nanswer-max-num-attempts-in-section-wide-check-work = Setting `maxNumAttempts` on an `<answer>` inside a container with `sectionWideCheckWork` has no effect, as the number of attempts is controlled by the container. Set `maxNumAttempts` on the container instead.\n\nnested-section-wide-check-work-max-num-attempts = Setting `maxNumAttempts` on a container with `sectionWideCheckWork` that is inside another container with `sectionWideCheckWork` has no effect, as the number of attempts is controlled by the outer container. Set `maxNumAttempts` on the outer container instead.\n\n# $attributes is a list of attribute names; $attributesCount is its length.\nanswer-attributes-need-symbolic-equality =\n    { $attributesCount ->\n        [one] The { $attributes } attribute will have no effect without symbolicEquality set.\n       *[other] The { $attributes } attributes will have no effect without symbolicEquality set.\n    }\n\nanswer-invalid-type = Invalid type for answer: { $type }\n\n## `<module>`, `<conditionalContent>`, `<slider>`, `<pretzel>`\n\nmodule-attribute-child-needs-name = Since the component `<{ $component }>` does not have a name, it cannot be used for a module attribute\n\nmodule-attribute-name-already-defined = The component `<{ $component } name="{ $name }">` cannot be used as an attribute for a module because the `<module>` component type already has a "{ $name }" attribute defined.\n\nconditional-content-condition-ignored = Attribute `condition` is ignored on a `<conditionalContent>` component with case or else children.\n\nslider-markers-type-mismatch = Markers type doesn\'t match slider type.\n\npretzel-problem-needs-statement-and-answer = Invalid pretzel: each `<problem>` must contain one `<statement>` and one `<answer>`.\n\npretzel-circuit-first-problem-distractor = Invalid pretzel: in mode="circuit", the first `<problem>` cannot be a distractor.\n\n## Attribute values\n\n# $values is a list of the values that were rejected, each already in\n# backticks; $valuesCount is how many there were.\nattribute-invalid-values =\n    { $valuesCount ->\n        [one] Invalid value { $values } for attribute `{ $attribute }`; ignoring.\n       *[other] Invalid values { $values } for attribute `{ $attribute }`; ignoring.\n    }\n\nattribute-must-be-references = Invalid value `{ $value }` for attribute `{ $attribute }`. Attribute must be composed of references that begin with a `$`.\n\n# $names is a list of the rejected names, each already in single quotes.\nmath-input-invalid-function-names = <mathInput>: ignored invalid function name(s) in { $attribute }: { $names }. Each name\'s display segment must be at least 2 characters (letters or dashes); an optional `|<mathspeak alternative>` suffix may follow.\n\n## Building components from the source\n\n# Raised while the source is being turned into components, by throwing rather\n# than by building a record: the thrower is caught, the component becomes an\n# `_error`, and the diagnostic is re-raised from it.\n\ncomponent-type-invalid = Invalid component type: `<{ $componentType }>`\n\nattribute-repeated = Cannot repeat attribute { $attribute }.\n\nattribute-invalid-for-component = Invalid attribute "{ $attribute }" for a component of type `<{ $componentType }>`.\n\n## Style definition contrast\n\n# $context names the pair being compared, $mode which colour scheme it was\n# rendered in. Both are symbolic — the phrase is chosen here so a translator\n# can rewrite it, rather than being handed over already in English.\nstyle-definition-insufficient-contrast =\n    Style definition { $styleNumber } has insufficient contrast for { $context ->\n        [text-on-background] text color against background color\n        [high-contrast] high-contrast color against the canvas\n        [line] line color against the canvas\n        [marker] marker color against the canvas\n       *[text-on-canvas] text color against the canvas\n    }{ $mode ->\n        [dark] { " (dark mode)" }\n       *[light] { "" }\n    } ({ NUMBER($ratio, minimumFractionDigits: 2, maximumFractionDigits: 2) }:1; requires at least { $threshold }:1).\n\n# $suggestion says whether a concrete replacement colour could be computed.\n# The attribute names and colour values in the `available` branch are\n# DoenetML source, not prose, and stay as they are in every language.\nstyle-definition-dark-mode-text-background-contrast =\n    Although style definition { $styleNumber } has specified colors that provide sufficient contrast for light mode, the dark-mode colors derived from these values have insufficient contrast for the text color against the background color ({ NUMBER($ratio, minimumFractionDigits: 2, maximumFractionDigits: 2) }:1; requires at least { $threshold }:1). { $suggestion ->\n        [available] To ensure sufficient contrast in dark mode, either increase the light-mode contrast (e.g., set { $lightAttribute }="{ $lightColor }") or override the dark-mode color (e.g., set { $darkAttribute }="{ $darkColor }").\n       *[none] To ensure sufficient contrast in dark mode, increase the light-mode contrast or override the derived colors with textColorDarkMode and/or backgroundColorDarkMode.\n    }\n\nstyle-definition-dark-mode-text-canvas-contrast =\n    Although style definition { $styleNumber } has a specified text color that provides sufficient contrast for light mode, the dark-mode text color derived from this value has insufficient contrast against the canvas ({ NUMBER($ratio, minimumFractionDigits: 2, maximumFractionDigits: 2) }:1; requires at least { $threshold }:1). { $suggestion ->\n        [available] To ensure sufficient contrast in dark mode, either increase the light-mode contrast (e.g., set textColor="{ $lightColor }") or override the dark-mode color (e.g., set textColorDarkMode="{ $darkColor }").\n       *[none] To ensure sufficient contrast in dark mode, increase the light-mode contrast or override the derived color with textColorDarkMode.\n    }\n\nsection-multiple-style-palettes = A section can select only one <stylePalette>; using the last one.\n\n## Unique variants\n\n# Explanations of why a component\'s unique variants could not be worked out.\n# $component is the tag that could not be analyzed and stays as written; the\n# reason is a separate message per situation, so a host can tell them apart by\n# code and a translator sees a whole sentence rather than a fragment.\n\nvariant-num-to-select-not-non-negative-integer = cannot determine unique variants of { $component } as numToSelect isn\'t a non-negative integer.\n\nvariant-num-to-select-not-constant-number = cannot determine unique variants of { $component } as numToSelect isn\'t constant number.\n\nvariant-with-replacement-not-constant-boolean = cannot determine unique variants of { $component } as withReplacement isn\'t constant boolean.\n\nvariant-select-weight-disables-unique = Unique variants for select disabled if have an option with selectWeight or selectForVariants specified\n\nvariant-coprime-undetermined = cannot determine unique variants of { $component } as cannot determine coprime is always false.\n\n# $attribute is an attribute name (`from`, `to`, `step`, `sort`, `length`) and\n# stays as written.\nvariant-attribute-not-constant = cannot determine unique variants of { $component } as { $attribute } isn\'t a constant.\n\nvariant-attribute-not-number = cannot determine unique variants of { $component } as { $attribute } isn\'t a number.\n\n# $type is the sequence type the component was declared with. $expected names\n# what the value had to be, symbolically, because which one applies depends on\n# both the type and the attribute.\nvariant-attribute-wrong-type-for-sequence =\n    cannot determine unique variants of { $component } of { $type } type as { $attribute } isn\'t { $expected ->\n        [letters-combination] a combination of letters\n        [math-expression] a valid math expression\n        [integer] an integer\n       *[number] a number\n    }.\n\nvariant-length-not-integer = cannot determine unique variants of { $component } as length isn\'t an integer.\n\nvariant-sort-not-implemented = have not implemented unique variants of a { $component } with sort\n\nvariant-exclude-combinations-not-implemented = have not implemented unique variants of a { $component } with excludeCombinations\n\nvariant-math-exclude-not-implemented = have not implemented unique variants of a { $component } of type math with exclude\n\nvariant-non-constant-exclude-not-implemented = have not implemented unique variants of a { $component } with non-constant exclude\n\n## PreFigure conversion\n\n# $subject identifies the component the warning is about, already written as\n# `<tag>` or `<tag> (name)`. It is composed in code rather than here because\n# Fluent terms cannot take a variable as an argument, so a shared subject\n# fragment cannot be parameterized from the catalog. It holds only a tag name,\n# a component name and punctuation — never a word — which is why a descendant\n# with no type reads `<?>` rather than `<unknown>`.\n\nprefigure-descendant-unsupported = { $subject }: unsupported in graph prefigure renderer; descendant skipped.\n\nprefigure-descendant-invalid-geometry = { $subject }: non-finite or incomplete geometry; descendant skipped.\n\nprefigure-curve-label-omitted = { $subject }: labels are not supported on converted curve elements; label omitted.\n\nprefigure-curve-unsupported-definition-type = { $subject }: unsupported curve function definition type \'{ $definitionType }\'; descendant skipped.\n\nprefigure-region-flip-functions-unsupported = { $subject }: unsupported flipFunctions attribute on regionBetweenCurves; descendant skipped.\n\nprefigure-region-non-formula-child = { $subject }: only formula-typed child functions are supported on regionBetweenCurves; descendant skipped.\n\n# $labelKind says which family of object carried the label, since the advice\n# is the same but the object is not.\nprefigure-label-position-unsupported =\n    { $subject }: unsupported labelPosition \'{ $labelPosition }\' for { $labelKind ->\n        [line-family] line-family label\n       *[point] point label\n    }; default PreFigure alignment used.\n\nprefigure-fill-style-unsupported = { $subject }: fill style \'{ $fillStyle }\' is unsupported by PreFigure; falling back to a solid fill.\n\nprefigure-line-style-unknown = { $subject }: unknown line style \'{ $lineStyle }\' omitted from PreFigure output.\n\nprefigure-marker-style-mapped-to-diamond = { $subject }: marker style \'{ $markerStyle }\' mapped to PreFigure style \'diamond\'.\n\nprefigure-marker-style-unsupported = { $subject }: marker style \'{ $markerStyle }\' is unsupported by PreFigure; default style used.\n\n## PreFigure annotations\n\nannotation-ref-unresolvable = `<annotation>`: invalid `ref`; cannot resolve target. Annotation omitted.\n\nannotation-ref-multiple-targets = `<annotation>`: `ref` resolved to multiple targets; using the first target.\n\nannotation-ref-outside-graph = `<annotation>`: invalid `ref`; target is outside the containing graph. Annotation omitted.\n\nannotation-ref-unsupported-target = `<annotation>`: invalid `ref`; target is not a supported graphical object in prefigure conversion. Annotation omitted.\n\nannotation-text-missing = `<annotation>`: missing or empty `text`; emitting empty text.\n\n## Composites and references\n\n# $componentType is the type the composite was asked to create, when it is\n# known; `none` when the composite did not say.\ncomposite-circular-dependency =\n    { $componentType ->\n        [none] Circular dependency detected.\n       *[other] Circular dependency detected involving `<{ $componentType }>` component.\n    }\n\n# $reference is the reference as the author wrote it, already carrying its `$`.\nreference-no-referent = No referent found for reference: `{ $reference }`\n\nreference-multiple-referents = Multiple referents found for reference: `{ $reference }`\n\n## Children that do not match\n\nchildren-invalid-attribute-format = Invalid format for attribute { $attribute } of `<{ $componentType }>`.\n\n# $children is the list of child types that did not match, already joined.\nchildren-invalid = Invalid children for `<{ $componentType }>`: Found invalid children: { $children }\n\n## Falling back to a default\n\nattribute-value-invalid-using-default = Invalid value `{ $value }` for attribute `{ $attribute }`, using value `{ $default }`\n\n## Loading a DoenetML version\n\n# $fallback is the version that will be used instead, or `none` when the\n# embedding page named a standalone bundle of its own.\ndoenetml-version-not-found =\n    { $fallback ->\n        [none] DoenetML version { $version } not found.\n       *[other] DoenetML version { $version } not found. Falling back to version { $fallback }\n    }\n\n## Reading the DoenetML\n\n# The parser\'s own diagnostics: what the author sees before anything runs, and\n# for a beginner usually the first Doenet message they ever read.\n#\n# The parser writes its English beside the code rather than rendering it from\n# here, because `@doenet/parser` is inside the language-server bundle and a\n# catalog there is dead weight on the editor\'s critical path\n# (`packages/lsp/scripts/check-server-bundle.mjs` fails if one arrives). The\n# two copies are held together by a test in that package, which parses a\n# corpus and asserts every coded error renders to exactly what the parser\n# wrote — so a message edited here without its counterpart fails there.\n#\n# $tag, $value, $attribute and their relatives quote the author\'s own source\n# back at them and stay exactly as written. The `Invalid DoenetML: ` opening is\n# repeated in each message instead of being a shared term: a term a locale\n# forgets to define renders as its own name, and a prefix on fifteen messages\n# is not worth that failure mode.\n\nparse-invalid-doenetml = Invalid DoenetML: { $content }\n\nparse-tag-missing-close-tag = Invalid DoenetML: The tag `{ $tag }` has no closing tag. Expected a self-closing tag or a `</{ $tagName }>` tag.\n\nparse-tag-error = Invalid DoenetML: Error in tag `<{ $tagName }>`\n\nparse-attribute-missing-value = Invalid DoenetML: Invalid attribute `{ $attribute }` appears to be missing a value.\n\nparse-attribute-invalid = Invalid DoenetML: Invalid attribute `{ $attribute }`\n\nparse-attribute-value-invalid = Invalid DoenetML: Invalid attribute value `{ $value }`\n\n# $quote is the quote character that would balance the pair: `"` or `\'`.\nparse-attribute-value-quote-mismatch = Invalid DoenetML: Invalid attribute value `{ $value }`. The quote marks do not match. You appear to be missing a `{ $quote }`\n\nparse-open-tag-name-missing = Invalid DoenetML: Found a tag without a tag name, e.g. `<`\n\nparse-tag-not-closed = Invalid DoenetML: Tag `{ $tag }` was not closed (a `>` appears to be missing).\n\nparse-self-closing-tag-name-missing = Invalid DoenetML: Found a tag without a tag name `<{ $content }>`\n\nparse-self-closing-tag-not-closed = Invalid DoenetML: Tag `{ $tag }` was not closed (`/>` appears to be missing).\n\nparse-tag-invalid-attributes = Invalid DoenetML: Tag `{ $tag }` is not valid. It may have incorrect attributes.\n\nparse-close-tag-name-missing = Invalid DoenetML: Found a closing tag without a tag name, e.g. `</`\n\n# $attribute is the attribute name and $value the unquoted token that followed\n# it, shown reassembled the way the author should have written it.\nparse-attribute-value-unquoted = Attribute values must be enclosed in quotes: `{ $attribute }="{ $value }"`\n\nparse-close-tag-without-open-tag = Invalid DoenetML: Found closing tag `{ $tag }`, but no corresponding opening tag\n\nparse-close-tag-mismatched = Invalid DoenetML: Mismatched closing tag. Expected `</{ $expected }>`. Found `{ $found }`\n\n# The conversion\'s fall-through: the syntax tree held a node shape it has no\n# case for. Reaching an author means the grammar and the conversion have gone\n# out of step, which is a bug in Doenet rather than in their document — hence\n# `parser-` rather than the `parse-` of every message above, which is about\n# what the author wrote. They are still the one looking at it, so it is\n# translated like any other error. $node is the node\'s own name and stays as\n# it is.\nparser-node-unconvertible = Could not convert node { $node } to Dast node.\n\n## Names\n\n# $reason says which rule the name broke, as a key rather than a phrase, so the\n# whole sentence is translatable rather than assembled from two halves.\nname-attribute-invalid =\n    Invalid attribute name=\'{ $name }\'. { $reason ->\n        [characters] Names can contain only letters, numbers, underscores or hyphens.\n       *[start] Names must start with a letter.\n    }\n\ncomponent-name-invalid-start = Invalid component name "{ $name }". Names must start with a letter.\n\n## `<answer>` sugar\n\nanswer-video-watched-missing-video = Answer with type videoWatched must have a video attribute\n\nanswer-video-watched-video-not-reference = Answer with type videoWatched must have video attribute that is a reference\n\nanswer-name-not-single-text = Answer name attribute must have a single text child\n\n## Referencing another document\n\n# $attribute is the attribute the URI was written in (`copyFrom`, `extend`, …)\n# and stays as written; $uri is the author\'s own value.\n\nexternal-doenetml-recursion-limit = Unable to retrieve external DoenetML due to too many levels of recursion. Is there a circular reference?\n\nexternal-doenetml-unavailable = Unable to retrieve DoenetML from { $attribute }="{ $uri }"\n\nexternal-doenetml-type-mismatch = Invalid DoenetML retrieved from { $attribute }="{ $uri }": it did not match the component type "{ $componentType }"\n\n## Deprecated syntax\n\n# $from and $to are attribute names and $component a tag name; all three stay\n# as written. $component is `none` for a rename that applies to every component\n# accepting the attribute, where naming one would be wrong.\n#\n# The `[deprecation]` opening is a literal marker shared by all four messages,\n# not a word: leave it as it is.\n\ndeprecated-attribute-renamed =\n    { $component ->\n        [none] [deprecation] Attribute `{ $from }` is deprecated; use `{ $to }` instead.\n       *[other] [deprecation] Attribute `{ $from }` on `<{ $component }>` is deprecated; use `{ $to }` instead.\n    }\n\ndeprecated-attribute-renamed-conflict =\n    { $component ->\n        [none] [deprecation] Attribute `{ $from }` is deprecated and ignored because `{ $to }` is also specified.\n       *[other] [deprecation] Attribute `{ $from }` on `<{ $component }>` is deprecated and ignored because `{ $to }` is also specified.\n    }\n\ndeprecated-attribute-ignored = [deprecation] Attribute `{ $attribute }` on `<{ $component }>` is deprecated and ignored.\n\n# An attribute replaced by a child element rather than by another attribute:\n# $attribute and $component stay as written, and so does $child, which is the\n# tag name of the child to write instead.\n\ndeprecated-attribute-to-child = [deprecation] Attribute `{ $attribute }` on `<{ $component }>` is deprecated; use a `<{ $child }>` child instead.\n\n# One value of an attribute is deprecated while the attribute itself stays:\n# $value is what the author wrote and $to what to write instead, with\n# $attribute and $component naming where. All four stay as written.\n\ndeprecated-attribute-value-renamed = [deprecation] Value `{ $value }` of attribute `{ $attribute }` on `<{ $component }>` is deprecated; use `{ $to }` instead.\n\n\n## Language coverage\n\n# `<pluralize>` runs an English part-of-speech model over its text and puts the\n# nouns in the plural. There is no equivalent for an arbitrary language — a\n# correct plural needs that language\'s own morphology, and often its\n# dictionary — so in a document written in anything else the word is left as\n# the author typed it, and this says so rather than silently doing nothing.\n#\n# Raised only when there is nothing else to fall back on: a `pluralForm` is the\n# author supplying their own language\'s plural, and that is honored in every\n# language, which is why this can recommend it.\n#\n# $locale is the document\'s language tag, as declared.\npluralize-english-only = `<pluralize>` can only pluralize English, so its text is left unchanged in a document written in { $locale }. Write the plural form directly, or set it with the `pluralForm` attribute.\n\n\n## Checking against the schema\n\n# What the editor draws a squiggle under: the language server\'s own check of\n# the document against the DoenetML schema, run on every keystroke and\n# answered before anything is evaluated. A beginner meets these first, and\n# usually only these, because a document that does not pass them rarely gets\n# as far as producing a diagnostic from the core.\n#\n# `@doenet/lsp-tools` writes its English beside the code rather than rendering\n# it from here, for the same reason `@doenet/parser` does: it is inside the\n# language-server bundle, which `@doenet/codemirror` embeds verbatim and\n# starts as a blob worker, and a catalog on the editor\'s critical path is what\n# `packages/lsp/scripts/check-server-bundle.mjs` exists to reject. The two\n# copies are held together by a test in that package, which runs the checker\n# over a corpus and asserts every coded violation renders to exactly what the\n# checker wrote — so a message edited here without its counterpart fails\n# there.\n#\n# $tag, $parent and $attribute quote the author\'s own source back at them and\n# stay exactly as written; the angle brackets and backticks around them are\n# punctuation this catalog supplies, not part of the name.\n\nschema-element-unrecognized = Element `<{ $tag }>` is not a recognized Doenet element.\n\nschema-element-not-allowed-at-root = Element `<{ $tag }>` is not allowed at the root of the document.\n\nschema-element-not-allowed-inside = Element `<{ $tag }>` is not allowed inside of `<{ $parent }>`.\n\nschema-attribute-unrecognized = Element `<{ $tag }>` doesn\'t have an attribute called `{ $attribute }`.\n\n# $allowed is the attribute\'s permitted values, each already in double quotes\n# and joined for the reader\'s language. $isList says whether the attribute\n# takes several of them at once: one situation, two sentences, because the\n# reader has to be told they are choosing a whole list rather than a value.\nschema-attribute-value-not-allowed =\n    { $isList ->\n        [true] Attribute `{ $attribute }` of element `<{ $tag }>` must be a list whose items are each one of: { $allowed }\n       *[other] Attribute `{ $attribute }` of element `<{ $tag }>` must be one of: { $allowed }\n    }\n\n\n## The `<select>` family\'s error boxes\n##\n## The messages that replace the whole component with a red box rather than\n## warning beside it: nothing can be selected, so there is nothing to render.\n## They were the last uncoded `_error` path in the worker (#1581).\n##\n## Counts arrive as numbers rather than as text, so a language that agrees a\n## noun with them can select on them. English does not agree here — it says\n## "1 options" today and these messages reproduce it exactly, because the box\'s\n## text is what the existing suites pin.\n##\n## Being numbers, they are grouped: a count of 1500 renders as "1,500" in\n## English where the concatenated sentence these replaced wrote "1500". That is\n## the one way the English moved, and it moved in the right direction — these\n## are quantities, not identifiers like a line or a section number, which is\n## the distinction that decides between a number and a string everywhere in\n## these catalogs.\n##\n## Translators: component and attribute names — `selectFromSequence`,\n## `selectPrimeNumbers`, `from`, `to`, `step` — are DoenetML identifiers, not\n## words. They are written into these messages as they stand and must be left\n## in English exactly as written.\n\nselect-variant-name-option-count-mismatch = Invalid variant name for select.  Variant name { $variantName } appears in { $numOptions } options but number to select is { $numToSelect }.\n\nselect-variant-name-without-options = Some variants are specified for select but no options are specified for possible variant name: { $variantName }.\n\nselect-variant-name-not-possible = Variant name { $variantName } that is specified for select is not a possible variant name.\n\nselect-too-few-options = Cannot select { $numToSelect } components from only { $numOptions }.\n\nselect-from-sequence-too-few-values = Cannot select { $numToSelect } values from a sequence of length { $length }.\n\nselect-from-sequence-indices-count-mismatch = Number of indices specified for select must match number to select\n\nselect-from-sequence-indices-not-integers = All indices specified for select must be integers\n\nselect-from-sequence-index-excluded = Specified index of selectfromsequence that was excluded\n\nselect-from-sequence-indices-excluded-combination = Specified indices of selectfromsequence that was an excluded combination\n\nselect-from-sequence-coprime-not-positive-integers = Cannot select coprime combinations as not selecting positive integers.\n\n# Translators: from, to and step are attribute names. They are written into\n# the message rather than passed in because these three never vary — an\n# argument is for a name that changes from one call to the next.\nselect-from-sequence-coprime-common-factor = Cannot select coprime numbers. All possible values share a common factor. (Specified values of "from" or "to" must be coprime with "step".)\n\nselect-from-sequence-coprime-single-number = Cannot select coprime combinations from a single number that is not 1.\n\nselect-from-sequence-excluded-too-many-combinations = Excluded over 70% of combinations in selectFromSequence\n\n# The sibling of `select-from-sequence-coprime-common-factor`, and a different\n# situation rather than a rewording of it: that one is decided up front, from\n# the sequence\'s own arithmetic. This one is what is left after two hundred\n# draws found no coprime combination among values that could have supplied one.\nselect-from-sequence-coprime-none-found = Could not select coprime numbers. All possible values share a common factor.\n\nselect-from-sequence-too-few-unique-values = Cannot select { $numToSelect } unique values from sequence of length { $numPossibleValues }\n\nselect-prime-numbers-too-few-values = Cannot select { $numToSelect } values from a list of primes of length { $numValues }\n\nselect-prime-numbers-values-count-mismatch = Number of values specified for select must match number to select\n\nselect-prime-numbers-values-not-prime = All values specified for select prime number must be in the list of primes\n\nselect-prime-numbers-values-excluded-combination = Specified values of selectPrimeNumbers was an excluded combination\n\nselect-prime-numbers-excluded-too-many-combinations = Excluded over 70% of combinations in selectPrimeNumbers\n\n# Both flukes are shared by `<selectFromSequence>` and `<selectPrimeNumbers>`,\n# which say the same thing in the same words. Neither is reachable in practice\n# — each follows two hundred independent draws — but an unreachable box is\n# still a box, and it renders in whatever language the rest of the page does.\nselect-random-combination-fluke = By extremely unlikely fluke, couldn\'t select combination of random values\n\nselect-random-value-fluke = By extremely unlikely fluke, couldn\'t select random value\n';
const editor = '# Editor and language-server surfaces: the footer, the diagnostics panel, the\n# variant picker, the accessibility button, and the context-help panel beside\n# them. Selected by `uiLocale`.\n#\n# The editor binds to *one* language, and it is the one the viewer below it\n# resolved rather than the one the surrounding host chrome uses: the panel\n# lists diagnostics rendered down there, where an authored `<document lang>` is\n# known, and a footer in one language above a panel in another reads worse than\n# either choice on its own (#1580).\n#\n# The LSP ships bundled with its DoenetML version, so these catalogs are\n# version-correct rather than always-latest.\n#\n# Message ids are lower-kebab-case Fluent identifiers, optionally with a\n# single `.attribute` suffix (`format-document`).\n\n\n## The viewer\'s controls\n##\n## `editor-update-viewer` names the button that recompiles the document. Its\n## word depends on whether the source changed or only the document\'s state\n## did, which is a distinction the code makes and a `$action` says: the button\n## is otherwise identical, and splitting it into two messages would let the two\n## drift.\n##\n## The keyboard hint is a separate branch rather than text appended after the\n## title, because where a shortcut sits in a sentence is not the same in every\n## language. `$shortcut` is a key combination and stays as written.\n##\n## The tooltip takes the word as `$word` rather than referencing the message\n## above it: a message reference resolves only inside its own bundle, so a\n## locale that translated the tooltip but not the word would render the\n## reference literally instead of falling back to English. This is the shape\n## `paginator-page-status` already uses.\n\n# Button label. "Update" recompiles after a source edit; "Reset" reverts\n# only the document\'s live state to match — same button, different verb.\n# Keep both forms short enough to fit a toolbar button.\neditor-update-viewer =\n    { $action ->\n        [reset] Reset\n       *[update] Update\n    }\n\n# Tooltip on the button above. { $word } is that button\'s own current label\n# ("Update" or "Reset"), so this reads as e.g. "Update Viewer" or\n# "Reset Viewer". { $shortcut } appends the keyboard shortcut when one exists.\neditor-update-viewer-title =\n    { $shortcut ->\n        [none] { $word } Viewer\n       *[other] { $word } Viewer { $shortcut }\n    }\n\n\n## The variant picker\n\n# Label for the variant picker control itself (a noun, like a field label).\neditor-variant = Variant\n\n# Placeholder text shown in the empty filter input, before the user types\n# anything — not a button or instruction.\neditor-variant-filter = Filter...\n\n# Button/action label: advances to the next variant in the list.\neditor-variant-next = Select next variant\n\n# Button/action label: goes back to the previous variant in the list.\neditor-variant-previous = Select previous variant\n\n\n## The accessibility status button\n##\n## `WCAG AA` is the name of the standard and is not translated, the way\n## `accessibility-heading-level-1` already leaves it.\n##\n## The tooltip and the label are separate messages rather than one with an\n## extra clause: the label counts the violations and the tooltip does not, and\n## a screen reader and a hover are different audiences.\n##\n## `$action` is whether clicking opens or closes the report — an interpolated\n## English verb before this, which no locale could reach and which no language\n## has to place where English does.\n\neditor-accessibility-title =\n    { $status ->\n        [violations] WCAG AA accessibility violation identified. Click to { $action ->\n            [close] close\n           *[open] open\n        } accessibility report.\n        [advisories] Click to { $action ->\n            [close] close\n           *[open] open\n        } accessibility report. No WCAG AA violations were found, but additional accessibility recommendations are available.\n       *[clean] Click to { $action ->\n            [close] close\n           *[open] open\n        } accessibility report. No accessibility issues were found.\n    }\n\neditor-accessibility-label =\n    { $status ->\n        [violations] WCAG AA accessibility violation identified. { $count ->\n            [one] { $count } WCAG AA violation\n           *[other] { $count } WCAG AA violations\n        } found. Click to { $action ->\n            [close] close\n           *[open] open\n        } accessibility report.\n        [advisories] No WCAG AA violations identified. { $count ->\n            [one] { $count } additional accessibility recommendation\n           *[other] { $count } additional accessibility recommendations\n        } found. Click to { $action ->\n            [close] close\n           *[open] open\n        } accessibility report.\n       *[clean] No WCAG AA violations identified. Click to { $action ->\n            [close] close\n           *[open] open\n        } accessibility report.\n    }\n\n# The word on the button itself, beside the icon, when there are violations.\neditor-accessibility-badge = WCAG\n\n\n## The footer\n##\n## `$version` is a version number and stays as written.\n\neditor-version-title = DoenetML version { $version }\n\neditor-tab-help = Context-sensitive help\n# The word beside the icon on that tab, which has room for one word.\neditor-tab-help-short = Context\neditor-tab-errors = Errors\neditor-tab-warnings = Warnings\neditor-tab-info = Info\neditor-tab-accessibility = Accessibility\neditor-tab-responses = Submitted responses\n\n# A tab\'s accessible name when it carries a count: "Errors: 3".\neditor-tab-with-count = { $label }: { $count }\n\neditor-options = Editor options\neditor-format-as-doenetml = Format as DoenetML\neditor-format-as-xml = Format as XML\n\n\n## The diagnostics panel\n\n# Where in the source a diagnostic was found. `$line` arrives as text, not as a\n# number: it identifies the line, so line 1234 is "#1234" and not "#1,234".\neditor-diagnostic-line = Line #{ $line }\n\neditor-no-errors = No Errors\neditor-no-warnings = No Warnings\neditor-no-info = No Info Diagnostics\n\neditor-show-info-annotations = Show info diagnostics in editor\neditor-show-accessibility-annotations = Show accessibility diagnostics in editor\n\n# Links into the documentation, which has no translated pages yet. The words\n# move; where they point does not (#1580).\neditor-accessibility-learn-more = Learn how Doenet approaches accessibility\n\n# The heading over the WCAG AA failures. The standard\'s name is a link, so it\n# arrives as an argument rather than being written into the sentence — the\n# brackets around it are this catalog\'s punctuation, and the link is the code\'s.\neditor-accessibility-violations-heading = Accessibility violations ({ $standard })\n\neditor-accessibility-other-heading = Other accessibility issues\neditor-none-found = None found\n\n\n## Submitted responses\n\neditor-no-responses = No submitted responses yet\neditor-response-answer-id = Answer Id\neditor-response-response = Response\neditor-response-credit = Credit\neditor-response-submitted = Submitted\n\n\n## The context-help panel\n##\n## The panel beside the diagnostics, which explains whatever the cursor is on.\n## It is the last of the surfaces the header above names.\n##\n## Several of these sentences have marked-up fragments in them — an element\n## name in `<code>`, a link, a rendered piece of inline markdown. Those arrive\n## as arguments and are put back as React nodes after the message is formatted,\n## so a translation owns the whole sentence including where each fragment sits\n## and the punctuation around it. A translation that drops one simply renders\n## without it rather than losing the sentence.\n##\n## Element names, attribute names and `styleNumber` are DoenetML identifiers\n## and stay as written. The descriptions and summaries the panel shows come\n## from the schema, which is generated from the documentation and is not\n## translated, and the values it resolves — a color\'s derived word, a\n## function name, a type — come from the language server in the same state.\n## What is here is the panel\'s own prose around them (#1580).\n\nhelp-placeholder = Place cursor on a tag name, attribute, or { $ref } for documentation.\n\nhelp-unsupported-ref-chain = Help for multi-part references like { $example } is not yet supported.\n\nhelp-unresolved-ref =\n    { $reason ->\n        [notFound] No referent found for reference: { $ref }.\n        [multiple] Multiple referents found for reference: { $ref }.\n       *[indeterminate] A referent for { $ref } could not be determined.\n    }\n\n# Both links end in an arrow, which is direction rather than punctuation and is\n# the same in every language this ships in — but it is inside the message, so a\n# language written right to left can turn it around.\nhelp-learn-about-references = Learn about references →\nhelp-reference-page = Reference page →\n\n# What can go where the cursor is. Two selectors joined: where the cursor sits,\n# and what the schema allows there.\nhelp-suggestions-header =\n    { $location ->\n        [inside] Inside { $element }\n       *[top] At the top level\n    }{ $allowed ->\n        [none] { " — nothing goes here." }\n        [text] { " — type text here." }\n        [text-and-components] { " — type text here, or try:" }\n       *[components] { " — things to try:" }\n    }\n\n# `$shortcut` is a key combination and stays as written. `$total` is a real\n# count, so a language that agrees a noun with it can select on it.\nhelp-suggestions-footer = Press { $shortcut } to see all { $total } components.\n\n# An element\'s name joined to its one-line summary. `$name` is empty where the\n# panel has already printed the name beside this — a suggestion in the list —\n# and only the separator is wanted, so the message has to read as punctuation\n# on its own.\nhelp-name-summary = { $name } — { $summary }\n\n# `$line` is text, not a number: it identifies a line, so line 1234 is "1234"\n# and not "1,234". `none` is how a position that is not known selects a\n# sentence without one rather than substituting an empty parenthesis.\nhelp-ref-is-reference =\n    { $line ->\n        [none] { $ref } is a reference to { $target }.\n       *[other] { $ref } is a reference to { $target } (line { $line }).\n    }\n\nhelp-ref-derived-from =\n    { $line ->\n        [none] Introduced by { $owner } as { $role }.\n       *[other] Introduced by { $owner } on line { $line } as { $role }.\n    }\n\nhelp-property-is-reference =\n    { $line ->\n        [none] { $ref } is a reference to the { $property } property of { $element }.\n       *[other] { $ref } is a reference to the { $property } property of { $element } (line { $line }).\n    }\n\n# The badge on the title row saying what kind of thing the panel is explaining.\nhelp-kind-attribute = attribute\nhelp-kind-snippet = snippet\nhelp-kind-array-entry = array entry\n\nhelp-default = Default:\nhelp-active-default = Active default:\n\n# Appended after the active default\'s value. `styleNumber` is the attribute\'s\n# own name and stays as written; the space and brackets around it do not.\nhelp-style-number-annotation = { " " }(styleNumber { $styleNumber })\n\nhelp-allowed-values =\n    { $perItem ->\n        [true] Allowed values (one per item):\n       *[other] Allowed values:\n    }\n\n# Heading for values the editor offers without requiring: the attribute accepts\n# others too, and nothing warns about them. Distinct from `help-allowed-values`\n# so a translation can pick a word that suggests rather than constrains.\nhelp-suggested-values = Suggested values:\n\nhelp-inserts = Inserts:\n\nhelp-coordinates =\n    { $count ->\n        [one] Coordinate:\n       *[other] Coordinates:\n    }\n\nhelp-type = Type:\n\nhelp-resolved-style = Resolved style (styleNumber { $styleNumber }):\n\nhelp-resolved-function-names = Resolved function names:\nhelp-reset-list = Reset list on this input:\nhelp-added-on-input = Added on this input:\nhelp-removed-on-input = Removed on this input:\n\n# The three names are attributes an author writes, so they stay as written.\nhelp-reset-overrides = { $reset } overrides { $additional } and { $removed }.\n';
const CATALOG_NAMESPACES = [
  "chrome",
  "content",
  "diagnostics",
  "editor"
];
const WORKER_NAMESPACES = ["content"];
const CHROME_NAMESPACES = [
  "chrome",
  "diagnostics",
  "editor"
];
function combineCatalogs(catalogs) {
  return CATALOG_NAMESPACES.map((namespace) => catalogs[namespace]).filter((source) => source !== void 0).join("\n");
}
const DEFAULT_LOCALE = "en";
const EN_CATALOGS = {
  chrome,
  content,
  diagnostics,
  editor
};
const EN_CATALOG_SOURCE = combineCatalogs(EN_CATALOGS);
function localeResourceKey(resources) {
  return Object.keys(resources ?? {}).sort().join(",");
}
const BUNDLED_LOCALES = [DEFAULT_LOCALE];
function englishResources(namespaces) {
  const picked = {};
  for (const namespace of namespaces) {
    picked[namespace] = EN_CATALOGS[namespace];
  }
  return combineCatalogs(picked);
}
function intlLocale(locale) {
  try {
    return pinNumberingSystem(locale);
  } catch {
    return pinNumberingSystem(DEFAULT_LOCALE);
  }
}
function pinNumberingSystem(locale) {
  return new Intl.Locale(locale, {
    numberingSystem: LATIN_DIGITS
  }).toString();
}
const LATIN_DIGITS = "latn";
const MATH_NOTATION_LOCALE = DEFAULT_LOCALE;
const DECIMAL_LITERAL = /^(-?\d+)(?:\.(\d+))?$/;
function formatDecimalString(locale, value) {
  const match2 = DECIMAL_LITERAL.exec(value);
  if (match2 === null) {
    return value;
  }
  const [, integerPart, fractionPart] = match2;
  const tag = intlLocale(locale);
  const grouped = groupIntegerPart(tag, integerPart);
  if (fractionPart === void 0) {
    return grouped;
  }
  return `${grouped}${decimalSeparator(tag)}${fractionPart}`;
}
function groupIntegerPart(tag, integerPart) {
  const negative = integerPart.startsWith("-");
  const { prefix, suffix, separator, primary, secondary } = numberPattern(
    tag,
    negative
  );
  const digits = integerPart.slice(negative ? 1 : 0);
  const groups = [];
  let size = primary;
  let end = digits.length;
  while (end > size) {
    groups.unshift(digits.slice(end - size, end));
    end -= size;
    size = secondary;
  }
  groups.unshift(digits.slice(0, end));
  return prefix + groups.join(separator) + suffix;
}
const GROUPING_PROBE = "1111111111111111";
function numberPattern(tag, negative) {
  const parts = new Intl.NumberFormat(tag, {
    // `true` is `"always"`. The probe is long enough that `"auto"` would
    // group it too, so this changes nothing here and only states what is
    // being asked for: where a group boundary falls, not whether some
    // particular number is given any.
    //
    // That second question is answered by {@link groupIntegerPart}, which
    // groups from a thousand up in every locale — what `<intComma>` has
    // always done, and not what CLDR would do left alone: Spanish,
    // Italian, Polish, Hungarian, Bulgarian, Slovenian, Latvian, Estonian
    // and European Portuguese all suppress the separator in a bare
    // four-digit number, though German does not.
    useGrouping: true,
    maximumFractionDigits: 0
    // The string overload: `Intl.NumberFormat` reads a decimal string
    // exactly. TypeScript's `lib.es5` signature predates it, hence the cast.
  }).formatToParts(
    (negative ? "-" : "") + GROUPING_PROBE
  );
  const groups = [];
  let prefix = "";
  let suffix = "";
  let separator = "";
  for (const part of parts) {
    if (part.type === "integer") {
      groups.push(part.value.length);
      suffix = "";
    } else if (part.type === "group") {
      separator = part.value;
    } else if (groups.length === 0) {
      prefix += part.value;
    } else {
      suffix += part.value;
    }
  }
  const primary = groups[groups.length - 1];
  return {
    prefix,
    suffix,
    separator,
    primary,
    secondary: groups[groups.length - 2] ?? primary
  };
}
function decimalSeparator(tag) {
  return new Intl.NumberFormat(tag).formatToParts(1.1).find((part) => part.type === "decimal")?.value ?? ".";
}
const LIST_TYPES = ["conjunction", "disjunction", "unit"];
function listFormatFor(locale, type) {
  return new Intl.ListFormat(intlLocale(locale), {
    style: "long",
    type: LIST_TYPES.includes(type) ? type : "conjunction"
  });
}
function lookupPattern(bundle, key) {
  const separator = key.indexOf(".");
  const id = separator === -1 ? key : key.slice(0, separator);
  const attribute = separator === -1 ? null : key.slice(separator + 1);
  const message = bundle.getMessage(id);
  if (!message) {
    return null;
  }
  if (attribute === null) {
    return message.value;
  }
  return message.attributes[attribute] ?? null;
}
function createChainLink(locale, source, useIsolating, onError) {
  const bundle = new FluentBundle(intlLocale(locale), {
    useIsolating
  });
  const errors = bundle.addResource(new FluentResource(source));
  for (const error of errors) {
    onError?.(error, "", locale);
  }
  return { locale, bundle };
}
function createTranslator(locales, resources, options = {}) {
  const {
    useIsolating = false,
    onError,
    includeBuiltinEnglish = true
  } = options;
  const bundles = [];
  for (const locale of locales) {
    const source = resources[locale];
    if (source !== void 0) {
      bundles.push(
        createChainLink(locale, source, useIsolating, onError)
      );
    }
  }
  if (includeBuiltinEnglish) {
    bundles.push(
      createChainLink(
        DEFAULT_LOCALE,
        EN_CATALOG_SOURCE,
        useIsolating,
        onError
      )
    );
  }
  const translate = (key, args, fallback) => {
    for (const { locale, bundle } of bundles) {
      const pattern = lookupPattern(bundle, key);
      if (pattern === null) {
        continue;
      }
      const errors = [];
      const formatted = bundle.formatPattern(pattern, args, errors);
      for (const error of errors) {
        onError?.(error, key, locale);
      }
      return formatted;
    }
    return fallback ?? key;
  };
  translate.localeOf = (key) => bundles.find(({ bundle }) => lookupPattern(bundle, key) !== null)?.locale;
  return translate;
}
function asFallbackTranslator(translate) {
  return (key, englishWord) => translate(key, void 0, englishWord);
}
const languageCodeRe = "([a-z]{2,3}|\\*)";
const scriptCodeRe = "(?:-([a-z]{4}|\\*))";
const regionCodeRe = "(?:-([a-z]{2}|\\*))";
const variantCodeRe = "(?:-(([0-9][a-z0-9]{3}|[a-z0-9]{5,8})|\\*))";
const localeRe = new RegExp(`^${languageCodeRe}${scriptCodeRe}?${regionCodeRe}?${variantCodeRe}?$`, "i");
class Locale {
  /**
   * Parses a locale id using the localeRe into an array with four elements.
   *
   * If the second argument `range` is set to true, it places range `*` char
   * in place of any missing piece.
   *
   * It also allows skipping the script section of the id, so `en-US` is
   * properly parsed as `en-*-US-*`.
   */
  constructor(locale) {
    const result = localeRe.exec(locale.replace(/_/g, "-"));
    if (!result) {
      this.isWellFormed = false;
      return;
    }
    let [, language, script, region, variant] = result;
    if (language) {
      this.language = language.toLowerCase();
    }
    if (script) {
      this.script = script[0].toUpperCase() + script.slice(1);
    }
    if (region) {
      this.region = region.toUpperCase();
    }
    this.variant = variant;
    this.isWellFormed = true;
  }
  isEqual(other) {
    return this.language === other.language && this.script === other.script && this.region === other.region && this.variant === other.variant;
  }
  matches(other, thisRange = false, otherRange = false) {
    return (this.language === other.language || thisRange && this.language === void 0 || otherRange && other.language === void 0) && (this.script === other.script || thisRange && this.script === void 0 || otherRange && other.script === void 0) && (this.region === other.region || thisRange && this.region === void 0 || otherRange && other.region === void 0) && (this.variant === other.variant || thisRange && this.variant === void 0 || otherRange && other.variant === void 0);
  }
  toString() {
    return [this.language, this.script, this.region, this.variant].filter((part) => part !== void 0).join("-");
  }
  clearVariants() {
    this.variant = void 0;
  }
  clearRegion() {
    this.region = void 0;
  }
  addLikelySubtags() {
    const newLocale = getLikelySubtagsMin(this.toString().toLowerCase());
    if (newLocale) {
      this.language = newLocale.language;
      this.script = newLocale.script;
      this.region = newLocale.region;
      this.variant = newLocale.variant;
      return true;
    }
    return false;
  }
}
const likelySubtagsMin = {
  ar: "ar-arab-eg",
  "az-arab": "az-arab-ir",
  "az-ir": "az-arab-ir",
  be: "be-cyrl-by",
  da: "da-latn-dk",
  el: "el-grek-gr",
  en: "en-latn-us",
  fa: "fa-arab-ir",
  ja: "ja-jpan-jp",
  ko: "ko-kore-kr",
  pt: "pt-latn-br",
  sr: "sr-cyrl-rs",
  "sr-ru": "sr-latn-ru",
  sv: "sv-latn-se",
  ta: "ta-taml-in",
  uk: "uk-cyrl-ua",
  zh: "zh-hans-cn",
  "zh-hant": "zh-hant-tw",
  "zh-hk": "zh-hant-hk",
  "zh-mo": "zh-hant-mo",
  "zh-tw": "zh-hant-tw",
  "zh-gb": "zh-hant-gb",
  "zh-us": "zh-hant-us"
};
const regionMatchingLangs = [
  "az",
  "bg",
  "cs",
  "de",
  "es",
  "fi",
  "fr",
  "hu",
  "it",
  "lt",
  "lv",
  "nl",
  "pl",
  "ro",
  "ru"
];
function getLikelySubtagsMin(loc) {
  if (Object.prototype.hasOwnProperty.call(likelySubtagsMin, loc)) {
    return new Locale(likelySubtagsMin[loc]);
  }
  const locale = new Locale(loc);
  if (locale.language && regionMatchingLangs.includes(locale.language)) {
    locale.region = locale.language.toUpperCase();
    return locale;
  }
  return null;
}
function filterMatches(requestedLocales, availableLocales, strategy) {
  const supportedLocales = /* @__PURE__ */ new Set();
  const availableLocalesMap = /* @__PURE__ */ new Map();
  for (let locale of availableLocales) {
    let newLocale = new Locale(locale);
    if (newLocale.isWellFormed) {
      availableLocalesMap.set(locale, new Locale(locale));
    }
  }
  outer: for (const reqLocStr of requestedLocales) {
    const reqLocStrLC = reqLocStr.toLowerCase();
    const requestedLocale = new Locale(reqLocStrLC);
    if (requestedLocale.language === void 0) {
      continue;
    }
    for (const key of availableLocalesMap.keys()) {
      if (reqLocStrLC === key.toLowerCase()) {
        supportedLocales.add(key);
        availableLocalesMap.delete(key);
        if (strategy === "lookup") {
          return Array.from(supportedLocales);
        } else if (strategy === "filtering") {
          continue;
        } else {
          continue outer;
        }
      }
    }
    for (const [key, availableLocale] of availableLocalesMap.entries()) {
      if (availableLocale.matches(requestedLocale, true, false)) {
        supportedLocales.add(key);
        availableLocalesMap.delete(key);
        if (strategy === "lookup") {
          return Array.from(supportedLocales);
        } else if (strategy === "filtering") {
          continue;
        } else {
          continue outer;
        }
      }
    }
    if (requestedLocale.addLikelySubtags()) {
      for (const [key, availableLocale] of availableLocalesMap.entries()) {
        if (availableLocale.matches(requestedLocale, true, false)) {
          supportedLocales.add(key);
          availableLocalesMap.delete(key);
          if (strategy === "lookup") {
            return Array.from(supportedLocales);
          } else if (strategy === "filtering") {
            continue;
          } else {
            continue outer;
          }
        }
      }
    }
    requestedLocale.clearVariants();
    for (const [key, availableLocale] of availableLocalesMap.entries()) {
      if (availableLocale.matches(requestedLocale, true, true)) {
        supportedLocales.add(key);
        availableLocalesMap.delete(key);
        if (strategy === "lookup") {
          return Array.from(supportedLocales);
        } else if (strategy === "filtering") {
          continue;
        } else {
          continue outer;
        }
      }
    }
    requestedLocale.clearRegion();
    if (requestedLocale.addLikelySubtags()) {
      for (const [key, availableLocale] of availableLocalesMap.entries()) {
        if (availableLocale.matches(requestedLocale, true, false)) {
          supportedLocales.add(key);
          availableLocalesMap.delete(key);
          if (strategy === "lookup") {
            return Array.from(supportedLocales);
          } else if (strategy === "filtering") {
            continue;
          } else {
            continue outer;
          }
        }
      }
    }
    requestedLocale.clearRegion();
    for (const [key, availableLocale] of availableLocalesMap.entries()) {
      if (availableLocale.matches(requestedLocale, true, true)) {
        supportedLocales.add(key);
        availableLocalesMap.delete(key);
        if (strategy === "lookup") {
          return Array.from(supportedLocales);
        } else if (strategy === "filtering") {
          continue;
        } else {
          continue outer;
        }
      }
    }
  }
  return Array.from(supportedLocales);
}
function negotiateLanguages(requestedLocales, availableLocales, { strategy = "filtering", defaultLocale } = {}) {
  const supportedLocales = filterMatches(Array.from(requestedLocales !== null && requestedLocales !== void 0 ? requestedLocales : []).map(String), Array.from(availableLocales !== null && availableLocales !== void 0 ? availableLocales : []).map(String), strategy);
  if (strategy === "lookup") {
    if (defaultLocale === void 0) {
      throw new Error("defaultLocale cannot be undefined for strategy `lookup`");
    }
    if (supportedLocales.length === 0) {
      supportedLocales.push(defaultLocale);
    }
  } else if (defaultLocale && !supportedLocales.includes(defaultLocale)) {
    supportedLocales.push(defaultLocale);
  }
  return supportedLocales;
}
const LANGUAGE_ALIASES = {
  no: "nb",
  tw: "ak",
  man: "mnk"
};
const MACROLANGUAGE_MEMBERS = {
  // Quechuan. The catalog is Southern Quechua (Cusco-Collao); the Central and
  // Northern varieties and Ecuadorian Kichwa are all members and all reach it.
  qu: [
    "qub",
    "qud",
    "quf",
    "qug",
    "quh",
    "quk",
    "qul",
    "qup",
    "qur",
    "qus",
    "quw",
    "qux",
    "quy",
    "quz",
    "qva",
    "qvc",
    "qve",
    "qvh",
    "qvi",
    "qvj",
    "qvl",
    "qvm",
    "qvn",
    "qvo",
    "qvp",
    "qvs",
    "qvw",
    "qvz",
    "qwa",
    "qwc",
    "qwh",
    "qws",
    "qxa",
    "qxc",
    "qxh",
    "qxl",
    "qxn",
    "qxo",
    "qxp",
    "qxr",
    "qxt",
    "qxu",
    "qxw"
  ],
  // Aymaran.
  ay: ["ayc", "ayr"],
  // Guaranian. The catalog is Paraguayan Guarani.
  gn: ["gnw", "gug", "gui", "gun", "nhd"],
  // Nahuan. The catalog is Central Nahuatl. `ppl` is Pipil (Nawat), the one
  // member outside Mexico, and it is grouped here for the same published
  // reason as the rest rather than for where it is spoken.
  nah: [
    "azd",
    "azn",
    "azz",
    "naz",
    "nch",
    "nci",
    "ncj",
    "ncl",
    "ncx",
    "ngu",
    "nhc",
    "nhe",
    "nhg",
    "nhi",
    "nhk",
    "nhm",
    "nhn",
    "nhp",
    "nhq",
    "nht",
    "nhv",
    "nhw",
    "nhx",
    "nhy",
    "nhz",
    "nlv",
    "npl",
    "nsu",
    "nuz",
    "ppl"
  ],
  // Ojibwa. The catalog is in the Fiero double-vowel orthography, which is
  // closest to `ciw` — the variety the orthography was devised for. These
  // seven are the whole of the macrolanguage; `alq` (Algonquin) is not among
  // them, and is left to miss for the `fat` reason — ISO 639-3 gives it a code
  // outside `oj`, so folding it here would be the judgement this map avoids.
  oj: ["ciw", "ojb", "ojc", "ojg", "ojs", "ojw", "otw"],
  // Bikol. The catalog is Central Bikol (Naga), which is `bcl` — the variety
  // Bikol publishing, broadcasting and the mother-tongue materials use. These
  // eight are the whole of the macrolanguage. Tagalog is not among them and
  // must not be added: `fil` is a language of its own with a catalog of its
  // own, and folding it here would serve a Tagalog reader Bikol.
  bik: ["bcl", "bln", "bto", "cts", "fbl", "lbl", "rbl", "ubl"],
  // Konkani. The catalog is Goan Konkani in Devanagari, the standard the Goa
  // Konkani Akademi publishes in. These two are the whole of the
  // macrolanguage; `gom` is the one `Intl.getCanonicalLocales` already folds.
  kok: ["gom", "knn"],
  // Dogri. The catalog is Dogri proper, which is `dgo` — the variety the
  // Eighth Schedule names and the J&K academy publishes in. These two are the
  // whole of the macrolanguage, and `xnr` (Kangri) is the member ICU leaves
  // unresolvable; `dgo` is the one it already folds.
  doi: ["dgo", "xnr"],
  // Fula. The catalog is Pulaar, the western variety of Senegal and
  // Mauritania, which is `fuc` — what CLDR fills a bare `ff` in as and the
  // one member it already folds. These nine are the whole of the
  // macrolanguage, so an Adamawa (`fub`) or Nigerian (`fuv`) Fulfulde reader
  // reaches Pulaar rather than English.
  ff: ["ffm", "fub", "fuc", "fue", "fuf", "fuh", "fui", "fuq", "fuv"],
  // Kanuri. The catalog is Central Kanuri, which is `knc` — what CLDR fills a
  // bare `kr` in as and the one member it already folds. These four are the
  // whole of the macrolanguage. `kby` (Manga Kanuri) maximizes to `kby-Arab`,
  // so a Manga reader most likely arrives in Ajami and is served Latin; that
  // is `locales/ha`'s asymmetry, and the answer to it is a second catalog
  // rather than a change here. `kbl` (Kanembu) is deliberately absent: ISO
  // 639-3 gives it a code outside `kr`, so folding it would be the judgement
  // this map avoids.
  kr: ["bms", "kby", "knc", "krt"],
  // Manding. `mnk` is a *member* rather than the macrolanguage — see
  // {@link LANGUAGE_ALIASES}'s `man` entry — so this lists the sibling
  // members, and deliberately omits `bam` and `dyu`, which `locales/bm` and
  // `locales/dyu` answer for themselves. `emk` is listed for the reason the
  // already-folded codes above are: `Intl.getCanonicalLocales` rewrites it to
  // `man`, so it would reach Mandinka through the alias anyway, and naming it
  // keeps the list the whole of a group rather than the leftovers of one.
  mnk: ["emk", "mku", "mlq", "msc", "mwk"],
  // Kongo. The catalog is written towards the Kikongo ya Bandundu standard.
  // These three are the whole of the macrolanguage; `kng` is the one
  // `Intl.getCanonicalLocales` already folds to `kg`, and it is listed for
  // the reason the other already-folded codes above are.
  //
  // `ktu` (Kituba) is deliberately absent, and this is the one exclusion here
  // that is not simply "it has a catalog of its own" — though it does, added
  // in #1685. Kituba is a creole *of* Kikongo rather than a variety of it,
  // ISO 639-3 gives it a code outside `kg`, and folding it would be the
  // judgement this map avoids. `mkw` (Kituba of the Republic of the Congo) is
  // absent for the same reason and is left to miss; answering it with `ktu`
  // would be defensible and is not a membership fact, so it is not done here.
  kg: ["kng", "kwy", "ldi"],
  // Songhay. `dje` is a *member* rather than the macrolanguage — the shape
  // {@link LANGUAGE_ALIASES}'s `man` entry explains — so this lists the
  // sibling members. These six are the whole of `son` apart from Zarma
  // itself, and ICU folds none of them, so every one of them reaches a
  // catalog only because this list exists.
  //
  // `son` itself is deliberately *not* aliased onto `dje`, and that is the
  // half worth reading. `man` earns its alias because CLDR decides for
  // itself which member a bare macrolanguage tag means —
  // `new Intl.Locale("man").maximize()` is `man-Latn-GM`, Mandinka's
  // country. `son` maximizes to nothing at all: CLDR adds no region, so it
  // has no opinion, and picking Zarma because it is the largest would be
  // exactly the judgement these maps exist to avoid. `negotiate.test.ts`
  // asserts the absent region rather than merely the absent entry, so a
  // change in ICU data that gave `son` a region would fail there and invite
  // someone to reconsider.
  //
  // `tda` (Tadaksahak) maximizes to `tda-Tfng-NE` — Tifinagh — so a reader
  // most likely arriving in that script is served Latin. That is
  // `locales/kr`'s asymmetry with `kby` in Ajami and `locales/ff`'s in
  // Adlam — the two other debts CLDR's own maximization creates, rather
  // than `locales/ha`'s, which CLDR does not: a bare `ha` maximizes to
  // `ha-Latn-NG`. The answer to it is a second catalog rather than a change
  // here.
  dje: ["ddn", "hmb", "khq", "ses", "tda", "twq"],
  // Buryat. The catalog is the Russia Buriat literary standard, which is what
  // ICU already folds `bxr` onto; `bxm` (Mongolia) and `bxu` (China) are the
  // members it does not, and they reach `locales/bua` only through this list.
  // `bxu` maximizes to `bxu-Mong-CN` — the Mongolian script — so a China
  // Buriat reader most likely arriving in that script is served Cyrillic.
  // That is `locales/kr`'s asymmetry with `kby` and `locales/dje`'s with
  // `tda`, and the answer to it is a second catalog rather than a change
  // here.
  bua: ["bxm", "bxr", "bxu"],
  // Komi. The catalog is Komi-Zyrian, which is what ICU folds `kpv` onto;
  // `koi` (Komi-Permyak) is the member it does not, and it is a written
  // standard of its own, so serving it Zyrian is the compromise every entry
  // in this map makes and `locales/kv`'s header records.
  kv: ["koi", "kpv"],
  // Mari. The catalog is Meadow Mari, which is what ICU folds `mhr` onto;
  // `mrj` (Hill Mari) is the member it does not, and, like `koi` above, it is
  // a written standard with an orthography of its own rather than a spelling
  // of this one.
  chm: ["mhr", "mrj"]
};
const MACROLANGUAGE_ALIASES = Object.fromEntries(
  Object.entries(MACROLANGUAGE_MEMBERS).flatMap(
    ([macro, members]) => members.map((member) => [member, macro])
  )
);
function applyLanguageAlias(tag) {
  const [language, ...rest] = tag.split("-");
  const lowered = language.toLowerCase();
  const alias = LANGUAGE_ALIASES[lowered] ?? MACROLANGUAGE_ALIASES[lowered];
  return alias === void 0 ? tag : [alias, ...rest].join("-");
}
function negotiateLocales(requested, available, options = {}) {
  const { defaultLocale = DEFAULT_LOCALE } = options;
  const availableWithDefault = available.includes(defaultLocale) ? available : [...available, defaultLocale];
  return negotiateLanguages(
    requested.map(applyLanguageAlias),
    availableWithDefault,
    {
      strategy: "filtering",
      defaultLocale
    }
  );
}
function resolveDocumentLocale(authoredLang, hostLocale) {
  const declared = (authoredLang ?? "").trim() || (hostLocale ?? "").trim();
  return normalizeLocaleTag(declared) || DEFAULT_LOCALE;
}
function resolveUiLocale(uiLocale, documentLocale) {
  return normalizeLocaleTag(uiLocale ?? "") || documentLocale;
}
function normalizeLocaleTag(tag) {
  const trimmed = tag.trim();
  if (trimmed === "") {
    return trimmed;
  }
  try {
    return new Intl.Locale(trimmed).toString();
  } catch {
    return trimmed;
  }
}
const PSEUDO_LOCALE = "en-XA";
const PSEUDO_RTL_LOCALE = "en-XB";
const PSEUDO_RTL_BRACKETS = ["‏»", "«‏"];
const ACCENT_MAP = {
  a: "á",
  b: "ƀ",
  c: "ç",
  d: "ð",
  e: "é",
  f: "ƒ",
  g: "ğ",
  h: "ĥ",
  i: "í",
  j: "ĵ",
  k: "ķ",
  l: "ļ",
  m: "ɱ",
  n: "ñ",
  o: "ó",
  p: "þ",
  q: "ʠ",
  r: "ř",
  s: "š",
  t: "ţ",
  u: "ú",
  v: "ṽ",
  w: "ŵ",
  x: "ẋ",
  y: "ý",
  z: "ž",
  A: "Á",
  B: "Ɓ",
  C: "Ç",
  D: "Ð",
  E: "É",
  F: "Ƒ",
  G: "Ğ",
  H: "Ĥ",
  I: "Í",
  J: "Ĵ",
  K: "Ķ",
  L: "Ļ",
  M: "Ṁ",
  N: "Ñ",
  O: "Ó",
  P: "Þ",
  Q: "Ɋ",
  R: "Ř",
  S: "Š",
  T: "Ţ",
  U: "Ú",
  V: "Ṽ",
  W: "Ŵ",
  X: "Ẋ",
  Y: "Ý",
  Z: "Ž"
};
const MESSAGE_PREFIX = /^([a-zA-Z][\w-]*\s*=\s*)/;
const TERM_PREFIX = /^(-[a-zA-Z][\w-]*\s*=\s*)/;
const ATTRIBUTE_PREFIX = /^(\s+\.[a-zA-Z][\w-]*\s*=\s*)/;
const VARIANT_PREFIX = /^(\s*\*?\[[^\]]*\]\s*)/;
const INDENT_PREFIX = /^(\s+)/;
function accentTextRuns(value) {
  let depth = 0;
  let letterCount = 0;
  let text = "";
  for (const char of value) {
    if (char === "{") {
      depth += 1;
      text += char;
      continue;
    }
    if (char === "}") {
      depth = Math.max(0, depth - 1);
      text += char;
      continue;
    }
    if (depth > 0) {
      text += char;
      continue;
    }
    const accented = ACCENT_MAP[char];
    if (accented === void 0) {
      text += char;
    } else {
      text += accented;
      letterCount += 1;
    }
  }
  return { text, letterCount };
}
function splitValue(line) {
  if (line.trim() === "" || line.trimStart().startsWith("#")) {
    return null;
  }
  for (const pattern of [
    MESSAGE_PREFIX,
    TERM_PREFIX,
    ATTRIBUTE_PREFIX,
    VARIANT_PREFIX,
    INDENT_PREFIX
  ]) {
    const match2 = pattern.exec(line);
    if (match2) {
      return { prefix: match2[1], value: line.slice(match2[1].length) };
    }
  }
  return { prefix: "", value: line };
}
function pseudoLocalize(ftlSource, options = {}) {
  const { expansion = 0.3, brackets = ["»", "«"] } = options;
  const [open, close] = brackets;
  return ftlSource.split("\n").map((line) => {
    const split = splitValue(line);
    if (split === null) {
      return line;
    }
    const { text, letterCount } = accentTextRuns(split.value);
    if (letterCount === 0) {
      return line;
    }
    const padding = "·".repeat(Math.ceil(letterCount * expansion));
    return `${split.prefix}${open}${text}${padding}${close}`;
  }).join("\n");
}
const RTL_SCRIPTS = /* @__PURE__ */ new Set([
  "Adlm",
  // Adlam
  "Arab",
  // Arabic
  "Aran",
  // Nastaliq
  "Armi",
  // Imperial Aramaic
  "Avst",
  // Avestan
  "Cprt",
  // Cypriot
  "Egyp",
  // Egyptian hieroglyphs
  "Elym",
  // Elymaic
  "Gara",
  // Garay
  "Hatr",
  // Hatran
  "Hebr",
  // Hebrew
  "Hung",
  // Old Hungarian
  "Khar",
  // Kharoshthi
  "Lydi",
  // Lydian
  "Mand",
  // Mandaic
  "Mani",
  // Manichaean
  "Mend",
  // Mende Kikakui
  "Merc",
  // Meroitic cursive
  "Mero",
  // Meroitic hieroglyphs
  "Narb",
  // Old North Arabian
  "Nbat",
  // Nabataean
  "Nkoo",
  // NKo
  "Orkh",
  // Old Turkic
  "Palm",
  // Palmyrene
  "Phli",
  // Inscriptional Pahlavi
  "Phlp",
  // Psalter Pahlavi
  "Phnx",
  // Phoenician
  "Prti",
  // Inscriptional Parthian
  "Rohg",
  // Hanifi Rohingya
  "Samr",
  // Samaritan
  "Sarb",
  // Old South Arabian
  "Sogd",
  // Sogdian
  "Sogo",
  // Old Sogdian
  "Syrc",
  // Syriac
  "Thaa",
  // Thaana
  "Todr",
  // Todhri
  "Yezi"
  // Yezidi
]);
const RTL_LANGUAGES = /* @__PURE__ */ new Set([
  "ae",
  // Avestan
  "ar",
  // Arabic
  "arc",
  // Aramaic
  "bcc",
  // Southern Balochi
  "bqi",
  // Bakhtiari
  "ckb",
  // Central Kurdish
  "dv",
  // Divehi
  "fa",
  // Persian
  "glk",
  // Gilaki
  "he",
  // Hebrew
  "iw",
  // Hebrew (retired)
  "ji",
  // Yiddish (retired)
  "khw",
  // Khowar
  "ks",
  // Kashmiri
  "lrc",
  // Northern Luri
  "mzn",
  // Mazanderani
  "nqo",
  // NKo
  "pnb",
  // Western Punjabi
  "prs",
  // Dari
  "ps",
  // Pashto
  "sd",
  // Sindhi
  "ug",
  // Uyghur
  "ur",
  // Urdu
  "yi"
  // Yiddish
]);
function directionOf(tag) {
  const trimmed = tag.trim();
  if (trimmed === "") {
    return "ltr";
  }
  if (trimmed.toLowerCase() === PSEUDO_RTL_LOCALE.toLowerCase()) {
    return "rtl";
  }
  try {
    const locale = new Intl.Locale(trimmed);
    const script = locale.maximize().script ?? locale.script;
    if (script) {
      return RTL_SCRIPTS.has(script) ? "rtl" : "ltr";
    }
    return RTL_LANGUAGES.has(locale.language ?? "") ? "rtl" : "ltr";
  } catch {
    return directionFromRawSubtags(trimmed);
  }
}
function directionFromRawSubtags(tag) {
  const subtags = tag.split(/[-_]/);
  for (const subtag of subtags.slice(1)) {
    if (/^[A-Za-z]{4}$/.test(subtag)) {
      const script = subtag[0].toUpperCase() + subtag.slice(1).toLowerCase();
      return RTL_SCRIPTS.has(script) ? "rtl" : "ltr";
    }
  }
  return RTL_LANGUAGES.has(subtags[0].toLowerCase()) ? "rtl" : "ltr";
}
const BIDI_FORMATTING_CHARACTERS = /[\u2066-\u2069\u200E\u200F]/g;
function stripBidiIsolates(text) {
  return text.replace(BIDI_FORMATTING_CHARACTERS, "");
}
const BUNDLED_CHROME_CATALOGS = {
  [DEFAULT_LOCALE]: englishResources(CHROME_NAMESPACES)
};
const pseudoChromeCatalogs = {};
function getPseudoChromeCatalog(locale) {
  return pseudoChromeCatalogs[locale] ??= pseudoLocalize(
    BUNDLED_CHROME_CATALOGS[DEFAULT_LOCALE],
    locale === PSEUDO_RTL_LOCALE ? { brackets: PSEUDO_RTL_BRACKETS } : void 0
  );
}
function chromeResources(normalizedUiLocale, hostResources) {
  const resources = { ...BUNDLED_CHROME_CATALOGS };
  if (normalizedUiLocale === PSEUDO_LOCALE || normalizedUiLocale === PSEUDO_RTL_LOCALE) {
    resources[normalizedUiLocale] = getPseudoChromeCatalog(normalizedUiLocale);
  }
  return Object.assign(resources, hostResources);
}
function isolatesPlaceables(locale) {
  const language = locale.split(/[-_]/)[0].toLowerCase();
  return language !== DEFAULT_LOCALE;
}
function createChromeTranslator(uiLocale, hostResources) {
  const normalized = normalizeLocaleTag(uiLocale) || DEFAULT_LOCALE;
  const resources = chromeResources(normalized, hostResources);
  const locales = negotiateLocales([normalized], Object.keys(resources));
  return createTranslator(locales, resources, {
    useIsolating: isolatesPlaceables(normalized)
  });
}
let enChromeTranslator;
const EN_CHROME_TRANSLATOR = (key, args, fallback) => (enChromeTranslator ??= createTranslator([], {}))(key, args, fallback);
const SUPPORTED_LOCALES = [
  {
    locale: "en",
    englishName: "English",
    endonym: "English",
    label: "English"
  },
  {
    locale: "ace",
    englishName: "Acehnese",
    endonym: "Acehnese",
    label: "Acehnese"
  },
  {
    locale: "af",
    englishName: "Afrikaans",
    endonym: "Afrikaans",
    label: "Afrikaans"
  },
  { locale: "ak", englishName: "Akan", endonym: "Akan", label: "Akan" },
  {
    locale: "am",
    englishName: "Amharic",
    endonym: "አማርኛ",
    label: "Amharic (አማርኛ)"
  },
  {
    locale: "ar",
    englishName: "Arabic",
    endonym: "العربية",
    label: "Arabic (العربية)"
  },
  {
    locale: "arn",
    englishName: "Mapuche",
    endonym: "Mapuche",
    label: "Mapuche"
  },
  {
    locale: "as",
    englishName: "Assamese",
    endonym: "অসমীয়া",
    label: "Assamese (অসমীয়া)"
  },
  {
    locale: "ast",
    englishName: "Asturian",
    endonym: "asturianu",
    label: "Asturian (asturianu)"
  },
  { locale: "ay", englishName: "Aymara", endonym: "Aymara", label: "Aymara" },
  {
    locale: "az",
    englishName: "Azerbaijani",
    endonym: "azərbaycan",
    label: "Azerbaijani (azərbaycan)"
  },
  {
    locale: "ba",
    englishName: "Bashkir",
    endonym: "башҡорт",
    label: "Bashkir (башҡорт)"
  },
  {
    locale: "ban",
    englishName: "Balinese",
    endonym: "Balinese",
    label: "Balinese"
  },
  {
    locale: "bci",
    englishName: "Baoulé",
    endonym: "bci",
    label: "Baoulé (bci)"
  },
  {
    locale: "be",
    englishName: "Belarusian",
    endonym: "беларуская",
    label: "Belarusian (беларуская)"
  },
  {
    locale: "bem",
    englishName: "Bemba",
    endonym: "Ichibemba",
    label: "Bemba (Ichibemba)"
  },
  {
    locale: "bg",
    englishName: "Bulgarian",
    endonym: "български",
    label: "Bulgarian (български)"
  },
  {
    locale: "bho",
    englishName: "Bhojpuri",
    endonym: "भोजपुरी",
    label: "Bhojpuri (भोजपुरी)"
  },
  { locale: "bik", englishName: "Bikol", endonym: "Bikol", label: "Bikol" },
  { locale: "bin", englishName: "Bini", endonym: "Bini", label: "Bini" },
  {
    locale: "bm",
    englishName: "Bambara",
    endonym: "bamanakan",
    label: "Bambara (bamanakan)"
  },
  {
    locale: "bn",
    englishName: "Bangla",
    endonym: "বাংলা",
    label: "Bangla (বাংলা)"
  },
  {
    locale: "bo",
    englishName: "Tibetan",
    endonym: "བོད་སྐད་",
    label: "Tibetan (བོད་སྐད་)"
  },
  {
    locale: "br",
    englishName: "Breton",
    endonym: "brezhoneg",
    label: "Breton (brezhoneg)"
  },
  { locale: "brx", englishName: "Bodo", endonym: "बर’", label: "Bodo (बर’)" },
  {
    locale: "bs",
    englishName: "Bosnian",
    endonym: "bosanski",
    label: "Bosnian (bosanski)"
  },
  {
    locale: "bua",
    englishName: "Buriat",
    endonym: "буряад",
    label: "Buriat (буряад)"
  },
  { locale: "bum", englishName: "Bulu", endonym: "Bulu", label: "Bulu" },
  {
    locale: "ca",
    englishName: "Catalan",
    endonym: "català",
    label: "Catalan (català)"
  },
  {
    locale: "ce",
    englishName: "Chechen",
    endonym: "нохчийн",
    label: "Chechen (нохчийн)"
  },
  {
    locale: "ceb",
    englishName: "Cebuano",
    endonym: "Cebuano",
    label: "Cebuano"
  },
  {
    locale: "ch",
    englishName: "Chamorro",
    endonym: "Chamorro",
    label: "Chamorro"
  },
  { locale: "chm", englishName: "Mari", endonym: "Mari", label: "Mari" },
  {
    locale: "co",
    englishName: "Corsican",
    endonym: "Corsican",
    label: "Corsican"
  },
  {
    locale: "cs",
    englishName: "Czech",
    endonym: "čeština",
    label: "Czech (čeština)"
  },
  {
    locale: "cv",
    englishName: "Chuvash",
    endonym: "чӑваш чӗлхи",
    label: "Chuvash (чӑваш чӗлхи)"
  },
  {
    locale: "cy",
    englishName: "Welsh",
    endonym: "Cymraeg",
    label: "Welsh (Cymraeg)"
  },
  {
    locale: "da",
    englishName: "Danish",
    endonym: "dansk",
    label: "Danish (dansk)"
  },
  {
    locale: "dag",
    englishName: "Dagbani",
    endonym: "Dagbanli",
    label: "Dagbani (Dagbanli)"
  },
  {
    locale: "de",
    englishName: "German",
    endonym: "Deutsch",
    label: "German (Deutsch)"
  },
  {
    locale: "dje",
    englishName: "Zarma",
    endonym: "Zarmaciine",
    label: "Zarma (Zarmaciine)"
  },
  {
    locale: "doi",
    englishName: "Dogri",
    endonym: "डोगरी",
    label: "Dogri (डोगरी)"
  },
  { locale: "dv", englishName: "Divehi", endonym: "Divehi", label: "Divehi" },
  {
    locale: "dyo",
    englishName: "Jola-Fonyi",
    endonym: "joola",
    label: "Jola-Fonyi (joola)"
  },
  { locale: "dyu", englishName: "Dyula", endonym: "Dyula", label: "Dyula" },
  {
    locale: "dz",
    englishName: "Dzongkha",
    endonym: "རྫོང་ཁ",
    label: "Dzongkha (རྫོང་ཁ)"
  },
  {
    locale: "ee",
    englishName: "Ewe",
    endonym: "eʋegbe",
    label: "Ewe (eʋegbe)"
  },
  { locale: "efi", englishName: "Efik", endonym: "Efik", label: "Efik" },
  {
    locale: "el",
    englishName: "Greek",
    endonym: "Ελληνικά",
    label: "Greek (Ελληνικά)"
  },
  {
    locale: "es",
    englishName: "Spanish",
    endonym: "español",
    label: "Spanish (español)"
  },
  {
    locale: "et",
    englishName: "Estonian",
    endonym: "eesti",
    label: "Estonian (eesti)"
  },
  {
    locale: "eu",
    englishName: "Basque",
    endonym: "euskara",
    label: "Basque (euskara)"
  },
  {
    locale: "ewo",
    englishName: "Ewondo",
    endonym: "ewondo",
    label: "Ewondo (ewondo)"
  },
  {
    locale: "fa",
    englishName: "Persian",
    endonym: "فارسی",
    label: "Persian (فارسی)"
  },
  {
    locale: "ff",
    englishName: "Fula",
    endonym: "Pulaar",
    label: "Fula (Pulaar)"
  },
  {
    locale: "fi",
    englishName: "Finnish",
    endonym: "suomi",
    label: "Finnish (suomi)"
  },
  {
    locale: "fil",
    englishName: "Filipino",
    endonym: "Filipino",
    label: "Filipino"
  },
  { locale: "fj", englishName: "Fijian", endonym: "Fijian", label: "Fijian" },
  {
    locale: "fo",
    englishName: "Faroese",
    endonym: "føroyskt",
    label: "Faroese (føroyskt)"
  },
  { locale: "fon", englishName: "Fon", endonym: "Fon", label: "Fon" },
  {
    locale: "fr",
    englishName: "French",
    endonym: "français",
    label: "French (français)"
  },
  {
    locale: "fy",
    englishName: "Western Frisian",
    endonym: "Frysk",
    label: "Western Frisian (Frysk)"
  },
  {
    locale: "ga",
    englishName: "Irish",
    endonym: "Gaeilge",
    label: "Irish (Gaeilge)"
  },
  { locale: "gaa", englishName: "Ga", endonym: "Gã", label: "Ga (Gã)" },
  {
    locale: "gd",
    englishName: "Scottish Gaelic",
    endonym: "Gàidhlig",
    label: "Scottish Gaelic (Gàidhlig)"
  },
  {
    locale: "gl",
    englishName: "Galician",
    endonym: "galego",
    label: "Galician (galego)"
  },
  {
    locale: "gn",
    englishName: "Guarani",
    endonym: "Guarani",
    label: "Guarani"
  },
  {
    locale: "gu",
    englishName: "Gujarati",
    endonym: "ગુજરાતી",
    label: "Gujarati (ગુજરાતી)"
  },
  { locale: "ha", englishName: "Hausa", endonym: "Hausa", label: "Hausa" },
  {
    locale: "haw",
    englishName: "Hawaiian",
    endonym: "ʻŌlelo Hawaiʻi",
    label: "Hawaiian (ʻŌlelo Hawaiʻi)"
  },
  {
    locale: "he",
    englishName: "Hebrew",
    endonym: "עברית",
    label: "Hebrew (עברית)"
  },
  {
    locale: "hi",
    englishName: "Hindi",
    endonym: "हिन्दी",
    label: "Hindi (हिन्दी)"
  },
  {
    locale: "hil",
    englishName: "Hiligaynon",
    endonym: "Hiligaynon",
    label: "Hiligaynon"
  },
  {
    locale: "hnj",
    englishName: "Hmong Njua",
    endonym: "Hmong Njua",
    label: "Hmong Njua"
  },
  {
    locale: "hr",
    englishName: "Croatian",
    endonym: "hrvatski",
    label: "Croatian (hrvatski)"
  },
  {
    locale: "ht",
    englishName: "Haitian Creole",
    endonym: "Haitian Creole",
    label: "Haitian Creole"
  },
  {
    locale: "hu",
    englishName: "Hungarian",
    endonym: "magyar",
    label: "Hungarian (magyar)"
  },
  {
    locale: "hy",
    englishName: "Armenian",
    endonym: "հայերեն",
    label: "Armenian (հայերեն)"
  },
  {
    locale: "id",
    englishName: "Indonesian",
    endonym: "Indonesia",
    label: "Indonesian (Indonesia)"
  },
  { locale: "ig", englishName: "Igbo", endonym: "Igbo", label: "Igbo" },
  { locale: "ilo", englishName: "Iloko", endonym: "Iloko", label: "Iloko" },
  {
    locale: "is",
    englishName: "Icelandic",
    endonym: "íslenska",
    label: "Icelandic (íslenska)"
  },
  {
    locale: "it",
    englishName: "Italian",
    endonym: "italiano",
    label: "Italian (italiano)"
  },
  {
    locale: "ja",
    englishName: "Japanese",
    endonym: "日本語",
    label: "Japanese (日本語)"
  },
  {
    locale: "jv",
    englishName: "Javanese",
    endonym: "Jawa",
    label: "Javanese (Jawa)"
  },
  {
    locale: "ka",
    englishName: "Georgian",
    endonym: "ქართული",
    label: "Georgian (ქართული)"
  },
  {
    locale: "kab",
    englishName: "Kabyle",
    endonym: "Taqbaylit",
    label: "Kabyle (Taqbaylit)"
  },
  {
    locale: "kbp",
    englishName: "Kabiyè",
    endonym: "Kabɩyɛ",
    label: "Kabiyè (Kabɩyɛ)"
  },
  { locale: "kg", englishName: "Kongo", endonym: "Kongo", label: "Kongo" },
  {
    locale: "ki",
    englishName: "Kikuyu",
    endonym: "Gikuyu",
    label: "Kikuyu (Gikuyu)"
  },
  {
    locale: "kk",
    englishName: "Kazakh",
    endonym: "қазақ тілі",
    label: "Kazakh (қазақ тілі)"
  },
  {
    locale: "km",
    englishName: "Khmer",
    endonym: "ខ្មែរ",
    label: "Khmer (ខ្មែរ)"
  },
  {
    locale: "kmb",
    englishName: "Kimbundu",
    endonym: "Kimbundu",
    label: "Kimbundu"
  },
  {
    locale: "kn",
    englishName: "Kannada",
    endonym: "ಕನ್ನಡ",
    label: "Kannada (ಕನ್ನಡ)"
  },
  {
    locale: "ko",
    englishName: "Korean",
    endonym: "한국어",
    label: "Korean (한국어)"
  },
  {
    locale: "kok",
    englishName: "Konkani",
    endonym: "कोंकणी",
    label: "Konkani (कोंकणी)"
  },
  {
    locale: "kpe",
    englishName: "Kpelle",
    endonym: "Kpelle",
    label: "Kpelle"
  },
  { locale: "kr", englishName: "Kanuri", endonym: "Kanuri", label: "Kanuri" },
  { locale: "kri", englishName: "Krio", endonym: "Krio", label: "Krio" },
  {
    locale: "ks",
    englishName: "Kashmiri",
    endonym: "کٲشُر",
    label: "Kashmiri (کٲشُر)"
  },
  {
    locale: "ktu",
    englishName: "Kituba",
    endonym: "Kikongo ya leta",
    label: "Kituba (Kikongo ya leta)"
  },
  { locale: "kv", englishName: "Komi", endonym: "Komi", label: "Komi" },
  {
    locale: "ky",
    englishName: "Kyrgyz",
    endonym: "кыргызча",
    label: "Kyrgyz (кыргызча)"
  },
  {
    locale: "lb",
    englishName: "Luxembourgish",
    endonym: "Lëtzebuergesch",
    label: "Luxembourgish (Lëtzebuergesch)"
  },
  {
    locale: "lg",
    englishName: "Ganda",
    endonym: "Luganda",
    label: "Ganda (Luganda)"
  },
  {
    locale: "ln",
    englishName: "Lingala",
    endonym: "lingála",
    label: "Lingala (lingála)"
  },
  { locale: "lo", englishName: "Lao", endonym: "ລາວ", label: "Lao (ລາວ)" },
  { locale: "lom", englishName: "Loma", endonym: "lom", label: "Loma (lom)" },
  {
    locale: "lt",
    englishName: "Lithuanian",
    endonym: "lietuvių",
    label: "Lithuanian (lietuvių)"
  },
  {
    locale: "lua",
    englishName: "Luba-Lulua",
    endonym: "Luba-Lulua",
    label: "Luba-Lulua"
  },
  {
    locale: "luo",
    englishName: "Luo",
    endonym: "Dholuo",
    label: "Luo (Dholuo)"
  },
  {
    locale: "lv",
    englishName: "Latvian",
    endonym: "latviešu",
    label: "Latvian (latviešu)"
  },
  {
    locale: "mad",
    englishName: "Madurese",
    endonym: "Madurese",
    label: "Madurese"
  },
  {
    locale: "mai",
    englishName: "Maithili",
    endonym: "मैथिली",
    label: "Maithili (मैथिली)"
  },
  { locale: "men", englishName: "Mende", endonym: "Mende", label: "Mende" },
  {
    locale: "mg",
    englishName: "Malagasy",
    endonym: "Malagasy",
    label: "Malagasy"
  },
  { locale: "mi", englishName: "Māori", endonym: "Māori", label: "Māori" },
  {
    locale: "min",
    englishName: "Minangkabau",
    endonym: "Minangkabau",
    label: "Minangkabau"
  },
  {
    locale: "mk",
    englishName: "Macedonian",
    endonym: "македонски",
    label: "Macedonian (македонски)"
  },
  {
    locale: "ml",
    englishName: "Malayalam",
    endonym: "മലയാളം",
    label: "Malayalam (മലയാളം)"
  },
  {
    locale: "mn",
    englishName: "Mongolian",
    endonym: "монгол",
    label: "Mongolian (монгол)"
  },
  {
    locale: "mni",
    englishName: "Manipuri",
    endonym: "মৈতৈলোন্",
    label: "Manipuri (মৈতৈলোন্)"
  },
  {
    locale: "mnk",
    englishName: "Mandinka",
    endonym: "Mandinkakaŋo",
    label: "Mandinka (Mandinkakaŋo)"
  },
  { locale: "mos", englishName: "Mossi", endonym: "Mossi", label: "Mossi" },
  {
    locale: "mr",
    englishName: "Marathi",
    endonym: "मराठी",
    label: "Marathi (मराठी)"
  },
  {
    locale: "ms",
    englishName: "Malay",
    endonym: "Melayu",
    label: "Malay (Melayu)"
  },
  {
    locale: "mt",
    englishName: "Maltese",
    endonym: "Malti",
    label: "Maltese (Malti)"
  },
  {
    locale: "my",
    englishName: "Burmese",
    endonym: "မြန်မာ",
    label: "Burmese (မြန်မာ)"
  },
  { locale: "myv", englishName: "Erzya", endonym: "Erzya", label: "Erzya" },
  {
    locale: "nah",
    englishName: "Nahuatl",
    endonym: "Nāhuatl",
    label: "Nahuatl (Nāhuatl)"
  },
  {
    locale: "nb",
    englishName: "Norwegian Bokmål",
    endonym: "norsk bokmål",
    label: "Norwegian Bokmål (norsk bokmål)"
  },
  {
    locale: "nds",
    englishName: "Low German",
    endonym: "Neddersass’sch",
    label: "Low German (Neddersass’sch)"
  },
  {
    locale: "ne",
    englishName: "Nepali",
    endonym: "नेपाली",
    label: "Nepali (नेपाली)"
  },
  {
    locale: "nl",
    englishName: "Dutch",
    endonym: "Nederlands",
    label: "Dutch (Nederlands)"
  },
  {
    locale: "nso",
    englishName: "Northern Sotho",
    endonym: "Sesotho sa Leboa",
    label: "Northern Sotho (Sesotho sa Leboa)"
  },
  { locale: "ny", englishName: "Nyanja", endonym: "Nyanja", label: "Nyanja" },
  {
    locale: "nyn",
    englishName: "Nyankole",
    endonym: "Runyankore",
    label: "Nyankole (Runyankore)"
  },
  {
    locale: "oc",
    englishName: "Occitan",
    endonym: "occitan",
    label: "Occitan (occitan)"
  },
  { locale: "oj", englishName: "Ojibwa", endonym: "Ojibwa", label: "Ojibwa" },
  {
    locale: "om",
    englishName: "Oromo",
    endonym: "Oromoo",
    label: "Oromo (Oromoo)"
  },
  {
    locale: "or",
    englishName: "Odia",
    endonym: "ଓଡ଼ିଆ",
    label: "Odia (ଓଡ଼ିଆ)"
  },
  {
    locale: "os",
    englishName: "Ossetic",
    endonym: "ирон",
    label: "Ossetic (ирон)"
  },
  {
    locale: "pa",
    englishName: "Punjabi",
    endonym: "ਪੰਜਾਬੀ",
    label: "Punjabi (ਪੰਜਾਬੀ)"
  },
  {
    locale: "pam",
    englishName: "Pampanga",
    endonym: "Pampanga",
    label: "Pampanga"
  },
  {
    locale: "pcm",
    englishName: "Nigerian Pidgin",
    endonym: "Naijíriá Píjin",
    label: "Nigerian Pidgin (Naijíriá Píjin)"
  },
  {
    locale: "pl",
    englishName: "Polish",
    endonym: "polski",
    label: "Polish (polski)"
  },
  {
    locale: "ps",
    englishName: "Pashto",
    endonym: "پښتو",
    label: "Pashto (پښتو)"
  },
  {
    locale: "pt",
    englishName: "Portuguese",
    endonym: "português",
    label: "Portuguese (português)"
  },
  {
    locale: "qu",
    englishName: "Quechua",
    endonym: "Runasimi",
    label: "Quechua (Runasimi)"
  },
  {
    locale: "quc",
    englishName: "Kʼicheʼ",
    endonym: "Kʼicheʼ",
    label: "Kʼicheʼ"
  },
  {
    locale: "rm",
    englishName: "Romansh",
    endonym: "rumantsch",
    label: "Romansh (rumantsch)"
  },
  {
    locale: "rn",
    englishName: "Rundi",
    endonym: "Ikirundi",
    label: "Rundi (Ikirundi)"
  },
  {
    locale: "ro",
    englishName: "Romanian",
    endonym: "română",
    label: "Romanian (română)"
  },
  {
    locale: "ru",
    englishName: "Russian",
    endonym: "русский",
    label: "Russian (русский)"
  },
  {
    locale: "rw",
    englishName: "Kinyarwanda",
    endonym: "Ikinyarwanda",
    label: "Kinyarwanda (Ikinyarwanda)"
  },
  {
    locale: "sa",
    englishName: "Sanskrit",
    endonym: "संस्कृत भाषा",
    label: "Sanskrit (संस्कृत भाषा)"
  },
  {
    locale: "sah",
    englishName: "Yakut",
    endonym: "саха тыла",
    label: "Yakut (саха тыла)"
  },
  {
    locale: "sat",
    englishName: "Santali",
    endonym: "ᱥᱟᱱᱛᱟᱲᱤ",
    label: "Santali (ᱥᱟᱱᱛᱟᱲᱤ)"
  },
  {
    locale: "sc",
    englishName: "Sardinian",
    endonym: "sardu",
    label: "Sardinian (sardu)"
  },
  {
    locale: "scn",
    englishName: "Sicilian",
    endonym: "sicilianu",
    label: "Sicilian (sicilianu)"
  },
  {
    locale: "sd",
    englishName: "Sindhi",
    endonym: "سنڌي",
    label: "Sindhi (سنڌي)"
  },
  {
    locale: "se",
    englishName: "Northern Sami",
    endonym: "davvisámegiella",
    label: "Northern Sami (davvisámegiella)"
  },
  {
    locale: "sg",
    englishName: "Sango",
    endonym: "Sängö",
    label: "Sango (Sängö)"
  },
  {
    locale: "shi",
    englishName: "Tachelhit",
    endonym: "ⵜⴰⵛⵍⵃⵉⵜ",
    label: "Tachelhit (ⵜⴰⵛⵍⵃⵉⵜ)"
  },
  {
    locale: "si",
    englishName: "Sinhala",
    endonym: "සිංහල",
    label: "Sinhala (සිංහල)"
  },
  {
    locale: "sk",
    englishName: "Slovak",
    endonym: "slovenčina",
    label: "Slovak (slovenčina)"
  },
  {
    locale: "sl",
    englishName: "Slovenian",
    endonym: "slovenščina",
    label: "Slovenian (slovenščina)"
  },
  { locale: "sm", englishName: "Samoan", endonym: "Samoan", label: "Samoan" },
  {
    locale: "sn",
    englishName: "Shona",
    endonym: "chiShona",
    label: "Shona (chiShona)"
  },
  {
    locale: "so",
    englishName: "Somali",
    endonym: "Soomaali",
    label: "Somali (Soomaali)"
  },
  {
    locale: "sq",
    englishName: "Albanian",
    endonym: "shqip",
    label: "Albanian (shqip)"
  },
  {
    locale: "sr",
    englishName: "Serbian",
    endonym: "српски",
    label: "Serbian (српски)"
  },
  { locale: "ss", englishName: "Swati", endonym: "Swati", label: "Swati" },
  {
    locale: "st",
    englishName: "Southern Sotho",
    endonym: "Sesotho",
    label: "Southern Sotho (Sesotho)"
  },
  {
    locale: "su",
    englishName: "Sundanese",
    endonym: "Basa Sunda",
    label: "Sundanese (Basa Sunda)"
  },
  { locale: "sus", englishName: "Susu", endonym: "Susu", label: "Susu" },
  {
    locale: "sv",
    englishName: "Swedish",
    endonym: "svenska",
    label: "Swedish (svenska)"
  },
  {
    locale: "sw",
    englishName: "Swahili",
    endonym: "Kiswahili",
    label: "Swahili (Kiswahili)"
  },
  {
    locale: "ta",
    englishName: "Tamil",
    endonym: "தமிழ்",
    label: "Tamil (தமிழ்)"
  },
  {
    locale: "te",
    englishName: "Telugu",
    endonym: "తెలుగు",
    label: "Telugu (తెలుగు)"
  },
  { locale: "tem", englishName: "Timne", endonym: "Timne", label: "Timne" },
  { locale: "tet", englishName: "Tetum", endonym: "Tetum", label: "Tetum" },
  {
    locale: "tg",
    englishName: "Tajik",
    endonym: "тоҷикӣ",
    label: "Tajik (тоҷикӣ)"
  },
  { locale: "th", englishName: "Thai", endonym: "ไทย", label: "Thai (ไทย)" },
  {
    locale: "ti",
    englishName: "Tigrinya",
    endonym: "ትግርኛ",
    label: "Tigrinya (ትግርኛ)"
  },
  { locale: "tiv", englishName: "Tiv", endonym: "Tiv", label: "Tiv" },
  {
    locale: "tk",
    englishName: "Turkmen",
    endonym: "türkmen dili",
    label: "Turkmen (türkmen dili)"
  },
  {
    locale: "tlh",
    englishName: "Klingon",
    endonym: "Klingon",
    label: "Klingon"
  },
  {
    locale: "tn",
    englishName: "Tswana",
    endonym: "Setswana",
    label: "Tswana (Setswana)"
  },
  {
    locale: "to",
    englishName: "Tongan",
    endonym: "lea fakatonga",
    label: "Tongan (lea fakatonga)"
  },
  {
    locale: "tpi",
    englishName: "Tok Pisin",
    endonym: "Tok Pisin",
    label: "Tok Pisin"
  },
  {
    locale: "tr",
    englishName: "Turkish",
    endonym: "Türkçe",
    label: "Turkish (Türkçe)"
  },
  { locale: "ts", englishName: "Tsonga", endonym: "Tsonga", label: "Tsonga" },
  {
    locale: "tt",
    englishName: "Tatar",
    endonym: "татар",
    label: "Tatar (татар)"
  },
  {
    locale: "ty",
    englishName: "Tahitian",
    endonym: "Tahitian",
    label: "Tahitian"
  },
  {
    locale: "tyv",
    englishName: "Tuvinian",
    endonym: "Тыва",
    label: "Tuvinian (Тыва)"
  },
  {
    locale: "udm",
    englishName: "Udmurt",
    endonym: "Udmurt",
    label: "Udmurt"
  },
  {
    locale: "ug",
    englishName: "Uyghur",
    endonym: "ئۇيغۇرچە",
    label: "Uyghur (ئۇيغۇرچە)"
  },
  {
    locale: "uk",
    englishName: "Ukrainian",
    endonym: "українська",
    label: "Ukrainian (українська)"
  },
  {
    locale: "umb",
    englishName: "Umbundu",
    endonym: "Umbundu",
    label: "Umbundu"
  },
  {
    locale: "ur",
    englishName: "Urdu",
    endonym: "اردو",
    label: "Urdu (اردو)"
  },
  {
    locale: "urh",
    englishName: "Urhobo",
    endonym: "urh",
    label: "Urhobo (urh)"
  },
  {
    locale: "uz",
    englishName: "Uzbek",
    endonym: "o‘zbek",
    label: "Uzbek (o‘zbek)"
  },
  { locale: "ve", englishName: "Venda", endonym: "Venda", label: "Venda" },
  {
    locale: "vi",
    englishName: "Vietnamese",
    endonym: "Tiếng Việt",
    label: "Vietnamese (Tiếng Việt)"
  },
  { locale: "war", englishName: "Waray", endonym: "Waray", label: "Waray" },
  { locale: "wo", englishName: "Wolof", endonym: "Wolof", label: "Wolof" },
  {
    locale: "xal",
    englishName: "Kalmyk",
    endonym: "Kalmyk",
    label: "Kalmyk"
  },
  {
    locale: "xh",
    englishName: "Xhosa",
    endonym: "IsiXhosa",
    label: "Xhosa (IsiXhosa)"
  },
  {
    locale: "yi",
    englishName: "Yiddish",
    endonym: "ייִדיש",
    label: "Yiddish (ייִדיש)"
  },
  {
    locale: "yo",
    englishName: "Yoruba",
    endonym: "Èdè Yorùbá",
    label: "Yoruba (Èdè Yorùbá)"
  },
  {
    locale: "zgh",
    englishName: "Standard Moroccan Tamazight",
    endonym: "ⵜⴰⵎⴰⵣⵉⵖⵜ",
    label: "Standard Moroccan Tamazight (ⵜⴰⵎⴰⵣⵉⵖⵜ)"
  },
  {
    locale: "zh-Hans",
    englishName: "Simplified Chinese",
    endonym: "简体中文",
    label: "Simplified Chinese (简体中文)"
  },
  {
    locale: "zh-Hant",
    englishName: "Traditional Chinese",
    endonym: "繁體中文",
    label: "Traditional Chinese (繁體中文)"
  },
  {
    locale: "zu",
    englishName: "Zulu",
    endonym: "isiZulu",
    label: "Zulu (isiZulu)"
  }
];
const CODE_SPLIT_CATALOGS = typeof __DOENET_CODE_SPLIT_CATALOGS__ === "undefined" || __DOENET_CODE_SPLIT_CATALOGS__;
const LAZY_CATALOG_MODULES = CODE_SPLIT_CATALOGS ? /* @__PURE__ */ Object.assign({
  "../locales/ace/chrome.ftl": () => import("./chrome-BKPKksFe.js").then((m) => m["default"]),
  "../locales/ace/content.ftl": () => import("./content-CdU7HF7V.js").then((m) => m["default"]),
  "../locales/ace/diagnostics.ftl": () => import("./diagnostics-Dh9by5Ih.js").then((m) => m["default"]),
  "../locales/ace/editor.ftl": () => import("./editor-Mm8ZIUbW.js").then((m) => m["default"]),
  "../locales/af/chrome.ftl": () => import("./chrome-CvP6Ky7F.js").then((m) => m["default"]),
  "../locales/af/content.ftl": () => import("./content-pq_bAgY0.js").then((m) => m["default"]),
  "../locales/af/diagnostics.ftl": () => import("./diagnostics-OScGSKm8.js").then((m) => m["default"]),
  "../locales/af/editor.ftl": () => import("./editor-DNyL0OUz.js").then((m) => m["default"]),
  "../locales/ak/chrome.ftl": () => import("./chrome-DT_D7Qn-.js").then((m) => m["default"]),
  "../locales/ak/content.ftl": () => import("./content-CtvUULXH.js").then((m) => m["default"]),
  "../locales/ak/diagnostics.ftl": () => import("./diagnostics-CDApdn12.js").then((m) => m["default"]),
  "../locales/ak/editor.ftl": () => import("./editor-bXWDrC_X.js").then((m) => m["default"]),
  "../locales/am/chrome.ftl": () => import("./chrome-u2v_7lQ5.js").then((m) => m["default"]),
  "../locales/am/content.ftl": () => import("./content-4FiDiAkH.js").then((m) => m["default"]),
  "../locales/am/diagnostics.ftl": () => import("./diagnostics-CS128Gmq.js").then((m) => m["default"]),
  "../locales/am/editor.ftl": () => import("./editor-DT1b-NST.js").then((m) => m["default"]),
  "../locales/ar/chrome.ftl": () => import("./chrome-C4ev1rYx.js").then((m) => m["default"]),
  "../locales/ar/content.ftl": () => import("./content-GsVI9JDC.js").then((m) => m["default"]),
  "../locales/ar/diagnostics.ftl": () => import("./diagnostics-DBZQRmiG.js").then((m) => m["default"]),
  "../locales/ar/editor.ftl": () => import("./editor-Det6IyoR.js").then((m) => m["default"]),
  "../locales/arn/chrome.ftl": () => import("./chrome-DsmDFcz1.js").then((m) => m["default"]),
  "../locales/arn/content.ftl": () => import("./content-BSvBgI69.js").then((m) => m["default"]),
  "../locales/arn/diagnostics.ftl": () => import("./diagnostics-CBrmujMZ.js").then((m) => m["default"]),
  "../locales/arn/editor.ftl": () => import("./editor-CY5taf8G.js").then((m) => m["default"]),
  "../locales/as/chrome.ftl": () => import("./chrome-B6zluygH.js").then((m) => m["default"]),
  "../locales/as/content.ftl": () => import("./content-Cr334N5t.js").then((m) => m["default"]),
  "../locales/as/diagnostics.ftl": () => import("./diagnostics-JMNeWZj3.js").then((m) => m["default"]),
  "../locales/as/editor.ftl": () => import("./editor-Xf4FuZTJ.js").then((m) => m["default"]),
  "../locales/ast/chrome.ftl": () => import("./chrome-DhD0G7oZ.js").then((m) => m["default"]),
  "../locales/ast/content.ftl": () => import("./content-BZxHC7ej.js").then((m) => m["default"]),
  "../locales/ast/diagnostics.ftl": () => import("./diagnostics-BtM97dAn.js").then((m) => m["default"]),
  "../locales/ast/editor.ftl": () => import("./editor-DPndeOFe.js").then((m) => m["default"]),
  "../locales/ay/chrome.ftl": () => import("./chrome-bcHHwrHP.js").then((m) => m["default"]),
  "../locales/ay/content.ftl": () => import("./content--RD0Sj8G.js").then((m) => m["default"]),
  "../locales/ay/diagnostics.ftl": () => import("./diagnostics-D8_fXt1C.js").then((m) => m["default"]),
  "../locales/ay/editor.ftl": () => import("./editor-FfKELxJo.js").then((m) => m["default"]),
  "../locales/az/chrome.ftl": () => import("./chrome-C9gbLQBK.js").then((m) => m["default"]),
  "../locales/az/content.ftl": () => import("./content-Cks0LHS7.js").then((m) => m["default"]),
  "../locales/az/diagnostics.ftl": () => import("./diagnostics-tTGKkMPK.js").then((m) => m["default"]),
  "../locales/az/editor.ftl": () => import("./editor-LFxuuslr.js").then((m) => m["default"]),
  "../locales/ba/chrome.ftl": () => import("./chrome-DarvZq9E.js").then((m) => m["default"]),
  "../locales/ba/content.ftl": () => import("./content-DjssraRx.js").then((m) => m["default"]),
  "../locales/ba/diagnostics.ftl": () => import("./diagnostics-t0IZTo3C.js").then((m) => m["default"]),
  "../locales/ba/editor.ftl": () => import("./editor-DO65Aacp.js").then((m) => m["default"]),
  "../locales/ban/chrome.ftl": () => import("./chrome-D0CL1Mf6.js").then((m) => m["default"]),
  "../locales/ban/content.ftl": () => import("./content-Cf0g6PIQ.js").then((m) => m["default"]),
  "../locales/ban/diagnostics.ftl": () => import("./diagnostics-DK7Ijsme.js").then((m) => m["default"]),
  "../locales/ban/editor.ftl": () => import("./editor-DpMyacW-.js").then((m) => m["default"]),
  "../locales/bci/chrome.ftl": () => import("./chrome-D9ratfRe.js").then((m) => m["default"]),
  "../locales/bci/content.ftl": () => import("./content-B7VrZfbH.js").then((m) => m["default"]),
  "../locales/bci/diagnostics.ftl": () => import("./diagnostics-BNsPQa7_.js").then((m) => m["default"]),
  "../locales/bci/editor.ftl": () => import("./editor-BV2Opotm.js").then((m) => m["default"]),
  "../locales/be/chrome.ftl": () => import("./chrome-woIKiRbL.js").then((m) => m["default"]),
  "../locales/be/content.ftl": () => import("./content-dStvXZNr.js").then((m) => m["default"]),
  "../locales/be/diagnostics.ftl": () => import("./diagnostics-BSY53XBI.js").then((m) => m["default"]),
  "../locales/be/editor.ftl": () => import("./editor-zI4vYf9k.js").then((m) => m["default"]),
  "../locales/bem/chrome.ftl": () => import("./chrome-D-VNLwbG.js").then((m) => m["default"]),
  "../locales/bem/content.ftl": () => import("./content-CjXnyvNG.js").then((m) => m["default"]),
  "../locales/bem/diagnostics.ftl": () => import("./diagnostics-BaaStz45.js").then((m) => m["default"]),
  "../locales/bem/editor.ftl": () => import("./editor-Cym4X4jq.js").then((m) => m["default"]),
  "../locales/bg/chrome.ftl": () => import("./chrome-Z1aZHe3q.js").then((m) => m["default"]),
  "../locales/bg/content.ftl": () => import("./content-DQCapiR2.js").then((m) => m["default"]),
  "../locales/bg/diagnostics.ftl": () => import("./diagnostics-wu0HP8fV.js").then((m) => m["default"]),
  "../locales/bg/editor.ftl": () => import("./editor-B6LoDuOl.js").then((m) => m["default"]),
  "../locales/bho/chrome.ftl": () => import("./chrome-CsguJ-A4.js").then((m) => m["default"]),
  "../locales/bho/content.ftl": () => import("./content-CDxg1-7O.js").then((m) => m["default"]),
  "../locales/bho/diagnostics.ftl": () => import("./diagnostics-Cu_Wsh1t.js").then((m) => m["default"]),
  "../locales/bho/editor.ftl": () => import("./editor-DQvu-yja.js").then((m) => m["default"]),
  "../locales/bik/chrome.ftl": () => import("./chrome-lkN2S1Zc.js").then((m) => m["default"]),
  "../locales/bik/content.ftl": () => import("./content-DVdhAWL-.js").then((m) => m["default"]),
  "../locales/bik/diagnostics.ftl": () => import("./diagnostics-BcNEuHlp.js").then((m) => m["default"]),
  "../locales/bik/editor.ftl": () => import("./editor-DRRQd_gn.js").then((m) => m["default"]),
  "../locales/bin/chrome.ftl": () => import("./chrome-Cbo3aXEB.js").then((m) => m["default"]),
  "../locales/bin/content.ftl": () => import("./content-ONjlMO1K.js").then((m) => m["default"]),
  "../locales/bin/diagnostics.ftl": () => import("./diagnostics-CE7fDK71.js").then((m) => m["default"]),
  "../locales/bin/editor.ftl": () => import("./editor-BXip64NO.js").then((m) => m["default"]),
  "../locales/bm/chrome.ftl": () => import("./chrome-CTZZBw6a.js").then((m) => m["default"]),
  "../locales/bm/content.ftl": () => import("./content-DQZ4nGvP.js").then((m) => m["default"]),
  "../locales/bm/diagnostics.ftl": () => import("./diagnostics-B7fhX20A.js").then((m) => m["default"]),
  "../locales/bm/editor.ftl": () => import("./editor-DmKCw_us.js").then((m) => m["default"]),
  "../locales/bn/chrome.ftl": () => import("./chrome-DNekXQXJ.js").then((m) => m["default"]),
  "../locales/bn/content.ftl": () => import("./content-yVV8y9CA.js").then((m) => m["default"]),
  "../locales/bn/diagnostics.ftl": () => import("./diagnostics-CiQ-tODA.js").then((m) => m["default"]),
  "../locales/bn/editor.ftl": () => import("./editor-CM6kmtrw.js").then((m) => m["default"]),
  "../locales/bo/chrome.ftl": () => import("./chrome-BEnfX8Wl.js").then((m) => m["default"]),
  "../locales/bo/content.ftl": () => import("./content-CJyLEl5f.js").then((m) => m["default"]),
  "../locales/bo/diagnostics.ftl": () => import("./diagnostics-DSB4bcoc.js").then((m) => m["default"]),
  "../locales/bo/editor.ftl": () => import("./editor-rg4P77qW.js").then((m) => m["default"]),
  "../locales/br/chrome.ftl": () => import("./chrome-DlfUgFW8.js").then((m) => m["default"]),
  "../locales/br/content.ftl": () => import("./content-DJFTgWaN.js").then((m) => m["default"]),
  "../locales/br/diagnostics.ftl": () => import("./diagnostics-B_H6q_hj.js").then((m) => m["default"]),
  "../locales/br/editor.ftl": () => import("./editor-r2PPEu5o.js").then((m) => m["default"]),
  "../locales/brx/chrome.ftl": () => import("./chrome-C83fifWg.js").then((m) => m["default"]),
  "../locales/brx/content.ftl": () => import("./content-ClOP6sqp.js").then((m) => m["default"]),
  "../locales/brx/diagnostics.ftl": () => import("./diagnostics-C5Jj1aOs.js").then((m) => m["default"]),
  "../locales/brx/editor.ftl": () => import("./editor-DM0robfc.js").then((m) => m["default"]),
  "../locales/bs/chrome.ftl": () => import("./chrome-rTws7ehr.js").then((m) => m["default"]),
  "../locales/bs/content.ftl": () => import("./content-HyDQqh1Z.js").then((m) => m["default"]),
  "../locales/bs/diagnostics.ftl": () => import("./diagnostics-DfYeMg2a.js").then((m) => m["default"]),
  "../locales/bs/editor.ftl": () => import("./editor-DLhNEzRV.js").then((m) => m["default"]),
  "../locales/bua/chrome.ftl": () => import("./chrome-B2Erkwd_.js").then((m) => m["default"]),
  "../locales/bua/content.ftl": () => import("./content-CsCWwL4o.js").then((m) => m["default"]),
  "../locales/bua/diagnostics.ftl": () => import("./diagnostics-BUemmEGo.js").then((m) => m["default"]),
  "../locales/bua/editor.ftl": () => import("./editor-gXNTBjNw.js").then((m) => m["default"]),
  "../locales/bum/chrome.ftl": () => import("./chrome-B0k07CbP.js").then((m) => m["default"]),
  "../locales/bum/content.ftl": () => import("./content-DIq6XxjA.js").then((m) => m["default"]),
  "../locales/bum/diagnostics.ftl": () => import("./diagnostics-CLLc9aeb.js").then((m) => m["default"]),
  "../locales/bum/editor.ftl": () => import("./editor-DIsPpXC8.js").then((m) => m["default"]),
  "../locales/ca/chrome.ftl": () => import("./chrome-DmdG1Cdi.js").then((m) => m["default"]),
  "../locales/ca/content.ftl": () => import("./content-BynqGe47.js").then((m) => m["default"]),
  "../locales/ca/diagnostics.ftl": () => import("./diagnostics-Dqpo1RDT.js").then((m) => m["default"]),
  "../locales/ca/editor.ftl": () => import("./editor-CXSD_zl4.js").then((m) => m["default"]),
  "../locales/ce/chrome.ftl": () => import("./chrome-DhR0SoNy.js").then((m) => m["default"]),
  "../locales/ce/content.ftl": () => import("./content-C1UmfNhM.js").then((m) => m["default"]),
  "../locales/ce/diagnostics.ftl": () => import("./diagnostics-anATiAdt.js").then((m) => m["default"]),
  "../locales/ce/editor.ftl": () => import("./editor-D_HUSGOn.js").then((m) => m["default"]),
  "../locales/ceb/chrome.ftl": () => import("./chrome-D9gCadIE.js").then((m) => m["default"]),
  "../locales/ceb/content.ftl": () => import("./content-Di0xp5jT.js").then((m) => m["default"]),
  "../locales/ceb/diagnostics.ftl": () => import("./diagnostics-DC0wbv64.js").then((m) => m["default"]),
  "../locales/ceb/editor.ftl": () => import("./editor-BdPM-2EI.js").then((m) => m["default"]),
  "../locales/ch/chrome.ftl": () => import("./chrome-CGo9nUmD.js").then((m) => m["default"]),
  "../locales/ch/content.ftl": () => import("./content-N0NbpjSt.js").then((m) => m["default"]),
  "../locales/ch/diagnostics.ftl": () => import("./diagnostics-Dl7bwEZ5.js").then((m) => m["default"]),
  "../locales/ch/editor.ftl": () => import("./editor-BpOEK603.js").then((m) => m["default"]),
  "../locales/chm/chrome.ftl": () => import("./chrome-D-y3rB-x.js").then((m) => m["default"]),
  "../locales/chm/content.ftl": () => import("./content-DZXKhVJc.js").then((m) => m["default"]),
  "../locales/chm/diagnostics.ftl": () => import("./diagnostics-DPb98aK-.js").then((m) => m["default"]),
  "../locales/chm/editor.ftl": () => import("./editor-BFVe-CTv.js").then((m) => m["default"]),
  "../locales/co/chrome.ftl": () => import("./chrome-BFPVo6of.js").then((m) => m["default"]),
  "../locales/co/content.ftl": () => import("./content-BCbJBYQh.js").then((m) => m["default"]),
  "../locales/co/diagnostics.ftl": () => import("./diagnostics-aHc2vXXo.js").then((m) => m["default"]),
  "../locales/co/editor.ftl": () => import("./editor-BHxPTajt.js").then((m) => m["default"]),
  "../locales/cs/chrome.ftl": () => import("./chrome-D_OWxCIY.js").then((m) => m["default"]),
  "../locales/cs/content.ftl": () => import("./content-jiN77Mkj.js").then((m) => m["default"]),
  "../locales/cs/diagnostics.ftl": () => import("./diagnostics-DDZvoLxi.js").then((m) => m["default"]),
  "../locales/cs/editor.ftl": () => import("./editor-Bjzjuc0_.js").then((m) => m["default"]),
  "../locales/cv/chrome.ftl": () => import("./chrome-KwTT63JF.js").then((m) => m["default"]),
  "../locales/cv/content.ftl": () => import("./content-Ct3ucZSY.js").then((m) => m["default"]),
  "../locales/cv/diagnostics.ftl": () => import("./diagnostics-ChheMDfP.js").then((m) => m["default"]),
  "../locales/cv/editor.ftl": () => import("./editor-Q1X7AWb5.js").then((m) => m["default"]),
  "../locales/cy/chrome.ftl": () => import("./chrome-tEXQ0_37.js").then((m) => m["default"]),
  "../locales/cy/content.ftl": () => import("./content-CLZJ8hDC.js").then((m) => m["default"]),
  "../locales/cy/diagnostics.ftl": () => import("./diagnostics-O_scI37z.js").then((m) => m["default"]),
  "../locales/cy/editor.ftl": () => import("./editor-BB3xfzUV.js").then((m) => m["default"]),
  "../locales/da/chrome.ftl": () => import("./chrome-DmQFcBLS.js").then((m) => m["default"]),
  "../locales/da/content.ftl": () => import("./content-B7sOw8a3.js").then((m) => m["default"]),
  "../locales/da/diagnostics.ftl": () => import("./diagnostics-AL0sVk0p.js").then((m) => m["default"]),
  "../locales/da/editor.ftl": () => import("./editor-DcAkvU6B.js").then((m) => m["default"]),
  "../locales/dag/chrome.ftl": () => import("./chrome-OXaHYs8C.js").then((m) => m["default"]),
  "../locales/dag/content.ftl": () => import("./content-C6LLLGgO.js").then((m) => m["default"]),
  "../locales/dag/diagnostics.ftl": () => import("./diagnostics-DZ0wBEPL.js").then((m) => m["default"]),
  "../locales/dag/editor.ftl": () => import("./editor-BN8_uzoe.js").then((m) => m["default"]),
  "../locales/de/chrome.ftl": () => import("./chrome-B_5TveTj.js").then((m) => m["default"]),
  "../locales/de/content.ftl": () => import("./content-DHQx9gZC.js").then((m) => m["default"]),
  "../locales/de/diagnostics.ftl": () => import("./diagnostics-DirtEmHL.js").then((m) => m["default"]),
  "../locales/de/editor.ftl": () => import("./editor-CcpCA-P4.js").then((m) => m["default"]),
  "../locales/dje/chrome.ftl": () => import("./chrome-BdtYZk7y.js").then((m) => m["default"]),
  "../locales/dje/content.ftl": () => import("./content-D1fp9VVl.js").then((m) => m["default"]),
  "../locales/dje/diagnostics.ftl": () => import("./diagnostics-nHb9bhRK.js").then((m) => m["default"]),
  "../locales/dje/editor.ftl": () => import("./editor-DewqZacP.js").then((m) => m["default"]),
  "../locales/doi/chrome.ftl": () => import("./chrome-0IkhhwMB.js").then((m) => m["default"]),
  "../locales/doi/content.ftl": () => import("./content-B3u8RJxm.js").then((m) => m["default"]),
  "../locales/doi/diagnostics.ftl": () => import("./diagnostics-B-1XcBtb.js").then((m) => m["default"]),
  "../locales/doi/editor.ftl": () => import("./editor-BZWSppVe.js").then((m) => m["default"]),
  "../locales/dv/chrome.ftl": () => import("./chrome-gX2dLVPD.js").then((m) => m["default"]),
  "../locales/dv/content.ftl": () => import("./content-XpZJtEFi.js").then((m) => m["default"]),
  "../locales/dv/diagnostics.ftl": () => import("./diagnostics-DF5TbzjE.js").then((m) => m["default"]),
  "../locales/dv/editor.ftl": () => import("./editor-CKz_VIQl.js").then((m) => m["default"]),
  "../locales/dyo/chrome.ftl": () => import("./chrome-uLE0wwM2.js").then((m) => m["default"]),
  "../locales/dyo/content.ftl": () => import("./content-D-LPGyU6.js").then((m) => m["default"]),
  "../locales/dyo/diagnostics.ftl": () => import("./diagnostics-lUG1cjT6.js").then((m) => m["default"]),
  "../locales/dyo/editor.ftl": () => import("./editor-CDbRXMZJ.js").then((m) => m["default"]),
  "../locales/dyu/chrome.ftl": () => import("./chrome-BQjYUk-j.js").then((m) => m["default"]),
  "../locales/dyu/content.ftl": () => import("./content-BB9L0PWd.js").then((m) => m["default"]),
  "../locales/dyu/diagnostics.ftl": () => import("./diagnostics-CtNyyXdJ.js").then((m) => m["default"]),
  "../locales/dyu/editor.ftl": () => import("./editor-2vLb_GR2.js").then((m) => m["default"]),
  "../locales/dz/chrome.ftl": () => import("./chrome-D5tyhjXP.js").then((m) => m["default"]),
  "../locales/dz/content.ftl": () => import("./content-DXCwCRxL.js").then((m) => m["default"]),
  "../locales/dz/diagnostics.ftl": () => import("./diagnostics-kCYHrTwa.js").then((m) => m["default"]),
  "../locales/dz/editor.ftl": () => import("./editor-BZdIG8XX.js").then((m) => m["default"]),
  "../locales/ee/chrome.ftl": () => import("./chrome-C7m7KuEd.js").then((m) => m["default"]),
  "../locales/ee/content.ftl": () => import("./content-BSQnZ8wB.js").then((m) => m["default"]),
  "../locales/ee/diagnostics.ftl": () => import("./diagnostics-sdhtIf_f.js").then((m) => m["default"]),
  "../locales/ee/editor.ftl": () => import("./editor-B-dcBLD6.js").then((m) => m["default"]),
  "../locales/efi/chrome.ftl": () => import("./chrome-snTMLSpg.js").then((m) => m["default"]),
  "../locales/efi/content.ftl": () => import("./content-BxmT5nlk.js").then((m) => m["default"]),
  "../locales/efi/diagnostics.ftl": () => import("./diagnostics-iTn21qfw.js").then((m) => m["default"]),
  "../locales/efi/editor.ftl": () => import("./editor-D99fiuib.js").then((m) => m["default"]),
  "../locales/el/chrome.ftl": () => import("./chrome-DUOSfaJs.js").then((m) => m["default"]),
  "../locales/el/content.ftl": () => import("./content-CuCa3xMM.js").then((m) => m["default"]),
  "../locales/el/diagnostics.ftl": () => import("./diagnostics-DfUcePLp.js").then((m) => m["default"]),
  "../locales/el/editor.ftl": () => import("./editor-b6ADQVhy.js").then((m) => m["default"]),
  "../locales/es/chrome.ftl": () => import("./chrome-DfpPGhDB.js").then((m) => m["default"]),
  "../locales/es/content.ftl": () => import("./content-CNOsHENE.js").then((m) => m["default"]),
  "../locales/es/diagnostics.ftl": () => import("./diagnostics-Ym7cNYqW.js").then((m) => m["default"]),
  "../locales/es/editor.ftl": () => import("./editor-D7ueEssQ.js").then((m) => m["default"]),
  "../locales/et/chrome.ftl": () => import("./chrome-HIf1DUT0.js").then((m) => m["default"]),
  "../locales/et/content.ftl": () => import("./content-BPaM-0zK.js").then((m) => m["default"]),
  "../locales/et/diagnostics.ftl": () => import("./diagnostics-BgdBV7T5.js").then((m) => m["default"]),
  "../locales/et/editor.ftl": () => import("./editor-C_x0CMn1.js").then((m) => m["default"]),
  "../locales/eu/chrome.ftl": () => import("./chrome-CbIlt4V0.js").then((m) => m["default"]),
  "../locales/eu/content.ftl": () => import("./content-CPKxoL1W.js").then((m) => m["default"]),
  "../locales/eu/diagnostics.ftl": () => import("./diagnostics-qfGuaL2m.js").then((m) => m["default"]),
  "../locales/eu/editor.ftl": () => import("./editor-B9OjprnP.js").then((m) => m["default"]),
  "../locales/ewo/chrome.ftl": () => import("./chrome-CNqMMwMa.js").then((m) => m["default"]),
  "../locales/ewo/content.ftl": () => import("./content-D4f5RJkM.js").then((m) => m["default"]),
  "../locales/ewo/diagnostics.ftl": () => import("./diagnostics-Cd5aYb_R.js").then((m) => m["default"]),
  "../locales/ewo/editor.ftl": () => import("./editor-CWjp-laV.js").then((m) => m["default"]),
  "../locales/fa/chrome.ftl": () => import("./chrome-DYoHm2R2.js").then((m) => m["default"]),
  "../locales/fa/content.ftl": () => import("./content-DLwuzaDY.js").then((m) => m["default"]),
  "../locales/fa/diagnostics.ftl": () => import("./diagnostics-CGnYsW66.js").then((m) => m["default"]),
  "../locales/fa/editor.ftl": () => import("./editor-CpQef_b1.js").then((m) => m["default"]),
  "../locales/ff/chrome.ftl": () => import("./chrome-D0NNRryQ.js").then((m) => m["default"]),
  "../locales/ff/content.ftl": () => import("./content-BgdceVk8.js").then((m) => m["default"]),
  "../locales/ff/diagnostics.ftl": () => import("./diagnostics-WLV5kqXH.js").then((m) => m["default"]),
  "../locales/ff/editor.ftl": () => import("./editor-Bb5ob7WO.js").then((m) => m["default"]),
  "../locales/fi/chrome.ftl": () => import("./chrome-CsJECRuQ.js").then((m) => m["default"]),
  "../locales/fi/content.ftl": () => import("./content-BkA_rTsl.js").then((m) => m["default"]),
  "../locales/fi/diagnostics.ftl": () => import("./diagnostics-CJvUm0Q7.js").then((m) => m["default"]),
  "../locales/fi/editor.ftl": () => import("./editor-vU6VUDQb.js").then((m) => m["default"]),
  "../locales/fil/chrome.ftl": () => import("./chrome-BQORfNoh.js").then((m) => m["default"]),
  "../locales/fil/content.ftl": () => import("./content-CKmHXCeu.js").then((m) => m["default"]),
  "../locales/fil/diagnostics.ftl": () => import("./diagnostics-TEncjul8.js").then((m) => m["default"]),
  "../locales/fil/editor.ftl": () => import("./editor-BSDmfo2T.js").then((m) => m["default"]),
  "../locales/fj/chrome.ftl": () => import("./chrome-BBcyTWRs.js").then((m) => m["default"]),
  "../locales/fj/content.ftl": () => import("./content-COvmt1j1.js").then((m) => m["default"]),
  "../locales/fj/diagnostics.ftl": () => import("./diagnostics-CqYX9iUc.js").then((m) => m["default"]),
  "../locales/fj/editor.ftl": () => import("./editor-D_WsyOXh.js").then((m) => m["default"]),
  "../locales/fo/chrome.ftl": () => import("./chrome-gIXgzpYJ.js").then((m) => m["default"]),
  "../locales/fo/content.ftl": () => import("./content-yPEODSK2.js").then((m) => m["default"]),
  "../locales/fo/diagnostics.ftl": () => import("./diagnostics-CPmg93FI.js").then((m) => m["default"]),
  "../locales/fo/editor.ftl": () => import("./editor-PMpdDMz1.js").then((m) => m["default"]),
  "../locales/fon/chrome.ftl": () => import("./chrome-C_JmXbk6.js").then((m) => m["default"]),
  "../locales/fon/content.ftl": () => import("./content-Ls9VwPvN.js").then((m) => m["default"]),
  "../locales/fon/diagnostics.ftl": () => import("./diagnostics-DyvMZGf_.js").then((m) => m["default"]),
  "../locales/fon/editor.ftl": () => import("./editor-B3h3I5u9.js").then((m) => m["default"]),
  "../locales/fr/chrome.ftl": () => import("./chrome-B8NDTyxN.js").then((m) => m["default"]),
  "../locales/fr/content.ftl": () => import("./content-BvUWw9KK.js").then((m) => m["default"]),
  "../locales/fr/diagnostics.ftl": () => import("./diagnostics-Dv3tKIJO.js").then((m) => m["default"]),
  "../locales/fr/editor.ftl": () => import("./editor-CapWY6t5.js").then((m) => m["default"]),
  "../locales/fy/chrome.ftl": () => import("./chrome-BJkwQsWm.js").then((m) => m["default"]),
  "../locales/fy/content.ftl": () => import("./content-Ca3obMaT.js").then((m) => m["default"]),
  "../locales/fy/diagnostics.ftl": () => import("./diagnostics-reGN2kpt.js").then((m) => m["default"]),
  "../locales/fy/editor.ftl": () => import("./editor-B1faH_vl.js").then((m) => m["default"]),
  "../locales/ga/chrome.ftl": () => import("./chrome-CXlKTKi6.js").then((m) => m["default"]),
  "../locales/ga/content.ftl": () => import("./content-BU3Jldt0.js").then((m) => m["default"]),
  "../locales/ga/diagnostics.ftl": () => import("./diagnostics-CR_Jn2eK.js").then((m) => m["default"]),
  "../locales/ga/editor.ftl": () => import("./editor-BH2SJj21.js").then((m) => m["default"]),
  "../locales/gaa/chrome.ftl": () => import("./chrome-CM-CLfNs.js").then((m) => m["default"]),
  "../locales/gaa/content.ftl": () => import("./content-DPOW52Fp.js").then((m) => m["default"]),
  "../locales/gaa/diagnostics.ftl": () => import("./diagnostics-BwJxwDv6.js").then((m) => m["default"]),
  "../locales/gaa/editor.ftl": () => import("./editor-DhbdZdgU.js").then((m) => m["default"]),
  "../locales/gd/chrome.ftl": () => import("./chrome-IMlU4bAZ.js").then((m) => m["default"]),
  "../locales/gd/content.ftl": () => import("./content-DYfN5sPa.js").then((m) => m["default"]),
  "../locales/gd/diagnostics.ftl": () => import("./diagnostics-BC1XlSjs.js").then((m) => m["default"]),
  "../locales/gd/editor.ftl": () => import("./editor-DTCWok8x.js").then((m) => m["default"]),
  "../locales/gl/chrome.ftl": () => import("./chrome-Bcq7u0kG.js").then((m) => m["default"]),
  "../locales/gl/content.ftl": () => import("./content-CnDUZr8O.js").then((m) => m["default"]),
  "../locales/gl/diagnostics.ftl": () => import("./diagnostics-BKUDcL13.js").then((m) => m["default"]),
  "../locales/gl/editor.ftl": () => import("./editor-DzARo8Ww.js").then((m) => m["default"]),
  "../locales/gn/chrome.ftl": () => import("./chrome-CtzECWU5.js").then((m) => m["default"]),
  "../locales/gn/content.ftl": () => import("./content-iw-V5T6O.js").then((m) => m["default"]),
  "../locales/gn/diagnostics.ftl": () => import("./diagnostics-D0afd0pE.js").then((m) => m["default"]),
  "../locales/gn/editor.ftl": () => import("./editor-tY-HpbAq.js").then((m) => m["default"]),
  "../locales/gu/chrome.ftl": () => import("./chrome-DnJz1U1A.js").then((m) => m["default"]),
  "../locales/gu/content.ftl": () => import("./content-bi4gT7y7.js").then((m) => m["default"]),
  "../locales/gu/diagnostics.ftl": () => import("./diagnostics-CbTyxO__.js").then((m) => m["default"]),
  "../locales/gu/editor.ftl": () => import("./editor-BJYT98BQ.js").then((m) => m["default"]),
  "../locales/ha/chrome.ftl": () => import("./chrome-CHR94bOg.js").then((m) => m["default"]),
  "../locales/ha/content.ftl": () => import("./content-DtSflvl8.js").then((m) => m["default"]),
  "../locales/ha/diagnostics.ftl": () => import("./diagnostics-CsS_mdmu.js").then((m) => m["default"]),
  "../locales/ha/editor.ftl": () => import("./editor-D6wRuEI_.js").then((m) => m["default"]),
  "../locales/haw/chrome.ftl": () => import("./chrome-DAiRBc_0.js").then((m) => m["default"]),
  "../locales/haw/content.ftl": () => import("./content-Dmrka-Is.js").then((m) => m["default"]),
  "../locales/haw/diagnostics.ftl": () => import("./diagnostics-CipCfysJ.js").then((m) => m["default"]),
  "../locales/haw/editor.ftl": () => import("./editor-CG5lWuly.js").then((m) => m["default"]),
  "../locales/he/chrome.ftl": () => import("./chrome-Dkkhber7.js").then((m) => m["default"]),
  "../locales/he/content.ftl": () => import("./content-aVvsymhv.js").then((m) => m["default"]),
  "../locales/he/diagnostics.ftl": () => import("./diagnostics-CkwH8Uzo.js").then((m) => m["default"]),
  "../locales/he/editor.ftl": () => import("./editor-B-HPacEA.js").then((m) => m["default"]),
  "../locales/hi/chrome.ftl": () => import("./chrome-DjupYm6J.js").then((m) => m["default"]),
  "../locales/hi/content.ftl": () => import("./content-CjRwUjOG.js").then((m) => m["default"]),
  "../locales/hi/diagnostics.ftl": () => import("./diagnostics-oiIE67p4.js").then((m) => m["default"]),
  "../locales/hi/editor.ftl": () => import("./editor-FDoQ3Ymt.js").then((m) => m["default"]),
  "../locales/hil/chrome.ftl": () => import("./chrome-BPExQTEQ.js").then((m) => m["default"]),
  "../locales/hil/content.ftl": () => import("./content-CSp3t0FI.js").then((m) => m["default"]),
  "../locales/hil/diagnostics.ftl": () => import("./diagnostics-xymDuj2A.js").then((m) => m["default"]),
  "../locales/hil/editor.ftl": () => import("./editor-BHkZMWnb.js").then((m) => m["default"]),
  "../locales/hnj/chrome.ftl": () => import("./chrome-C8t1gYHu.js").then((m) => m["default"]),
  "../locales/hnj/content.ftl": () => import("./content-By_oRFIe.js").then((m) => m["default"]),
  "../locales/hnj/diagnostics.ftl": () => import("./diagnostics-BKuKvU6t.js").then((m) => m["default"]),
  "../locales/hnj/editor.ftl": () => import("./editor-B6-NJBVs.js").then((m) => m["default"]),
  "../locales/hr/chrome.ftl": () => import("./chrome-BRnprDc0.js").then((m) => m["default"]),
  "../locales/hr/content.ftl": () => import("./content-DtIE9rGF.js").then((m) => m["default"]),
  "../locales/hr/diagnostics.ftl": () => import("./diagnostics-88VUvCBU.js").then((m) => m["default"]),
  "../locales/hr/editor.ftl": () => import("./editor-CT5xXMUY.js").then((m) => m["default"]),
  "../locales/ht/chrome.ftl": () => import("./chrome-EQc6ReFU.js").then((m) => m["default"]),
  "../locales/ht/content.ftl": () => import("./content-BYXefnz3.js").then((m) => m["default"]),
  "../locales/ht/diagnostics.ftl": () => import("./diagnostics-D5PS6n4f.js").then((m) => m["default"]),
  "../locales/ht/editor.ftl": () => import("./editor-SZefpQOZ.js").then((m) => m["default"]),
  "../locales/hu/chrome.ftl": () => import("./chrome-CdzUURaY.js").then((m) => m["default"]),
  "../locales/hu/content.ftl": () => import("./content-ZP1VQCa8.js").then((m) => m["default"]),
  "../locales/hu/diagnostics.ftl": () => import("./diagnostics-C8dg6y4z.js").then((m) => m["default"]),
  "../locales/hu/editor.ftl": () => import("./editor-BQ3k3WYL.js").then((m) => m["default"]),
  "../locales/hy/chrome.ftl": () => import("./chrome-CCUIoyUZ.js").then((m) => m["default"]),
  "../locales/hy/content.ftl": () => import("./content-B_rdc5ZO.js").then((m) => m["default"]),
  "../locales/hy/diagnostics.ftl": () => import("./diagnostics-C79-ojAk.js").then((m) => m["default"]),
  "../locales/hy/editor.ftl": () => import("./editor-CFe1PKkg.js").then((m) => m["default"]),
  "../locales/id/chrome.ftl": () => import("./chrome-vjlHoxL5.js").then((m) => m["default"]),
  "../locales/id/content.ftl": () => import("./content-CrLFa4AO.js").then((m) => m["default"]),
  "../locales/id/diagnostics.ftl": () => import("./diagnostics-CFoWXwjU.js").then((m) => m["default"]),
  "../locales/id/editor.ftl": () => import("./editor-B4HbP_jI.js").then((m) => m["default"]),
  "../locales/ig/chrome.ftl": () => import("./chrome-CRkVHFpX.js").then((m) => m["default"]),
  "../locales/ig/content.ftl": () => import("./content-Bl6iZAq7.js").then((m) => m["default"]),
  "../locales/ig/diagnostics.ftl": () => import("./diagnostics-DmvxGE2K.js").then((m) => m["default"]),
  "../locales/ig/editor.ftl": () => import("./editor-D3zpDcMt.js").then((m) => m["default"]),
  "../locales/ilo/chrome.ftl": () => import("./chrome-Cr6cTo58.js").then((m) => m["default"]),
  "../locales/ilo/content.ftl": () => import("./content-BQNlfiv1.js").then((m) => m["default"]),
  "../locales/ilo/diagnostics.ftl": () => import("./diagnostics-B9S45SCr.js").then((m) => m["default"]),
  "../locales/ilo/editor.ftl": () => import("./editor-CDXOhGnI.js").then((m) => m["default"]),
  "../locales/is/chrome.ftl": () => import("./chrome-BizzC5eP.js").then((m) => m["default"]),
  "../locales/is/content.ftl": () => import("./content-Dc3ul7bY.js").then((m) => m["default"]),
  "../locales/is/diagnostics.ftl": () => import("./diagnostics-CuKx4fvj.js").then((m) => m["default"]),
  "../locales/is/editor.ftl": () => import("./editor-BT09WtEX.js").then((m) => m["default"]),
  "../locales/it/chrome.ftl": () => import("./chrome-BAI0sZCD.js").then((m) => m["default"]),
  "../locales/it/content.ftl": () => import("./content-CpQ6IHzs.js").then((m) => m["default"]),
  "../locales/it/diagnostics.ftl": () => import("./diagnostics-CH_aro1x.js").then((m) => m["default"]),
  "../locales/it/editor.ftl": () => import("./editor-LYCPn3J3.js").then((m) => m["default"]),
  "../locales/ja/chrome.ftl": () => import("./chrome-D4aqLK6a.js").then((m) => m["default"]),
  "../locales/ja/content.ftl": () => import("./content-B9OM480k.js").then((m) => m["default"]),
  "../locales/ja/diagnostics.ftl": () => import("./diagnostics-Bguiybzc.js").then((m) => m["default"]),
  "../locales/ja/editor.ftl": () => import("./editor-CieiZ861.js").then((m) => m["default"]),
  "../locales/jv/chrome.ftl": () => import("./chrome-CuseG_E8.js").then((m) => m["default"]),
  "../locales/jv/content.ftl": () => import("./content-BSemJvkq.js").then((m) => m["default"]),
  "../locales/jv/diagnostics.ftl": () => import("./diagnostics-ATg2MuZJ.js").then((m) => m["default"]),
  "../locales/jv/editor.ftl": () => import("./editor-BlbMCgKe.js").then((m) => m["default"]),
  "../locales/ka/chrome.ftl": () => import("./chrome-C-mwsZhR.js").then((m) => m["default"]),
  "../locales/ka/content.ftl": () => import("./content-jsN9g27C.js").then((m) => m["default"]),
  "../locales/ka/diagnostics.ftl": () => import("./diagnostics-VZoDiaD1.js").then((m) => m["default"]),
  "../locales/ka/editor.ftl": () => import("./editor-NXwT7vSv.js").then((m) => m["default"]),
  "../locales/kab/chrome.ftl": () => import("./chrome-DtC3L7G7.js").then((m) => m["default"]),
  "../locales/kab/content.ftl": () => import("./content-GhGAk8F2.js").then((m) => m["default"]),
  "../locales/kab/diagnostics.ftl": () => import("./diagnostics-CEs3whf7.js").then((m) => m["default"]),
  "../locales/kab/editor.ftl": () => import("./editor-DICkqO10.js").then((m) => m["default"]),
  "../locales/kbp/chrome.ftl": () => import("./chrome-zdNpyfX-.js").then((m) => m["default"]),
  "../locales/kbp/content.ftl": () => import("./content-DN12bnCU.js").then((m) => m["default"]),
  "../locales/kbp/diagnostics.ftl": () => import("./diagnostics-SjILXWcM.js").then((m) => m["default"]),
  "../locales/kbp/editor.ftl": () => import("./editor-EwqhE8W9.js").then((m) => m["default"]),
  "../locales/kg/chrome.ftl": () => import("./chrome-CL_3F_ce.js").then((m) => m["default"]),
  "../locales/kg/content.ftl": () => import("./content-cvPtFJ7b.js").then((m) => m["default"]),
  "../locales/kg/diagnostics.ftl": () => import("./diagnostics-ECDNdztl.js").then((m) => m["default"]),
  "../locales/kg/editor.ftl": () => import("./editor-BG36z13U.js").then((m) => m["default"]),
  "../locales/ki/chrome.ftl": () => import("./chrome-DgcFSRld.js").then((m) => m["default"]),
  "../locales/ki/content.ftl": () => import("./content-CmwHsFxj.js").then((m) => m["default"]),
  "../locales/ki/diagnostics.ftl": () => import("./diagnostics-Dzk5JDC1.js").then((m) => m["default"]),
  "../locales/ki/editor.ftl": () => import("./editor-DUsh5Zno.js").then((m) => m["default"]),
  "../locales/kk/chrome.ftl": () => import("./chrome-CLsS3pF4.js").then((m) => m["default"]),
  "../locales/kk/content.ftl": () => import("./content-BpDxfAwC.js").then((m) => m["default"]),
  "../locales/kk/diagnostics.ftl": () => import("./diagnostics-D_mlneZO.js").then((m) => m["default"]),
  "../locales/kk/editor.ftl": () => import("./editor-C9QCyvPI.js").then((m) => m["default"]),
  "../locales/km/chrome.ftl": () => import("./chrome-Bg35SlVe.js").then((m) => m["default"]),
  "../locales/km/content.ftl": () => import("./content-Bnc7Jx6t.js").then((m) => m["default"]),
  "../locales/km/diagnostics.ftl": () => import("./diagnostics-Bx73Dgvk.js").then((m) => m["default"]),
  "../locales/km/editor.ftl": () => import("./editor-CZKI-6b3.js").then((m) => m["default"]),
  "../locales/kmb/chrome.ftl": () => import("./chrome-Bq2GJ-lp.js").then((m) => m["default"]),
  "../locales/kmb/content.ftl": () => import("./content-DtM2Id_h.js").then((m) => m["default"]),
  "../locales/kmb/diagnostics.ftl": () => import("./diagnostics-CG_lI8Si.js").then((m) => m["default"]),
  "../locales/kmb/editor.ftl": () => import("./editor-kO6mHBp0.js").then((m) => m["default"]),
  "../locales/kn/chrome.ftl": () => import("./chrome-BRo9Pjx4.js").then((m) => m["default"]),
  "../locales/kn/content.ftl": () => import("./content-wDRFW2L1.js").then((m) => m["default"]),
  "../locales/kn/diagnostics.ftl": () => import("./diagnostics-DJVdAQGJ.js").then((m) => m["default"]),
  "../locales/kn/editor.ftl": () => import("./editor-3F2ZvZVi.js").then((m) => m["default"]),
  "../locales/ko/chrome.ftl": () => import("./chrome-CgDP51OM.js").then((m) => m["default"]),
  "../locales/ko/content.ftl": () => import("./content-BQwabDuM.js").then((m) => m["default"]),
  "../locales/ko/diagnostics.ftl": () => import("./diagnostics-Bog7W4UC.js").then((m) => m["default"]),
  "../locales/ko/editor.ftl": () => import("./editor-BWnT0YSR.js").then((m) => m["default"]),
  "../locales/kok/chrome.ftl": () => import("./chrome-BmbbIhBH.js").then((m) => m["default"]),
  "../locales/kok/content.ftl": () => import("./content-BzpyNzUc.js").then((m) => m["default"]),
  "../locales/kok/diagnostics.ftl": () => import("./diagnostics-BdeKI9Za.js").then((m) => m["default"]),
  "../locales/kok/editor.ftl": () => import("./editor-Sub2PAqt.js").then((m) => m["default"]),
  "../locales/kpe/chrome.ftl": () => import("./chrome-yv_Nb7X0.js").then((m) => m["default"]),
  "../locales/kpe/content.ftl": () => import("./content-3mWq3gyD.js").then((m) => m["default"]),
  "../locales/kpe/diagnostics.ftl": () => import("./diagnostics-C46pU_Zb.js").then((m) => m["default"]),
  "../locales/kpe/editor.ftl": () => import("./editor-BeLh4xwX.js").then((m) => m["default"]),
  "../locales/kr/chrome.ftl": () => import("./chrome-wA5XKuCC.js").then((m) => m["default"]),
  "../locales/kr/content.ftl": () => import("./content-DrwoeAwh.js").then((m) => m["default"]),
  "../locales/kr/diagnostics.ftl": () => import("./diagnostics-BVzus_Uq.js").then((m) => m["default"]),
  "../locales/kr/editor.ftl": () => import("./editor-BPLQgbUl.js").then((m) => m["default"]),
  "../locales/kri/chrome.ftl": () => import("./chrome-BT3gw_IS.js").then((m) => m["default"]),
  "../locales/kri/content.ftl": () => import("./content-x9Xq-jBJ.js").then((m) => m["default"]),
  "../locales/kri/diagnostics.ftl": () => import("./diagnostics-CJX3ysrB.js").then((m) => m["default"]),
  "../locales/kri/editor.ftl": () => import("./editor-DfAuHieg.js").then((m) => m["default"]),
  "../locales/ks/chrome.ftl": () => import("./chrome-Btg_kbX8.js").then((m) => m["default"]),
  "../locales/ks/content.ftl": () => import("./content-Bzei2_7y.js").then((m) => m["default"]),
  "../locales/ks/diagnostics.ftl": () => import("./diagnostics-D7zKhQgx.js").then((m) => m["default"]),
  "../locales/ks/editor.ftl": () => import("./editor-BjeUb7MD.js").then((m) => m["default"]),
  "../locales/ktu/chrome.ftl": () => import("./chrome-CvflMdVy.js").then((m) => m["default"]),
  "../locales/ktu/content.ftl": () => import("./content-efmt3WyV.js").then((m) => m["default"]),
  "../locales/ktu/diagnostics.ftl": () => import("./diagnostics-e--h4ZMI.js").then((m) => m["default"]),
  "../locales/ktu/editor.ftl": () => import("./editor-1opdTIvR.js").then((m) => m["default"]),
  "../locales/kv/chrome.ftl": () => import("./chrome-oeyWMvPV.js").then((m) => m["default"]),
  "../locales/kv/content.ftl": () => import("./content-OpDLLaH8.js").then((m) => m["default"]),
  "../locales/kv/diagnostics.ftl": () => import("./diagnostics-DKdDO6lI.js").then((m) => m["default"]),
  "../locales/kv/editor.ftl": () => import("./editor-DjmvyBS4.js").then((m) => m["default"]),
  "../locales/ky/chrome.ftl": () => import("./chrome-CQK6IWZu.js").then((m) => m["default"]),
  "../locales/ky/content.ftl": () => import("./content-D7Rm-XEj.js").then((m) => m["default"]),
  "../locales/ky/diagnostics.ftl": () => import("./diagnostics-B5sw56WY.js").then((m) => m["default"]),
  "../locales/ky/editor.ftl": () => import("./editor-BDW9_5Rb.js").then((m) => m["default"]),
  "../locales/lb/chrome.ftl": () => import("./chrome-CudHsmkQ.js").then((m) => m["default"]),
  "../locales/lb/content.ftl": () => import("./content-DHgAMpMO.js").then((m) => m["default"]),
  "../locales/lb/diagnostics.ftl": () => import("./diagnostics-CSk-oSp0.js").then((m) => m["default"]),
  "../locales/lb/editor.ftl": () => import("./editor-CEivI0tO.js").then((m) => m["default"]),
  "../locales/lg/chrome.ftl": () => import("./chrome-DIo0s7ZO.js").then((m) => m["default"]),
  "../locales/lg/content.ftl": () => import("./content-_UhUvW8U.js").then((m) => m["default"]),
  "../locales/lg/diagnostics.ftl": () => import("./diagnostics-Bbr8asi8.js").then((m) => m["default"]),
  "../locales/lg/editor.ftl": () => import("./editor-DuLkMIVb.js").then((m) => m["default"]),
  "../locales/ln/chrome.ftl": () => import("./chrome-UxF-e9EW.js").then((m) => m["default"]),
  "../locales/ln/content.ftl": () => import("./content-CvjVtY2B.js").then((m) => m["default"]),
  "../locales/ln/diagnostics.ftl": () => import("./diagnostics-C-zNTHvC.js").then((m) => m["default"]),
  "../locales/ln/editor.ftl": () => import("./editor-lakzu1TG.js").then((m) => m["default"]),
  "../locales/lo/chrome.ftl": () => import("./chrome-RkbzOFCa.js").then((m) => m["default"]),
  "../locales/lo/content.ftl": () => import("./content-CW2TRJ6g.js").then((m) => m["default"]),
  "../locales/lo/diagnostics.ftl": () => import("./diagnostics-CtKhNhfR.js").then((m) => m["default"]),
  "../locales/lo/editor.ftl": () => import("./editor-D_XiV2gN.js").then((m) => m["default"]),
  "../locales/lom/chrome.ftl": () => import("./chrome-1mAx37bD.js").then((m) => m["default"]),
  "../locales/lom/content.ftl": () => import("./content-Cs57labq.js").then((m) => m["default"]),
  "../locales/lom/diagnostics.ftl": () => import("./diagnostics-DVyTJJgt.js").then((m) => m["default"]),
  "../locales/lom/editor.ftl": () => import("./editor-Bo9tpEiz.js").then((m) => m["default"]),
  "../locales/lt/chrome.ftl": () => import("./chrome-6VsC1QCD.js").then((m) => m["default"]),
  "../locales/lt/content.ftl": () => import("./content-32nJScj-.js").then((m) => m["default"]),
  "../locales/lt/diagnostics.ftl": () => import("./diagnostics-DSVUw5xi.js").then((m) => m["default"]),
  "../locales/lt/editor.ftl": () => import("./editor-1jkrBSUh.js").then((m) => m["default"]),
  "../locales/lua/chrome.ftl": () => import("./chrome-B4mbf1y6.js").then((m) => m["default"]),
  "../locales/lua/content.ftl": () => import("./content-CacKY8vA.js").then((m) => m["default"]),
  "../locales/lua/diagnostics.ftl": () => import("./diagnostics-N2xAgkCD.js").then((m) => m["default"]),
  "../locales/lua/editor.ftl": () => import("./editor-BYHrZw8q.js").then((m) => m["default"]),
  "../locales/luo/chrome.ftl": () => import("./chrome-CI8nYSbi.js").then((m) => m["default"]),
  "../locales/luo/content.ftl": () => import("./content-Dc1JUBE6.js").then((m) => m["default"]),
  "../locales/luo/diagnostics.ftl": () => import("./diagnostics-ByB1Vf-c.js").then((m) => m["default"]),
  "../locales/luo/editor.ftl": () => import("./editor-Dq7aQOZZ.js").then((m) => m["default"]),
  "../locales/lv/chrome.ftl": () => import("./chrome-C9NNoSok.js").then((m) => m["default"]),
  "../locales/lv/content.ftl": () => import("./content-CcKa_0K0.js").then((m) => m["default"]),
  "../locales/lv/diagnostics.ftl": () => import("./diagnostics-BZg9m_bI.js").then((m) => m["default"]),
  "../locales/lv/editor.ftl": () => import("./editor-peXw5YNq.js").then((m) => m["default"]),
  "../locales/mad/chrome.ftl": () => import("./chrome-BG37S2HB.js").then((m) => m["default"]),
  "../locales/mad/content.ftl": () => import("./content-DyRBU98u.js").then((m) => m["default"]),
  "../locales/mad/diagnostics.ftl": () => import("./diagnostics-DO-5NHmb.js").then((m) => m["default"]),
  "../locales/mad/editor.ftl": () => import("./editor-D6b7OiYP.js").then((m) => m["default"]),
  "../locales/mai/chrome.ftl": () => import("./chrome-xYa-oQT8.js").then((m) => m["default"]),
  "../locales/mai/content.ftl": () => import("./content-BOeXSrLM.js").then((m) => m["default"]),
  "../locales/mai/diagnostics.ftl": () => import("./diagnostics-Dk3GBWtN.js").then((m) => m["default"]),
  "../locales/mai/editor.ftl": () => import("./editor-DD5qobd6.js").then((m) => m["default"]),
  "../locales/men/chrome.ftl": () => import("./chrome-BpSBkCmB.js").then((m) => m["default"]),
  "../locales/men/content.ftl": () => import("./content-DoTRnBEO.js").then((m) => m["default"]),
  "../locales/men/diagnostics.ftl": () => import("./diagnostics-rEopRFy9.js").then((m) => m["default"]),
  "../locales/men/editor.ftl": () => import("./editor-BKwYSBgr.js").then((m) => m["default"]),
  "../locales/mg/chrome.ftl": () => import("./chrome-3idkOSoC.js").then((m) => m["default"]),
  "../locales/mg/content.ftl": () => import("./content-CUPxBcF_.js").then((m) => m["default"]),
  "../locales/mg/diagnostics.ftl": () => import("./diagnostics-3qPx7Ur6.js").then((m) => m["default"]),
  "../locales/mg/editor.ftl": () => import("./editor-COG0mDPi.js").then((m) => m["default"]),
  "../locales/mi/chrome.ftl": () => import("./chrome-XyLEqQK6.js").then((m) => m["default"]),
  "../locales/mi/content.ftl": () => import("./content-BR1JhrYw.js").then((m) => m["default"]),
  "../locales/mi/diagnostics.ftl": () => import("./diagnostics-j055o9_o.js").then((m) => m["default"]),
  "../locales/mi/editor.ftl": () => import("./editor-BhfSnlsg.js").then((m) => m["default"]),
  "../locales/min/chrome.ftl": () => import("./chrome-BK2g9M5_.js").then((m) => m["default"]),
  "../locales/min/content.ftl": () => import("./content-DMTjwCk7.js").then((m) => m["default"]),
  "../locales/min/diagnostics.ftl": () => import("./diagnostics-BqwYeenv.js").then((m) => m["default"]),
  "../locales/min/editor.ftl": () => import("./editor-D4eHcfEx.js").then((m) => m["default"]),
  "../locales/mk/chrome.ftl": () => import("./chrome-DRKbH_VL.js").then((m) => m["default"]),
  "../locales/mk/content.ftl": () => import("./content-E7LTnLOy.js").then((m) => m["default"]),
  "../locales/mk/diagnostics.ftl": () => import("./diagnostics-Brs8GKTF.js").then((m) => m["default"]),
  "../locales/mk/editor.ftl": () => import("./editor-Do0S8iSR.js").then((m) => m["default"]),
  "../locales/ml/chrome.ftl": () => import("./chrome-CKsjurvM.js").then((m) => m["default"]),
  "../locales/ml/content.ftl": () => import("./content-AmBB6WiC.js").then((m) => m["default"]),
  "../locales/ml/diagnostics.ftl": () => import("./diagnostics-CKZ3FYqq.js").then((m) => m["default"]),
  "../locales/ml/editor.ftl": () => import("./editor-I3QxWC9L.js").then((m) => m["default"]),
  "../locales/mn/chrome.ftl": () => import("./chrome-DpPpw2Eg.js").then((m) => m["default"]),
  "../locales/mn/content.ftl": () => import("./content-BoiMaIYB.js").then((m) => m["default"]),
  "../locales/mn/diagnostics.ftl": () => import("./diagnostics-BFXcad1P.js").then((m) => m["default"]),
  "../locales/mn/editor.ftl": () => import("./editor-LPeX_X-O.js").then((m) => m["default"]),
  "../locales/mni/chrome.ftl": () => import("./chrome-CMdM9YVy.js").then((m) => m["default"]),
  "../locales/mni/content.ftl": () => import("./content-DDvt4mU8.js").then((m) => m["default"]),
  "../locales/mni/diagnostics.ftl": () => import("./diagnostics-BRI056OK.js").then((m) => m["default"]),
  "../locales/mni/editor.ftl": () => import("./editor-CNh5geSw.js").then((m) => m["default"]),
  "../locales/mnk/chrome.ftl": () => import("./chrome-DwZc8LOp.js").then((m) => m["default"]),
  "../locales/mnk/content.ftl": () => import("./content-2OUr5nIz.js").then((m) => m["default"]),
  "../locales/mnk/diagnostics.ftl": () => import("./diagnostics-D5lJqKIt.js").then((m) => m["default"]),
  "../locales/mnk/editor.ftl": () => import("./editor-BPQa1j0p.js").then((m) => m["default"]),
  "../locales/mos/chrome.ftl": () => import("./chrome-D1wQtqOu.js").then((m) => m["default"]),
  "../locales/mos/content.ftl": () => import("./content-C67Xsc1z.js").then((m) => m["default"]),
  "../locales/mos/diagnostics.ftl": () => import("./diagnostics-X_-PEhbH.js").then((m) => m["default"]),
  "../locales/mos/editor.ftl": () => import("./editor-DA-m29UL.js").then((m) => m["default"]),
  "../locales/mr/chrome.ftl": () => import("./chrome-7BsBhNb5.js").then((m) => m["default"]),
  "../locales/mr/content.ftl": () => import("./content-BP3102Zm.js").then((m) => m["default"]),
  "../locales/mr/diagnostics.ftl": () => import("./diagnostics-BooyIKeu.js").then((m) => m["default"]),
  "../locales/mr/editor.ftl": () => import("./editor-7e64BWyZ.js").then((m) => m["default"]),
  "../locales/ms/chrome.ftl": () => import("./chrome-D7xHLPQv.js").then((m) => m["default"]),
  "../locales/ms/content.ftl": () => import("./content-Dyp7Qn4T.js").then((m) => m["default"]),
  "../locales/ms/diagnostics.ftl": () => import("./diagnostics-BtZiQGyc.js").then((m) => m["default"]),
  "../locales/ms/editor.ftl": () => import("./editor-B9whGNBQ.js").then((m) => m["default"]),
  "../locales/mt/chrome.ftl": () => import("./chrome-LRjzC2gt.js").then((m) => m["default"]),
  "../locales/mt/content.ftl": () => import("./content-DQKXIheE.js").then((m) => m["default"]),
  "../locales/mt/diagnostics.ftl": () => import("./diagnostics-Cic9l8P4.js").then((m) => m["default"]),
  "../locales/mt/editor.ftl": () => import("./editor-DG9RkQrq.js").then((m) => m["default"]),
  "../locales/my/chrome.ftl": () => import("./chrome-BjgSUlIz.js").then((m) => m["default"]),
  "../locales/my/content.ftl": () => import("./content-B8OvfDw4.js").then((m) => m["default"]),
  "../locales/my/diagnostics.ftl": () => import("./diagnostics-CSsbMRGe.js").then((m) => m["default"]),
  "../locales/my/editor.ftl": () => import("./editor-r3Cm0Ji_.js").then((m) => m["default"]),
  "../locales/myv/chrome.ftl": () => import("./chrome-CzJszSEW.js").then((m) => m["default"]),
  "../locales/myv/content.ftl": () => import("./content-Dds3iFk7.js").then((m) => m["default"]),
  "../locales/myv/diagnostics.ftl": () => import("./diagnostics-5WZMt4rW.js").then((m) => m["default"]),
  "../locales/myv/editor.ftl": () => import("./editor-hK-_64d_.js").then((m) => m["default"]),
  "../locales/nah/chrome.ftl": () => import("./chrome-CA-qQ18L.js").then((m) => m["default"]),
  "../locales/nah/content.ftl": () => import("./content-Cxuuj8Cs.js").then((m) => m["default"]),
  "../locales/nah/diagnostics.ftl": () => import("./diagnostics-BovYEkam.js").then((m) => m["default"]),
  "../locales/nah/editor.ftl": () => import("./editor-CUeI6g4B.js").then((m) => m["default"]),
  "../locales/nb/chrome.ftl": () => import("./chrome-MZ_IYGSD.js").then((m) => m["default"]),
  "../locales/nb/content.ftl": () => import("./content-S9hgIZTI.js").then((m) => m["default"]),
  "../locales/nb/diagnostics.ftl": () => import("./diagnostics-DORyALkm.js").then((m) => m["default"]),
  "../locales/nb/editor.ftl": () => import("./editor-CP3gTbP9.js").then((m) => m["default"]),
  "../locales/nds/chrome.ftl": () => import("./chrome-NrFYpoEY.js").then((m) => m["default"]),
  "../locales/nds/content.ftl": () => import("./content-vK6CXwJk.js").then((m) => m["default"]),
  "../locales/nds/diagnostics.ftl": () => import("./diagnostics-EAIFFCXe.js").then((m) => m["default"]),
  "../locales/nds/editor.ftl": () => import("./editor-DpIR7wK6.js").then((m) => m["default"]),
  "../locales/ne/chrome.ftl": () => import("./chrome-CdRH8bys.js").then((m) => m["default"]),
  "../locales/ne/content.ftl": () => import("./content-C9tDOFbm.js").then((m) => m["default"]),
  "../locales/ne/diagnostics.ftl": () => import("./diagnostics-B660tSeU.js").then((m) => m["default"]),
  "../locales/ne/editor.ftl": () => import("./editor-AXdqEMA0.js").then((m) => m["default"]),
  "../locales/nl/chrome.ftl": () => import("./chrome-D1h4nTC_.js").then((m) => m["default"]),
  "../locales/nl/content.ftl": () => import("./content-KY2omZn-.js").then((m) => m["default"]),
  "../locales/nl/diagnostics.ftl": () => import("./diagnostics-BtfrNhYc.js").then((m) => m["default"]),
  "../locales/nl/editor.ftl": () => import("./editor-C3hzpB0d.js").then((m) => m["default"]),
  "../locales/nso/chrome.ftl": () => import("./chrome-D2uJ27Wn.js").then((m) => m["default"]),
  "../locales/nso/content.ftl": () => import("./content-Dz08tsj8.js").then((m) => m["default"]),
  "../locales/nso/diagnostics.ftl": () => import("./diagnostics-DVgf01S4.js").then((m) => m["default"]),
  "../locales/nso/editor.ftl": () => import("./editor-nvH6BT3p.js").then((m) => m["default"]),
  "../locales/ny/chrome.ftl": () => import("./chrome-InmiFA9h.js").then((m) => m["default"]),
  "../locales/ny/content.ftl": () => import("./content-SgBS3fiK.js").then((m) => m["default"]),
  "../locales/ny/diagnostics.ftl": () => import("./diagnostics-DV-5j5XX.js").then((m) => m["default"]),
  "../locales/ny/editor.ftl": () => import("./editor-C7puFfXQ.js").then((m) => m["default"]),
  "../locales/nyn/chrome.ftl": () => import("./chrome-BaBNdyAg.js").then((m) => m["default"]),
  "../locales/nyn/content.ftl": () => import("./content-BsAWHQhn.js").then((m) => m["default"]),
  "../locales/nyn/diagnostics.ftl": () => import("./diagnostics-BNo_WoyZ.js").then((m) => m["default"]),
  "../locales/nyn/editor.ftl": () => import("./editor-D4HWhqTn.js").then((m) => m["default"]),
  "../locales/oc/chrome.ftl": () => import("./chrome-DOJkFGGu.js").then((m) => m["default"]),
  "../locales/oc/content.ftl": () => import("./content-Xl2Sxy4N.js").then((m) => m["default"]),
  "../locales/oc/diagnostics.ftl": () => import("./diagnostics-DEkry2Cq.js").then((m) => m["default"]),
  "../locales/oc/editor.ftl": () => import("./editor-CrGk11-d.js").then((m) => m["default"]),
  "../locales/oj/chrome.ftl": () => import("./chrome-CUdw-6RD.js").then((m) => m["default"]),
  "../locales/oj/content.ftl": () => import("./content-2fP13HjY.js").then((m) => m["default"]),
  "../locales/oj/diagnostics.ftl": () => import("./diagnostics-CBVSTR-M.js").then((m) => m["default"]),
  "../locales/oj/editor.ftl": () => import("./editor-BCirdfMw.js").then((m) => m["default"]),
  "../locales/om/chrome.ftl": () => import("./chrome-CjhVsG2w.js").then((m) => m["default"]),
  "../locales/om/content.ftl": () => import("./content-Xc7AXCPT.js").then((m) => m["default"]),
  "../locales/om/diagnostics.ftl": () => import("./diagnostics-CazND7PF.js").then((m) => m["default"]),
  "../locales/om/editor.ftl": () => import("./editor-B3FWofO7.js").then((m) => m["default"]),
  "../locales/or/chrome.ftl": () => import("./chrome-BjdiJkUf.js").then((m) => m["default"]),
  "../locales/or/content.ftl": () => import("./content-CSASyEHP.js").then((m) => m["default"]),
  "../locales/or/diagnostics.ftl": () => import("./diagnostics-Iq-35wf6.js").then((m) => m["default"]),
  "../locales/or/editor.ftl": () => import("./editor-D6JC5GKV.js").then((m) => m["default"]),
  "../locales/os/chrome.ftl": () => import("./chrome-C55uz_m5.js").then((m) => m["default"]),
  "../locales/os/content.ftl": () => import("./content-BvrHpQgV.js").then((m) => m["default"]),
  "../locales/os/diagnostics.ftl": () => import("./diagnostics-D1aBvbav.js").then((m) => m["default"]),
  "../locales/os/editor.ftl": () => import("./editor-Cd9gOF-8.js").then((m) => m["default"]),
  "../locales/pa/chrome.ftl": () => import("./chrome-JlpSZC9O.js").then((m) => m["default"]),
  "../locales/pa/content.ftl": () => import("./content-C2Mzvn49.js").then((m) => m["default"]),
  "../locales/pa/diagnostics.ftl": () => import("./diagnostics-Bl6VTckc.js").then((m) => m["default"]),
  "../locales/pa/editor.ftl": () => import("./editor-DLOzJrT_.js").then((m) => m["default"]),
  "../locales/pam/chrome.ftl": () => import("./chrome-D_huAh0-.js").then((m) => m["default"]),
  "../locales/pam/content.ftl": () => import("./content-DTSFoDK_.js").then((m) => m["default"]),
  "../locales/pam/diagnostics.ftl": () => import("./diagnostics-DYJB723e.js").then((m) => m["default"]),
  "../locales/pam/editor.ftl": () => import("./editor-B3bV0Nit.js").then((m) => m["default"]),
  "../locales/pcm/chrome.ftl": () => import("./chrome-DP4KV5K3.js").then((m) => m["default"]),
  "../locales/pcm/content.ftl": () => import("./content-Cahye_Fo.js").then((m) => m["default"]),
  "../locales/pcm/diagnostics.ftl": () => import("./diagnostics-P0SYtQeE.js").then((m) => m["default"]),
  "../locales/pcm/editor.ftl": () => import("./editor-BPgqT4HD.js").then((m) => m["default"]),
  "../locales/pl/chrome.ftl": () => import("./chrome-B6Pk0GLw.js").then((m) => m["default"]),
  "../locales/pl/content.ftl": () => import("./content-1AiwI1W-.js").then((m) => m["default"]),
  "../locales/pl/diagnostics.ftl": () => import("./diagnostics-BlCFtBcT.js").then((m) => m["default"]),
  "../locales/pl/editor.ftl": () => import("./editor-DeZ1mycz.js").then((m) => m["default"]),
  "../locales/ps/chrome.ftl": () => import("./chrome-p1GvtecU.js").then((m) => m["default"]),
  "../locales/ps/content.ftl": () => import("./content-C7oeeAjo.js").then((m) => m["default"]),
  "../locales/ps/diagnostics.ftl": () => import("./diagnostics-DaDo7IAn.js").then((m) => m["default"]),
  "../locales/ps/editor.ftl": () => import("./editor-wfD0t9SY.js").then((m) => m["default"]),
  "../locales/pt/chrome.ftl": () => import("./chrome-C1oJJLZD.js").then((m) => m["default"]),
  "../locales/pt/content.ftl": () => import("./content-Dcjg1axI.js").then((m) => m["default"]),
  "../locales/pt/diagnostics.ftl": () => import("./diagnostics-CwRiIrom.js").then((m) => m["default"]),
  "../locales/pt/editor.ftl": () => import("./editor-BVSn8RVg.js").then((m) => m["default"]),
  "../locales/qu/chrome.ftl": () => import("./chrome-CFDeVFTt.js").then((m) => m["default"]),
  "../locales/qu/content.ftl": () => import("./content-CJcwebUp.js").then((m) => m["default"]),
  "../locales/qu/diagnostics.ftl": () => import("./diagnostics-CbqcWl-D.js").then((m) => m["default"]),
  "../locales/qu/editor.ftl": () => import("./editor-CTzgcl8e.js").then((m) => m["default"]),
  "../locales/quc/chrome.ftl": () => import("./chrome-Cp5KT5m3.js").then((m) => m["default"]),
  "../locales/quc/content.ftl": () => import("./content-C9cC5var.js").then((m) => m["default"]),
  "../locales/quc/diagnostics.ftl": () => import("./diagnostics-BXrkoIoH.js").then((m) => m["default"]),
  "../locales/quc/editor.ftl": () => import("./editor-BG8at2Vx.js").then((m) => m["default"]),
  "../locales/rm/chrome.ftl": () => import("./chrome-nsWyzOHj.js").then((m) => m["default"]),
  "../locales/rm/content.ftl": () => import("./content-D64HK-Xp.js").then((m) => m["default"]),
  "../locales/rm/diagnostics.ftl": () => import("./diagnostics-DV57XDL9.js").then((m) => m["default"]),
  "../locales/rm/editor.ftl": () => import("./editor-Bqd2bKnO.js").then((m) => m["default"]),
  "../locales/rn/chrome.ftl": () => import("./chrome-Dwh3vy0D.js").then((m) => m["default"]),
  "../locales/rn/content.ftl": () => import("./content-CjM5nnGW.js").then((m) => m["default"]),
  "../locales/rn/diagnostics.ftl": () => import("./diagnostics-CKMF8ZPM.js").then((m) => m["default"]),
  "../locales/rn/editor.ftl": () => import("./editor-BJDs7NDl.js").then((m) => m["default"]),
  "../locales/ro/chrome.ftl": () => import("./chrome-DZC8jPH-.js").then((m) => m["default"]),
  "../locales/ro/content.ftl": () => import("./content-CBuh-zxa.js").then((m) => m["default"]),
  "../locales/ro/diagnostics.ftl": () => import("./diagnostics-CVbpZKYs.js").then((m) => m["default"]),
  "../locales/ro/editor.ftl": () => import("./editor-9xbSmJTU.js").then((m) => m["default"]),
  "../locales/ru/chrome.ftl": () => import("./chrome-Dbyp13c6.js").then((m) => m["default"]),
  "../locales/ru/content.ftl": () => import("./content-DIzG7CoF.js").then((m) => m["default"]),
  "../locales/ru/diagnostics.ftl": () => import("./diagnostics-ChJ-E3yU.js").then((m) => m["default"]),
  "../locales/ru/editor.ftl": () => import("./editor-DGGxFNYi.js").then((m) => m["default"]),
  "../locales/rw/chrome.ftl": () => import("./chrome-BTdPYGYc.js").then((m) => m["default"]),
  "../locales/rw/content.ftl": () => import("./content-KZe-iAWj.js").then((m) => m["default"]),
  "../locales/rw/diagnostics.ftl": () => import("./diagnostics-AkaYeETP.js").then((m) => m["default"]),
  "../locales/rw/editor.ftl": () => import("./editor-D0FGELL6.js").then((m) => m["default"]),
  "../locales/sa/chrome.ftl": () => import("./chrome-BwLpnMQN.js").then((m) => m["default"]),
  "../locales/sa/content.ftl": () => import("./content-qbiYQ6qz.js").then((m) => m["default"]),
  "../locales/sa/diagnostics.ftl": () => import("./diagnostics-CXoCIBAJ.js").then((m) => m["default"]),
  "../locales/sa/editor.ftl": () => import("./editor-BJygq5NK.js").then((m) => m["default"]),
  "../locales/sah/chrome.ftl": () => import("./chrome-CjjDq2us.js").then((m) => m["default"]),
  "../locales/sah/content.ftl": () => import("./content-BGAXbENC.js").then((m) => m["default"]),
  "../locales/sah/diagnostics.ftl": () => import("./diagnostics-zLNSfDKm.js").then((m) => m["default"]),
  "../locales/sah/editor.ftl": () => import("./editor-CFtCWTLO.js").then((m) => m["default"]),
  "../locales/sat/chrome.ftl": () => import("./chrome-DWpj2BYm.js").then((m) => m["default"]),
  "../locales/sat/content.ftl": () => import("./content-B1BQLXf9.js").then((m) => m["default"]),
  "../locales/sat/diagnostics.ftl": () => import("./diagnostics-2MlskzRu.js").then((m) => m["default"]),
  "../locales/sat/editor.ftl": () => import("./editor-D31pFnax.js").then((m) => m["default"]),
  "../locales/sc/chrome.ftl": () => import("./chrome-DmwqtdEK.js").then((m) => m["default"]),
  "../locales/sc/content.ftl": () => import("./content-DAXSzYkG.js").then((m) => m["default"]),
  "../locales/sc/diagnostics.ftl": () => import("./diagnostics-DCUxfl9s.js").then((m) => m["default"]),
  "../locales/sc/editor.ftl": () => import("./editor-BHQdF2Gr.js").then((m) => m["default"]),
  "../locales/scn/chrome.ftl": () => import("./chrome-Bx_lMTNo.js").then((m) => m["default"]),
  "../locales/scn/content.ftl": () => import("./content-DPEIuoS8.js").then((m) => m["default"]),
  "../locales/scn/diagnostics.ftl": () => import("./diagnostics-DDg8R2QF.js").then((m) => m["default"]),
  "../locales/scn/editor.ftl": () => import("./editor-SVw4BhQu.js").then((m) => m["default"]),
  "../locales/sd/chrome.ftl": () => import("./chrome-BvZES_Os.js").then((m) => m["default"]),
  "../locales/sd/content.ftl": () => import("./content-DNsfLRG0.js").then((m) => m["default"]),
  "../locales/sd/diagnostics.ftl": () => import("./diagnostics-tyAQ2UZm.js").then((m) => m["default"]),
  "../locales/sd/editor.ftl": () => import("./editor-COcPqrGZ.js").then((m) => m["default"]),
  "../locales/se/chrome.ftl": () => import("./chrome-BjQkWW7d.js").then((m) => m["default"]),
  "../locales/se/content.ftl": () => import("./content-BqYEc07e.js").then((m) => m["default"]),
  "../locales/se/diagnostics.ftl": () => import("./diagnostics-BD-iOcBS.js").then((m) => m["default"]),
  "../locales/se/editor.ftl": () => import("./editor-CgOFZk1W.js").then((m) => m["default"]),
  "../locales/sg/chrome.ftl": () => import("./chrome-C-27Qbtc.js").then((m) => m["default"]),
  "../locales/sg/content.ftl": () => import("./content-BEOzfXgP.js").then((m) => m["default"]),
  "../locales/sg/diagnostics.ftl": () => import("./diagnostics-DwroGd2G.js").then((m) => m["default"]),
  "../locales/sg/editor.ftl": () => import("./editor-DAsDB0Fp.js").then((m) => m["default"]),
  "../locales/shi/chrome.ftl": () => import("./chrome-DP7fTCNR.js").then((m) => m["default"]),
  "../locales/shi/content.ftl": () => import("./content-UZJ7zTla.js").then((m) => m["default"]),
  "../locales/shi/diagnostics.ftl": () => import("./diagnostics-BvrEabYj.js").then((m) => m["default"]),
  "../locales/shi/editor.ftl": () => import("./editor-CxuZukGj.js").then((m) => m["default"]),
  "../locales/si/chrome.ftl": () => import("./chrome-DKWG-cpW.js").then((m) => m["default"]),
  "../locales/si/content.ftl": () => import("./content-CAWWqpxO.js").then((m) => m["default"]),
  "../locales/si/diagnostics.ftl": () => import("./diagnostics-CPD4z0kV.js").then((m) => m["default"]),
  "../locales/si/editor.ftl": () => import("./editor-Cnjm5v9E.js").then((m) => m["default"]),
  "../locales/sk/chrome.ftl": () => import("./chrome-HSS8RlpL.js").then((m) => m["default"]),
  "../locales/sk/content.ftl": () => import("./content-CycHylS-.js").then((m) => m["default"]),
  "../locales/sk/diagnostics.ftl": () => import("./diagnostics-CrsS4EwB.js").then((m) => m["default"]),
  "../locales/sk/editor.ftl": () => import("./editor-B9Hrq8ge.js").then((m) => m["default"]),
  "../locales/sl/chrome.ftl": () => import("./chrome-DgYZTRwH.js").then((m) => m["default"]),
  "../locales/sl/content.ftl": () => import("./content-B-9WdxAM.js").then((m) => m["default"]),
  "../locales/sl/diagnostics.ftl": () => import("./diagnostics-DrEpahHK.js").then((m) => m["default"]),
  "../locales/sl/editor.ftl": () => import("./editor-ChGGLVsC.js").then((m) => m["default"]),
  "../locales/sm/chrome.ftl": () => import("./chrome-D2veOoN1.js").then((m) => m["default"]),
  "../locales/sm/content.ftl": () => import("./content-BAmB2fyg.js").then((m) => m["default"]),
  "../locales/sm/diagnostics.ftl": () => import("./diagnostics-COCkd6SQ.js").then((m) => m["default"]),
  "../locales/sm/editor.ftl": () => import("./editor-Di1F-NOv.js").then((m) => m["default"]),
  "../locales/sn/chrome.ftl": () => import("./chrome-D1flemtv.js").then((m) => m["default"]),
  "../locales/sn/content.ftl": () => import("./content-DT92DIDT.js").then((m) => m["default"]),
  "../locales/sn/diagnostics.ftl": () => import("./diagnostics-4tMk6WXw.js").then((m) => m["default"]),
  "../locales/sn/editor.ftl": () => import("./editor-CiX-hg3T.js").then((m) => m["default"]),
  "../locales/so/chrome.ftl": () => import("./chrome-DJydrXkX.js").then((m) => m["default"]),
  "../locales/so/content.ftl": () => import("./content-SeQXZJLW.js").then((m) => m["default"]),
  "../locales/so/diagnostics.ftl": () => import("./diagnostics-Cg1JlAdR.js").then((m) => m["default"]),
  "../locales/so/editor.ftl": () => import("./editor-BdAbY--0.js").then((m) => m["default"]),
  "../locales/sq/chrome.ftl": () => import("./chrome-CkfzTj1C.js").then((m) => m["default"]),
  "../locales/sq/content.ftl": () => import("./content-CGi3G7gS.js").then((m) => m["default"]),
  "../locales/sq/diagnostics.ftl": () => import("./diagnostics-ByMdXcrK.js").then((m) => m["default"]),
  "../locales/sq/editor.ftl": () => import("./editor-DpauK4ae.js").then((m) => m["default"]),
  "../locales/sr/chrome.ftl": () => import("./chrome-DKqaK4cK.js").then((m) => m["default"]),
  "../locales/sr/content.ftl": () => import("./content-7Uco0Cxy.js").then((m) => m["default"]),
  "../locales/sr/diagnostics.ftl": () => import("./diagnostics-CQDVVVqu.js").then((m) => m["default"]),
  "../locales/sr/editor.ftl": () => import("./editor-CY0LWGNo.js").then((m) => m["default"]),
  "../locales/ss/chrome.ftl": () => import("./chrome-DbEf8xd0.js").then((m) => m["default"]),
  "../locales/ss/content.ftl": () => import("./content-DU_k16aU.js").then((m) => m["default"]),
  "../locales/ss/diagnostics.ftl": () => import("./diagnostics-ky6Jk9E3.js").then((m) => m["default"]),
  "../locales/ss/editor.ftl": () => import("./editor-DTek_1cT.js").then((m) => m["default"]),
  "../locales/st/chrome.ftl": () => import("./chrome-Bq30DPhq.js").then((m) => m["default"]),
  "../locales/st/content.ftl": () => import("./content-Bgrn_uza.js").then((m) => m["default"]),
  "../locales/st/diagnostics.ftl": () => import("./diagnostics-DYyZEUp-.js").then((m) => m["default"]),
  "../locales/st/editor.ftl": () => import("./editor-CgVCe8gJ.js").then((m) => m["default"]),
  "../locales/su/chrome.ftl": () => import("./chrome-n0jtx5hq.js").then((m) => m["default"]),
  "../locales/su/content.ftl": () => import("./content-DZMovYal.js").then((m) => m["default"]),
  "../locales/su/diagnostics.ftl": () => import("./diagnostics-ppRxVg8i.js").then((m) => m["default"]),
  "../locales/su/editor.ftl": () => import("./editor-Daf6uJEx.js").then((m) => m["default"]),
  "../locales/sus/chrome.ftl": () => import("./chrome-suODBStr.js").then((m) => m["default"]),
  "../locales/sus/content.ftl": () => import("./content-C1OxXG8-.js").then((m) => m["default"]),
  "../locales/sus/diagnostics.ftl": () => import("./diagnostics-G18CmoJU.js").then((m) => m["default"]),
  "../locales/sus/editor.ftl": () => import("./editor-B8HJu0ed.js").then((m) => m["default"]),
  "../locales/sv/chrome.ftl": () => import("./chrome-Ddq2ZBF7.js").then((m) => m["default"]),
  "../locales/sv/content.ftl": () => import("./content-D-XAQyKT.js").then((m) => m["default"]),
  "../locales/sv/diagnostics.ftl": () => import("./diagnostics-BXG53erX.js").then((m) => m["default"]),
  "../locales/sv/editor.ftl": () => import("./editor-BXFywlxW.js").then((m) => m["default"]),
  "../locales/sw/chrome.ftl": () => import("./chrome-BHml2NSG.js").then((m) => m["default"]),
  "../locales/sw/content.ftl": () => import("./content-C8JzMnIq.js").then((m) => m["default"]),
  "../locales/sw/diagnostics.ftl": () => import("./diagnostics-BfESdxqW.js").then((m) => m["default"]),
  "../locales/sw/editor.ftl": () => import("./editor-C__tOg36.js").then((m) => m["default"]),
  "../locales/ta/chrome.ftl": () => import("./chrome-B--_cxnp.js").then((m) => m["default"]),
  "../locales/ta/content.ftl": () => import("./content-i-L2hbay.js").then((m) => m["default"]),
  "../locales/ta/diagnostics.ftl": () => import("./diagnostics-DD-Q27T_.js").then((m) => m["default"]),
  "../locales/ta/editor.ftl": () => import("./editor-CvSOX_D0.js").then((m) => m["default"]),
  "../locales/te/chrome.ftl": () => import("./chrome-C_Ire2Fi.js").then((m) => m["default"]),
  "../locales/te/content.ftl": () => import("./content-BVBRU8gC.js").then((m) => m["default"]),
  "../locales/te/diagnostics.ftl": () => import("./diagnostics-DXlcoGy6.js").then((m) => m["default"]),
  "../locales/te/editor.ftl": () => import("./editor-B1EuEEX2.js").then((m) => m["default"]),
  "../locales/tem/chrome.ftl": () => import("./chrome-CDihyOpm.js").then((m) => m["default"]),
  "../locales/tem/content.ftl": () => import("./content-CTL-DApH.js").then((m) => m["default"]),
  "../locales/tem/diagnostics.ftl": () => import("./diagnostics-PGKBha8h.js").then((m) => m["default"]),
  "../locales/tem/editor.ftl": () => import("./editor-Bo13ojTs.js").then((m) => m["default"]),
  "../locales/tet/chrome.ftl": () => import("./chrome-HQEVqi65.js").then((m) => m["default"]),
  "../locales/tet/content.ftl": () => import("./content-u2asXZpw.js").then((m) => m["default"]),
  "../locales/tet/diagnostics.ftl": () => import("./diagnostics-CSArNBQV.js").then((m) => m["default"]),
  "../locales/tet/editor.ftl": () => import("./editor-C4MXfki7.js").then((m) => m["default"]),
  "../locales/tg/chrome.ftl": () => import("./chrome-BRs6hNxD.js").then((m) => m["default"]),
  "../locales/tg/content.ftl": () => import("./content-DSLb3kZ1.js").then((m) => m["default"]),
  "../locales/tg/diagnostics.ftl": () => import("./diagnostics-C_e7abDO.js").then((m) => m["default"]),
  "../locales/tg/editor.ftl": () => import("./editor-BLuGhMQM.js").then((m) => m["default"]),
  "../locales/th/chrome.ftl": () => import("./chrome-DWKqAqtU.js").then((m) => m["default"]),
  "../locales/th/content.ftl": () => import("./content-DOkzF4p-.js").then((m) => m["default"]),
  "../locales/th/diagnostics.ftl": () => import("./diagnostics-BO6jjKGP.js").then((m) => m["default"]),
  "../locales/th/editor.ftl": () => import("./editor-BuxvrUt9.js").then((m) => m["default"]),
  "../locales/ti/chrome.ftl": () => import("./chrome-DfFpq8OP.js").then((m) => m["default"]),
  "../locales/ti/content.ftl": () => import("./content-03n4weyG.js").then((m) => m["default"]),
  "../locales/ti/diagnostics.ftl": () => import("./diagnostics-DTuJR44f.js").then((m) => m["default"]),
  "../locales/ti/editor.ftl": () => import("./editor-uAK-grB8.js").then((m) => m["default"]),
  "../locales/tiv/chrome.ftl": () => import("./chrome-yVHrANw5.js").then((m) => m["default"]),
  "../locales/tiv/content.ftl": () => import("./content-C0uEIjs6.js").then((m) => m["default"]),
  "../locales/tiv/diagnostics.ftl": () => import("./diagnostics-BkaXE-Wk.js").then((m) => m["default"]),
  "../locales/tiv/editor.ftl": () => import("./editor-Do_OMcVv.js").then((m) => m["default"]),
  "../locales/tk/chrome.ftl": () => import("./chrome-BI6iZrto.js").then((m) => m["default"]),
  "../locales/tk/content.ftl": () => import("./content-CuUr_Qvo.js").then((m) => m["default"]),
  "../locales/tk/diagnostics.ftl": () => import("./diagnostics-C2Jjjl8T.js").then((m) => m["default"]),
  "../locales/tk/editor.ftl": () => import("./editor-DZ8VZouE.js").then((m) => m["default"]),
  "../locales/tlh/chrome.ftl": () => import("./chrome-CdPGl0bR.js").then((m) => m["default"]),
  "../locales/tlh/content.ftl": () => import("./content-cjeGTP9d.js").then((m) => m["default"]),
  "../locales/tlh/diagnostics.ftl": () => import("./diagnostics-hrIiBX_j.js").then((m) => m["default"]),
  "../locales/tlh/editor.ftl": () => import("./editor-BpOw8Zb4.js").then((m) => m["default"]),
  "../locales/tn/chrome.ftl": () => import("./chrome-qiltb9T3.js").then((m) => m["default"]),
  "../locales/tn/content.ftl": () => import("./content-DmNrDOEx.js").then((m) => m["default"]),
  "../locales/tn/diagnostics.ftl": () => import("./diagnostics-D2lHyE48.js").then((m) => m["default"]),
  "../locales/tn/editor.ftl": () => import("./editor-Bt2Rxj-H.js").then((m) => m["default"]),
  "../locales/to/chrome.ftl": () => import("./chrome-DA_VGo_0.js").then((m) => m["default"]),
  "../locales/to/content.ftl": () => import("./content-B-8jIUn1.js").then((m) => m["default"]),
  "../locales/to/diagnostics.ftl": () => import("./diagnostics-Cmzii-eC.js").then((m) => m["default"]),
  "../locales/to/editor.ftl": () => import("./editor-CcE9Rwmd.js").then((m) => m["default"]),
  "../locales/tpi/chrome.ftl": () => import("./chrome-CLgju_6z.js").then((m) => m["default"]),
  "../locales/tpi/content.ftl": () => import("./content-p5moz9mF.js").then((m) => m["default"]),
  "../locales/tpi/diagnostics.ftl": () => import("./diagnostics-CqCaryLz.js").then((m) => m["default"]),
  "../locales/tpi/editor.ftl": () => import("./editor-D2mG-PiF.js").then((m) => m["default"]),
  "../locales/tr/chrome.ftl": () => import("./chrome-BYDlcPEX.js").then((m) => m["default"]),
  "../locales/tr/content.ftl": () => import("./content-CL3kwnsb.js").then((m) => m["default"]),
  "../locales/tr/diagnostics.ftl": () => import("./diagnostics-DHsgQeXJ.js").then((m) => m["default"]),
  "../locales/tr/editor.ftl": () => import("./editor-0rYAgiep.js").then((m) => m["default"]),
  "../locales/ts/chrome.ftl": () => import("./chrome-D4UjSUr-.js").then((m) => m["default"]),
  "../locales/ts/content.ftl": () => import("./content-BafQh8yy.js").then((m) => m["default"]),
  "../locales/ts/diagnostics.ftl": () => import("./diagnostics-CINztezT.js").then((m) => m["default"]),
  "../locales/ts/editor.ftl": () => import("./editor-sJbpPQKH.js").then((m) => m["default"]),
  "../locales/tt/chrome.ftl": () => import("./chrome-DtfJi3Nt.js").then((m) => m["default"]),
  "../locales/tt/content.ftl": () => import("./content-VjWh3Iza.js").then((m) => m["default"]),
  "../locales/tt/diagnostics.ftl": () => import("./diagnostics-ByMjlw7Q.js").then((m) => m["default"]),
  "../locales/tt/editor.ftl": () => import("./editor-8EELgJgL.js").then((m) => m["default"]),
  "../locales/ty/chrome.ftl": () => import("./chrome-BAAFLMnE.js").then((m) => m["default"]),
  "../locales/ty/content.ftl": () => import("./content-XyLqI3C4.js").then((m) => m["default"]),
  "../locales/ty/diagnostics.ftl": () => import("./diagnostics-CCAb0CsR.js").then((m) => m["default"]),
  "../locales/ty/editor.ftl": () => import("./editor-CqF32WOI.js").then((m) => m["default"]),
  "../locales/tyv/chrome.ftl": () => import("./chrome-CNurEtgj.js").then((m) => m["default"]),
  "../locales/tyv/content.ftl": () => import("./content-dbV2QkfL.js").then((m) => m["default"]),
  "../locales/tyv/diagnostics.ftl": () => import("./diagnostics-DQCEqt8S.js").then((m) => m["default"]),
  "../locales/tyv/editor.ftl": () => import("./editor-CFi17HxZ.js").then((m) => m["default"]),
  "../locales/udm/chrome.ftl": () => import("./chrome-DHBZd3j-.js").then((m) => m["default"]),
  "../locales/udm/content.ftl": () => import("./content-c9bE6AS3.js").then((m) => m["default"]),
  "../locales/udm/diagnostics.ftl": () => import("./diagnostics-Brs7OYmZ.js").then((m) => m["default"]),
  "../locales/udm/editor.ftl": () => import("./editor-LIODEAcR.js").then((m) => m["default"]),
  "../locales/ug/chrome.ftl": () => import("./chrome-DRQmdKtl.js").then((m) => m["default"]),
  "../locales/ug/content.ftl": () => import("./content-DzqTzPQV.js").then((m) => m["default"]),
  "../locales/ug/diagnostics.ftl": () => import("./diagnostics-wZ590rUF.js").then((m) => m["default"]),
  "../locales/ug/editor.ftl": () => import("./editor-C4687qnc.js").then((m) => m["default"]),
  "../locales/uk/chrome.ftl": () => import("./chrome-CWV-OG2Z.js").then((m) => m["default"]),
  "../locales/uk/content.ftl": () => import("./content-CwRc5UCG.js").then((m) => m["default"]),
  "../locales/uk/diagnostics.ftl": () => import("./diagnostics-GCMGejiY.js").then((m) => m["default"]),
  "../locales/uk/editor.ftl": () => import("./editor-DIaK7AW8.js").then((m) => m["default"]),
  "../locales/umb/chrome.ftl": () => import("./chrome-Cm4pRELD.js").then((m) => m["default"]),
  "../locales/umb/content.ftl": () => import("./content-D0YYD_Kc.js").then((m) => m["default"]),
  "../locales/umb/diagnostics.ftl": () => import("./diagnostics-DaquRqXK.js").then((m) => m["default"]),
  "../locales/umb/editor.ftl": () => import("./editor-Bf7RythT.js").then((m) => m["default"]),
  "../locales/ur/chrome.ftl": () => import("./chrome-B-HraoxX.js").then((m) => m["default"]),
  "../locales/ur/content.ftl": () => import("./content-0BpxRhqI.js").then((m) => m["default"]),
  "../locales/ur/diagnostics.ftl": () => import("./diagnostics-CEcl2kEo.js").then((m) => m["default"]),
  "../locales/ur/editor.ftl": () => import("./editor-BhcgHXzd.js").then((m) => m["default"]),
  "../locales/urh/chrome.ftl": () => import("./chrome-CPQdPt8U.js").then((m) => m["default"]),
  "../locales/urh/content.ftl": () => import("./content-DlIJiYuN.js").then((m) => m["default"]),
  "../locales/urh/diagnostics.ftl": () => import("./diagnostics-CNIjrZHf.js").then((m) => m["default"]),
  "../locales/urh/editor.ftl": () => import("./editor-_Mkx0Ubz.js").then((m) => m["default"]),
  "../locales/uz/chrome.ftl": () => import("./chrome-BU7eJxLW.js").then((m) => m["default"]),
  "../locales/uz/content.ftl": () => import("./content-CMpKTIh_.js").then((m) => m["default"]),
  "../locales/uz/diagnostics.ftl": () => import("./diagnostics-DHCSDrkK.js").then((m) => m["default"]),
  "../locales/uz/editor.ftl": () => import("./editor-BYsShDoW.js").then((m) => m["default"]),
  "../locales/ve/chrome.ftl": () => import("./chrome-DL_ZYU5X.js").then((m) => m["default"]),
  "../locales/ve/content.ftl": () => import("./content-Dr06Hfrr.js").then((m) => m["default"]),
  "../locales/ve/diagnostics.ftl": () => import("./diagnostics-BTqFNg3x.js").then((m) => m["default"]),
  "../locales/ve/editor.ftl": () => import("./editor-CUaLDJcS.js").then((m) => m["default"]),
  "../locales/vi/chrome.ftl": () => import("./chrome-BGDlJ2ec.js").then((m) => m["default"]),
  "../locales/vi/content.ftl": () => import("./content-QaXfvtsL.js").then((m) => m["default"]),
  "../locales/vi/diagnostics.ftl": () => import("./diagnostics-DS52kX3L.js").then((m) => m["default"]),
  "../locales/vi/editor.ftl": () => import("./editor-BBgnmbaC.js").then((m) => m["default"]),
  "../locales/war/chrome.ftl": () => import("./chrome-COPAU619.js").then((m) => m["default"]),
  "../locales/war/content.ftl": () => import("./content-BpqypNCx.js").then((m) => m["default"]),
  "../locales/war/diagnostics.ftl": () => import("./diagnostics-3HXE8P3-.js").then((m) => m["default"]),
  "../locales/war/editor.ftl": () => import("./editor-DgAuucbH.js").then((m) => m["default"]),
  "../locales/wo/chrome.ftl": () => import("./chrome-C6n5nFAM.js").then((m) => m["default"]),
  "../locales/wo/content.ftl": () => import("./content-Duecsdvw.js").then((m) => m["default"]),
  "../locales/wo/diagnostics.ftl": () => import("./diagnostics-Cym8WLuD.js").then((m) => m["default"]),
  "../locales/wo/editor.ftl": () => import("./editor-Cu54xRJ1.js").then((m) => m["default"]),
  "../locales/xal/chrome.ftl": () => import("./chrome-fJtGQiL6.js").then((m) => m["default"]),
  "../locales/xal/content.ftl": () => import("./content-DOsl_Yxc.js").then((m) => m["default"]),
  "../locales/xal/diagnostics.ftl": () => import("./diagnostics-BTzI_9Uj.js").then((m) => m["default"]),
  "../locales/xal/editor.ftl": () => import("./editor-CyYW28yq.js").then((m) => m["default"]),
  "../locales/xh/chrome.ftl": () => import("./chrome-CkdHbwSZ.js").then((m) => m["default"]),
  "../locales/xh/content.ftl": () => import("./content-HaQ3Bu7y.js").then((m) => m["default"]),
  "../locales/xh/diagnostics.ftl": () => import("./diagnostics-Czsbo4QZ.js").then((m) => m["default"]),
  "../locales/xh/editor.ftl": () => import("./editor-QRZXb8el.js").then((m) => m["default"]),
  "../locales/yi/chrome.ftl": () => import("./chrome-DMW4j7xE.js").then((m) => m["default"]),
  "../locales/yi/content.ftl": () => import("./content-DB_aPs81.js").then((m) => m["default"]),
  "../locales/yi/diagnostics.ftl": () => import("./diagnostics-DmfKkvsl.js").then((m) => m["default"]),
  "../locales/yi/editor.ftl": () => import("./editor-Cwfx5ks8.js").then((m) => m["default"]),
  "../locales/yo/chrome.ftl": () => import("./chrome-r2VYDtHd.js").then((m) => m["default"]),
  "../locales/yo/content.ftl": () => import("./content-DlLOTOCw.js").then((m) => m["default"]),
  "../locales/yo/diagnostics.ftl": () => import("./diagnostics-BRiF1EEN.js").then((m) => m["default"]),
  "../locales/yo/editor.ftl": () => import("./editor-2dJrDhCY.js").then((m) => m["default"]),
  "../locales/zgh/chrome.ftl": () => import("./chrome-CEomPM3t.js").then((m) => m["default"]),
  "../locales/zgh/content.ftl": () => import("./content-O41rNS73.js").then((m) => m["default"]),
  "../locales/zgh/diagnostics.ftl": () => import("./diagnostics-DKA5hbQS.js").then((m) => m["default"]),
  "../locales/zgh/editor.ftl": () => import("./editor-BKcLKdQQ.js").then((m) => m["default"]),
  "../locales/zh-Hans/chrome.ftl": () => import("./chrome-CkNs_a9J.js").then((m) => m["default"]),
  "../locales/zh-Hans/content.ftl": () => import("./content-BtAqEt4i.js").then((m) => m["default"]),
  "../locales/zh-Hans/diagnostics.ftl": () => import("./diagnostics-Z-Cb-y6t.js").then((m) => m["default"]),
  "../locales/zh-Hans/editor.ftl": () => import("./editor-D3BAMHXa.js").then((m) => m["default"]),
  "../locales/zh-Hant/chrome.ftl": () => import("./chrome-CD6j5W6H.js").then((m) => m["default"]),
  "../locales/zh-Hant/content.ftl": () => import("./content-BaaqpUHm.js").then((m) => m["default"]),
  "../locales/zh-Hant/diagnostics.ftl": () => import("./diagnostics-CgbPCe1u.js").then((m) => m["default"]),
  "../locales/zh-Hant/editor.ftl": () => import("./editor-DTvxMJqO.js").then((m) => m["default"]),
  "../locales/zu/chrome.ftl": () => import("./chrome-Dt3NNKl1.js").then((m) => m["default"]),
  "../locales/zu/content.ftl": () => import("./content-C9NUMcD7.js").then((m) => m["default"]),
  "../locales/zu/diagnostics.ftl": () => import("./diagnostics-q-OjkzwD.js").then((m) => m["default"]),
  "../locales/zu/editor.ftl": () => import("./editor-CMknnhuI.js").then((m) => m["default"])
}) : {};
const CATALOG_PATH_PATTERN = /\/locales\/([^/]+)\/([^/]+)\.ftl$/;
function isCatalogNamespace(name) {
  return CATALOG_NAMESPACES.includes(name);
}
function loadersFromModules(modules) {
  const byLocale = {};
  for (const [modulePath, importModule] of Object.entries(modules)) {
    const match2 = CATALOG_PATH_PATTERN.exec(modulePath);
    if (!match2 || !isCatalogNamespace(match2[2])) {
      continue;
    }
    (byLocale[match2[1]] ??= {})[match2[2]] = importModule;
  }
  const loaders = {};
  for (const [locale, importers] of Object.entries(byLocale)) {
    loaders[locale] = async (namespaces) => {
      const wanted = namespaces.filter(
        (namespace) => Boolean(importers[namespace])
      );
      const sources = await Promise.all(
        wanted.map((namespace) => importers[namespace]())
      );
      const catalogs = {};
      wanted.forEach((namespace, index) => {
        catalogs[namespace] = sources[index];
      });
      return catalogs;
    };
  }
  return loaders;
}
const LAZY_LOCALE_LOADERS = loadersFromModules(LAZY_CATALOG_MODULES);
function fetchLocaleLoaders(baseUrl, locales = SUPPORTED_LOCALES.map((info) => info.locale)) {
  const base = baseUrl.toString().replace(/\/?$/, "/");
  const loaders = {};
  for (const locale of locales) {
    if (locale === DEFAULT_LOCALE) {
      continue;
    }
    loaders[locale] = async (namespaces) => {
      const catalogs = {};
      await Promise.all(
        namespaces.map(async (namespace) => {
          try {
            const url = new URL(`${locale}/${namespace}.ftl`, base);
            const response = await fetch(url);
            if (response.ok) {
              catalogs[namespace] = await response.text();
            }
          } catch {
          }
        })
      );
      return catalogs;
    };
  }
  return loaders;
}
let installedLoaders = null;
function setLocaleLoaders(loaders) {
  installedLoaders = loaders;
  inFlight.clear();
}
function currentLocaleLoaders() {
  return installedLoaders ?? LAZY_LOCALE_LOADERS;
}
function requestKey(locale, namespaces, available) {
  return [
    locale,
    [...namespaces].sort().join("+"),
    [...available].sort().join("+")
  ].join("|");
}
const inFlight = /* @__PURE__ */ new Map();
async function loadLocaleResources(locale, namespaces = CATALOG_NAMESPACES, options = {}) {
  const installed = currentLocaleLoaders();
  const { loaders = installed, available = [] } = options;
  const normalized = normalizeLocaleTag(locale);
  if (!normalized || normalized === DEFAULT_LOCALE) {
    return {};
  }
  const onHand = [...BUNDLED_LOCALES, ...available];
  const loadable = Object.keys(loaders);
  const chain = negotiateLocales([normalized], [...onHand, ...loadable]);
  const negotiated = chain.find((tag) => tag !== DEFAULT_LOCALE);
  if (!negotiated || onHand.includes(negotiated) || !loaders[negotiated]) {
    return {};
  }
  const target = negotiated;
  const loader = loaders[target];
  async function load() {
    const source = combineCatalogs(await loader(namespaces));
    return source ? { [target]: source } : {};
  }
  if (loaders !== installed) {
    return load();
  }
  const key = requestKey(target, namespaces, onHand);
  let pending = inFlight.get(key);
  if (!pending) {
    pending = load().catch((error) => {
      if (inFlight.get(key) === pending) {
        inFlight.delete(key);
      }
      throw error;
    });
    inFlight.set(key, pending);
  }
  return pending;
}
async function loadLocaleResourcesFor(locales, namespaces = CATALOG_NAMESPACES, options = {}) {
  const wanted = [
    ...new Set(locales.filter((locale) => !!locale))
  ];
  const loaded = await Promise.all(
    wanted.map(
      (locale) => loadLocaleResources(locale, namespaces, options)
    )
  );
  return Object.assign({}, ...loaded);
}
function reachableCatalogLocales(locales, resources) {
  const available = Object.keys(resources ?? {});
  if (available.length === 0) {
    return [];
  }
  const reachable = /* @__PURE__ */ new Set();
  for (const locale of locales) {
    if (!locale) {
      continue;
    }
    for (const tag of negotiateLocales([locale], available)) {
      if (available.includes(tag)) {
        reachable.add(tag);
      }
    }
  }
  return [...reachable].sort();
}
const DEFAULT_LOCALE_DATA = {
  locale: DEFAULT_LOCALE,
  resources: {}
};
function createTranslatorFromLocaleData(localeData, locale = localeData.locale) {
  const { resources } = localeData;
  const chain = negotiateLocales([locale], Object.keys(resources));
  return createTranslator(chain, resources);
}
const DIAGNOSTIC_CODES = {
  "doenet-i0001": "line-segment-attributes-ignored-with-endpoints",
  "doenet-i0002": "line-segment-midpoint-offset-without-midpoint",
  "doenet-i0003": "line-segment-attributes-ignored-with-endpoint-and-midpoint",
  "doenet-i0004": "choice-input-indices-count-mismatch",
  "doenet-i0005": "pretzel-indices-count-mismatch",
  "doenet-i0006": "shuffle-indices-count-mismatch",
  "doenet-i0007": "indices-ignored-out-of-range",
  "doenet-i0008": "pretzel-indices-repeated",
  "doenet-i0009": "pretzel-circuit-first-index",
  "doenet-i0010": "variant-index-must-be-number",
  "doenet-i0011": "variant-index-must-be-integer",
  "doenet-i0012": "sequence-invalid-length",
  "doenet-i0013": "sequence-invalid-step",
  "doenet-i0014": "sequence-invalid-endpoint-number",
  "doenet-i0015": "sequence-invalid-endpoint-letters",
  "doenet-i0016": "sequence-invalid-endpoint",
  "doenet-i0017": "angle-too-many-lines",
  "doenet-i0018": "copy-prop-not-found",
  "doenet-i0019": "prefigure-annotations-not-rendered",
  "doenet-i0020": "multiple-annotations-children",
  "doenet-i0021": "attribute-invalid-values",
  "doenet-i0022": "variant-num-to-select-not-non-negative-integer",
  "doenet-i0023": "variant-num-to-select-not-constant-number",
  "doenet-i0024": "variant-with-replacement-not-constant-boolean",
  "doenet-i0025": "variant-select-weight-disables-unique",
  "doenet-i0026": "variant-coprime-undetermined",
  "doenet-i0027": "variant-attribute-not-constant",
  "doenet-i0028": "variant-attribute-not-number",
  "doenet-i0029": "variant-attribute-wrong-type-for-sequence",
  "doenet-i0030": "variant-length-not-integer",
  "doenet-i0031": "variant-sort-not-implemented",
  "doenet-i0032": "variant-exclude-combinations-not-implemented",
  "doenet-i0033": "variant-math-exclude-not-implemented",
  "doenet-i0034": "variant-non-constant-exclude-not-implemented",
  "doenet-i0048": "attribute-value-invalid-using-default",
  "doenet-w0001": "line-points-undetermined-dimensions",
  "doenet-w0002": "line-points-too-few-dimensions",
  "doenet-w0003": "line-points-depend-on-variables",
  "doenet-w0004": "line-equation-invalid-format",
  "doenet-w0005": "ray-overprescribed-through",
  "doenet-w0006": "ray-dimension-mismatch",
  "doenet-w0007": "vector-overprescribed-head",
  "doenet-w0008": "vector-dimension-mismatch",
  "doenet-w0009": "attract-to-without-nearest-point",
  "doenet-w0010": "constrain-to-without-nearest-point",
  "doenet-w0011": "constrain-to-interior-without-nearest-point",
  "doenet-w0012": "choice-input-label-position-ignored",
  "doenet-w0013": "string-children-need-type",
  "doenet-w0014": "invalid-type-defaulting-to-math",
  "doenet-w0015": "string-not-valid-component-to-arrange",
  "doenet-w0016": "invalid-type-defaulting-to-number",
  "doenet-w0017": "invalid-variable-value",
  "doenet-w0018": "side-by-side-absolute-widths",
  "doenet-w0019": "side-by-side-absolute-margins",
  "doenet-w0020": "side-by-side-no-block-child",
  "doenet-w0021": "label-for-ignored-on-graphical",
  "doenet-w0022": "label-for-must-resolve-to-one",
  "doenet-w0023": "label-for-unresolved",
  "doenet-w0024": "label-for-answer-with-authored-inputs",
  "doenet-w0025": "label-for-answer-without-input",
  "doenet-w0026": "label-for-must-reference-input-or-answer",
  "doenet-w0027": "circle-through-points-non-numerical",
  "doenet-w0028": "circle-too-many-through-points",
  "doenet-w0029": "circle-overprescribed-radius-center-points",
  "doenet-w0030": "circle-center-with-multiple-points",
  "doenet-w0031": "circle-radius-too-small",
  "doenet-w0032": "circle-radius-with-many-points",
  "doenet-w0033": "circle-invalid-center-or-through-points",
  "doenet-w0034": "circle-radius-center-with-multiple-points",
  "doenet-w0035": "circle-change-radius-non-numerical",
  "doenet-w0036": "circle-radius-with-points-non-numerical",
  "doenet-w0037": "circle-change-center-non-numerical",
  "doenet-w0038": "function-domain-insufficient-dimensions",
  "doenet-w0039": "function-domain-invalid-format",
  "doenet-w0040": "function-ignoring-non-numerical",
  "doenet-w0041": "function-ignoring-empty",
  "doenet-w0042": "function-points-too-close",
  "doenet-w0043": "target-not-found",
  "doenet-w0044": "target-state-variable-not-found",
  "doenet-w0045": "ode-system-variables-match-independent",
  "doenet-w0046": "ode-system-duplicate-variable-names",
  "doenet-w0047": "ode-system-rhs-function-error",
  "doenet-w0048": "angle-invalid-through-point",
  "doenet-w0049": "parabola-vertex-too-many-points",
  "doenet-w0050": "parabola-too-many-points",
  "doenet-w0051": "select-from-sequence-coprime-not-numbers",
  "doenet-w0052": "select-from-sequence-coprime-with-exclude-combinations",
  "doenet-w0053": "ionic-compound-not-two-ions",
  "doenet-w0054": "ionic-compound-needs-cation-and-anion",
  "doenet-w0055": "intersection-too-many-items",
  "doenet-w0056": "function-iterates-input-output-mismatch",
  "doenet-w0057": "solve-equations-cannot-evaluate",
  "doenet-w0058": "math-operators-operand-number-required",
  "doenet-w0059": "eigen-decomposition-failed",
  "doenet-w0060": "prefigure-x-label-position-unsupported",
  "doenet-w0061": "prefigure-y-label-position-unsupported",
  "doenet-w0062": "prefigure-invalid-axis-bounds",
  "doenet-w0063": "prefigure-invalid-width",
  "doenet-w0064": "prefigure-invalid-aspect-ratio",
  "doenet-w0065": "copy-unrecognized-component-type",
  "doenet-w0066": "data-frame-inconsistent-row-lengths",
  "doenet-w0067": "data-frame-duplicate-column-names",
  "doenet-w0068": "data-frame-missing-column-name",
  "doenet-w0069": "answer-award-depends-on-own-response",
  "doenet-w0070": "answer-max-num-attempts-in-section-wide-check-work",
  "doenet-w0071": "answer-attributes-need-symbolic-equality",
  "doenet-w0072": "collect-no-source",
  "doenet-w0073": "collect-invalid-component-type",
  "doenet-w0074": "module-attribute-child-needs-name",
  "doenet-w0075": "module-attribute-name-already-defined",
  "doenet-w0076": "pretzel-problem-needs-statement-and-answer",
  "doenet-w0077": "attribute-must-be-references",
  "doenet-w0078": "answer-invalid-type",
  "doenet-w0079": "conditional-content-condition-ignored",
  "doenet-w0080": "slider-markers-type-mismatch",
  "doenet-w0081": "nested-section-wide-check-work-max-num-attempts",
  "doenet-w0082": "math-input-invalid-function-names",
  "doenet-w0083": "section-multiple-style-palettes",
  "doenet-w0084": "prefigure-descendant-unsupported",
  "doenet-w0085": "prefigure-descendant-invalid-geometry",
  "doenet-w0086": "prefigure-curve-label-omitted",
  "doenet-w0087": "prefigure-curve-unsupported-definition-type",
  "doenet-w0088": "prefigure-region-flip-functions-unsupported",
  "doenet-w0089": "prefigure-region-non-formula-child",
  "doenet-w0090": "prefigure-label-position-unsupported",
  "doenet-w0091": "prefigure-fill-style-unsupported",
  "doenet-w0092": "prefigure-line-style-unknown",
  "doenet-w0093": "prefigure-marker-style-mapped-to-diamond",
  "doenet-w0094": "prefigure-marker-style-unsupported",
  "doenet-w0095": "annotation-ref-unresolvable",
  "doenet-w0096": "annotation-ref-multiple-targets",
  "doenet-w0097": "annotation-ref-outside-graph",
  "doenet-w0098": "annotation-ref-unsupported-target",
  "doenet-w0099": "annotation-text-missing",
  "doenet-w0100": "reference-index-unavailable",
  "doenet-w0102": "component-action-unavailable",
  "doenet-w0104": "reference-no-referent",
  "doenet-w0105": "reference-multiple-referents",
  "doenet-w0106": "children-invalid-attribute-format",
  "doenet-w0107": "children-invalid",
  "doenet-w0108": "deprecated-attribute-renamed",
  "doenet-w0109": "deprecated-attribute-renamed-conflict",
  "doenet-w0110": "deprecated-attribute-ignored",
  "doenet-w0111": "pluralize-english-only",
  "doenet-w0112": "schema-element-unrecognized",
  "doenet-w0113": "schema-element-not-allowed-at-root",
  "doenet-w0114": "schema-element-not-allowed-inside",
  "doenet-w0115": "schema-attribute-unrecognized",
  "doenet-w0116": "schema-attribute-value-not-allowed",
  "doenet-w0117": "matches-pattern-parameter-not-in-pattern",
  "doenet-w0118": "prefigure-grid-spacing-too-fine",
  "doenet-w0119": "graph-grid-invalid",
  "doenet-w0120": "deprecated-attribute-to-child",
  "doenet-w0121": "deprecated-attribute-value-renamed",
  "doenet-w0122": "field-function-wrong-num-outputs",
  "doenet-w0123": "field-function-attribute-ignored-with-child",
  "doenet-w0124": "field-variables-ignored",
  "doenet-e0001": "pretzel-circuit-first-problem-distractor",
  "doenet-e0002": "component-type-invalid",
  "doenet-e0003": "attribute-repeated",
  "doenet-e0004": "attribute-invalid-for-component",
  "doenet-e0005": "composite-circular-dependency",
  "doenet-e0006": "doenetml-version-not-found",
  "doenet-e0007": "parse-invalid-doenetml",
  "doenet-e0008": "parse-tag-missing-close-tag",
  "doenet-e0009": "parse-tag-error",
  "doenet-e0010": "parse-attribute-missing-value",
  "doenet-e0011": "parse-attribute-invalid",
  "doenet-e0012": "parse-attribute-value-invalid",
  "doenet-e0013": "parse-attribute-value-quote-mismatch",
  "doenet-e0014": "parse-open-tag-name-missing",
  "doenet-e0015": "parse-tag-not-closed",
  "doenet-e0016": "parse-self-closing-tag-name-missing",
  "doenet-e0017": "parse-self-closing-tag-not-closed",
  "doenet-e0018": "parse-tag-invalid-attributes",
  "doenet-e0019": "parse-close-tag-name-missing",
  "doenet-e0020": "parse-attribute-value-unquoted",
  "doenet-e0021": "parse-close-tag-without-open-tag",
  "doenet-e0022": "parse-close-tag-mismatched",
  "doenet-e0023": "parser-node-unconvertible",
  "doenet-e0024": "component-name-invalid-start",
  "doenet-e0025": "name-attribute-invalid",
  "doenet-e0026": "answer-video-watched-missing-video",
  "doenet-e0027": "answer-video-watched-video-not-reference",
  "doenet-e0028": "answer-name-not-single-text",
  "doenet-e0029": "external-doenetml-recursion-limit",
  "doenet-e0030": "external-doenetml-unavailable",
  "doenet-e0031": "external-doenetml-type-mismatch",
  "doenet-e0032": "select-variant-name-option-count-mismatch",
  "doenet-e0033": "select-variant-name-without-options",
  "doenet-e0034": "select-variant-name-not-possible",
  "doenet-e0035": "select-too-few-options",
  "doenet-e0036": "select-from-sequence-too-few-values",
  "doenet-e0037": "select-from-sequence-indices-count-mismatch",
  "doenet-e0038": "select-from-sequence-indices-not-integers",
  "doenet-e0039": "select-from-sequence-index-excluded",
  "doenet-e0040": "select-from-sequence-indices-excluded-combination",
  "doenet-e0041": "select-from-sequence-coprime-not-positive-integers",
  "doenet-e0042": "select-from-sequence-coprime-common-factor",
  "doenet-e0043": "select-from-sequence-coprime-single-number",
  "doenet-e0044": "select-from-sequence-excluded-too-many-combinations",
  "doenet-e0045": "select-from-sequence-coprime-none-found",
  "doenet-e0046": "select-from-sequence-too-few-unique-values",
  "doenet-e0047": "select-prime-numbers-too-few-values",
  "doenet-e0048": "select-prime-numbers-values-count-mismatch",
  "doenet-e0049": "select-prime-numbers-values-not-prime",
  "doenet-e0050": "select-prime-numbers-values-excluded-combination",
  "doenet-e0051": "select-prime-numbers-excluded-too-many-combinations",
  "doenet-e0052": "select-random-combination-fluke",
  "doenet-e0053": "select-random-value-fluke",
  "doenet-a0001": "accessibility-short-description-or-decorative",
  "doenet-a0002": "accessibility-video-short-description",
  "doenet-a0003": "accessibility-input-short-description-or-label",
  "doenet-a0004": "accessibility-answer-input-short-description-or-label",
  "doenet-a0005": "accessibility-short-description-contains-math",
  "doenet-a0006": "accessibility-section-title-insufficient-contrast",
  "doenet-a0007": "style-definition-insufficient-contrast",
  "doenet-a0008": "style-definition-dark-mode-text-background-contrast",
  "doenet-a0009": "style-definition-dark-mode-text-canvas-contrast"
};
function isDiagnosticCode(code) {
  return Object.prototype.hasOwnProperty.call(DIAGNOSTIC_CODES, code);
}
function asListArg(value) {
  if (Array.isArray(value)) {
    return { list: value };
  }
  if (typeof value === "object" && value !== null && "list" in value) {
    const candidate = value;
    return Array.isArray(candidate.list) ? candidate : void 0;
  }
  return void 0;
}
function lowerArgs(args, locale) {
  if (args == null) {
    return void 0;
  }
  const lowered = {};
  for (const [name, value] of Object.entries(args)) {
    if (typeof value === "string" || typeof value === "number") {
      lowered[name] = value;
      continue;
    }
    const listArg = asListArg(value);
    if (listArg === void 0) {
      lowered[name] = String(value);
      continue;
    }
    lowered[name] = listFormatFor(locale, listArg.type).format(
      listArg.list.map((item) => String(item))
    );
    lowered[`${name}Count`] = listArg.list.length;
  }
  return lowered;
}
function createDiagnosticFormatter(translate, locale) {
  return (diagnostic) => {
    const { code } = diagnostic;
    if (code == null || !isDiagnosticCode(code)) {
      return diagnostic.message;
    }
    const key = DIAGNOSTIC_CODES[code];
    return translate(
      key,
      lowerArgs(diagnostic.args, translate.localeOf?.(key) ?? locale),
      diagnostic.message
    );
  };
}
let enTranslator;
function formatEnglishDiagnostic(code, args) {
  if (!isDiagnosticCode(code)) {
    return String(code);
  }
  const key = DIAGNOSTIC_CODES[code];
  enTranslator ??= createTranslator([], {});
  return enTranslator(key, lowerArgs(args, DEFAULT_LOCALE), code);
}
const MESSAGE_KEYS = [
  "answer-checking",
  "answer-submitting",
  "answer-checking-status",
  "answer-submitting-status",
  "answer-correct",
  "answer-incorrect",
  "answer-response-saved",
  "answer-percent-credit",
  "answer-percent-correct",
  "answer-percent-short",
  "max-credit-available",
  "attempts-remaining",
  "validation-correct",
  "validation-incorrect",
  "validation-partially-correct",
  "answer-show-responses",
  "feedback-heading",
  "collapsible-click-to-open",
  "collapsible-click-to-close",
  "collapsible-initializing",
  "footnote-show",
  "footnote-hide",
  "description-more-information",
  "slider-previous",
  "slider-next",
  "keyboard-open",
  "keyboard-close",
  "choice-input-remove-choice",
  "matrix-remove-row",
  "matrix-add-row",
  "matrix-remove-column",
  "matrix-add-column",
  "subset-add-remove-points",
  "subset-toggle-points-intervals",
  "subset-move-points",
  "subset-clear",
  "orbital-add-row",
  "orbital-remove-row",
  "orbital-add-box",
  "orbital-remove-box",
  "orbital-add-up-arrow",
  "orbital-add-down-arrow",
  "orbital-remove-arrow",
  "orbital-row-label",
  "pretzel-answer",
  "summary-statistics-caption",
  "math-input-preview-region",
  "math-input-preview",
  "math-input-invalid-expression",
  "viewer-initializing",
  "error-heading",
  "error-found-at",
  "document-contains-errors",
  "diagnostic-heading-error",
  "diagnostic-heading-warning",
  "diagnostic-heading-information",
  "diagnostic-heading-hint",
  "accessibility-heading-level-1",
  "accessibility-heading-level-2",
  "something-went-wrong",
  "renderer-load-failed",
  "core-start-failed",
  "core-start-failed-busy",
  "color.black",
  "color.white",
  "color.gray",
  "color.red",
  "color.orange",
  "color.yellow",
  "color.green",
  "color.cyan",
  "color.blue",
  "color.purple",
  "color.pink",
  "color.brown",
  "line-width.thick",
  "line-width.thin",
  "line-style.dashed",
  "line-style.dotted",
  "fill-style.horizontal",
  "fill-style.vertical",
  "fill-style.diagonal",
  "fill-style.backdiagonal",
  "fill-style.dots",
  "fill-style.diamonds",
  "noun.line",
  "noun.line-segment",
  "noun.ray",
  "noun.vector",
  "noun.curve",
  "noun.function",
  "noun.slope-field",
  "noun.vector-field",
  "noun.parabola",
  "noun.polyline",
  "noun.polygon",
  "noun.triangle",
  "noun.rectangle",
  "noun.circle",
  "noun.region",
  "noun.point",
  "noun.square",
  "noun.diamond",
  "noun.cross",
  "noun.plus",
  "noun-regular-polygon",
  "noun-gender",
  "style-stroke",
  "style-with-noun",
  "style-filled-word",
  "style-filled",
  "style-filled-with-noun",
  "style-border-clause",
  "style-fill",
  "style-unfilled",
  "style-text",
  "style-background-none",
  "boolean-true",
  "boolean-false",
  "answer-submit-label",
  "answer-submit-label-no-correctness",
  "section-name.activity",
  "section-name.aside",
  "section-name.cascade",
  "section-name.definition",
  "section-name.example",
  "section-name.exercise",
  "section-name.exercises",
  "section-name.given-answer",
  "section-name.note",
  "section-name.objectives",
  "section-name.paragraphs",
  "section-name.part",
  "section-name.problem",
  "section-name.problems",
  "section-name.proof",
  "section-name.question",
  "section-name.section",
  "section-name.solution",
  "section-name.task",
  "section-name.theorem",
  "section-title-prefix",
  "hint-title",
  "table-name",
  "figure-name",
  "paginator-previous",
  "paginator-next",
  "paginator-page",
  "paginator-page-status",
  "piecewise-condition-or",
  "piecewise-condition-if",
  "piecewise-condition-otherwise",
  "element-name.h",
  "element-name.he",
  "element-name.li",
  "element-name.be",
  "element-name.b",
  "element-name.c",
  "element-name.n",
  "element-name.o",
  "element-name.f",
  "element-name.ne",
  "element-name.na",
  "element-name.mg",
  "element-name.al",
  "element-name.si",
  "element-name.p",
  "element-name.s",
  "element-name.cl",
  "element-name.ar",
  "element-name.k",
  "element-name.ca",
  "element-name.sc",
  "element-name.ti",
  "element-name.v",
  "element-name.cr",
  "element-name.mn",
  "element-name.fe",
  "element-name.co",
  "element-name.ni",
  "element-name.cu",
  "element-name.zn",
  "element-name.ga",
  "element-name.ge",
  "element-name.as",
  "element-name.se",
  "element-name.br",
  "element-name.kr",
  "element-name.rb",
  "element-name.sr",
  "element-name.y",
  "element-name.zr",
  "element-name.nb",
  "element-name.mo",
  "element-name.tc",
  "element-name.ru",
  "element-name.rh",
  "element-name.pd",
  "element-name.ag",
  "element-name.cd",
  "element-name.in",
  "element-name.sn",
  "element-name.sb",
  "element-name.te",
  "element-name.i",
  "element-name.xe",
  "element-name.cs",
  "element-name.ba",
  "element-name.la",
  "element-name.ce",
  "element-name.pr",
  "element-name.nd",
  "element-name.pm",
  "element-name.sm",
  "element-name.eu",
  "element-name.gd",
  "element-name.tb",
  "element-name.dy",
  "element-name.ho",
  "element-name.er",
  "element-name.tm",
  "element-name.yb",
  "element-name.lu",
  "element-name.hf",
  "element-name.ta",
  "element-name.w",
  "element-name.re",
  "element-name.os",
  "element-name.ir",
  "element-name.pt",
  "element-name.au",
  "element-name.hg",
  "element-name.tl",
  "element-name.pb",
  "element-name.bi",
  "element-name.po",
  "element-name.at",
  "element-name.rn",
  "element-name.fr",
  "element-name.ra",
  "element-name.ac",
  "element-name.th",
  "element-name.pa",
  "element-name.u",
  "element-name.np",
  "element-name.pu",
  "element-name.am",
  "element-name.cm",
  "element-name.bk",
  "element-name.cf",
  "element-name.es",
  "element-name.fm",
  "element-name.md",
  "element-name.no",
  "element-name.lr",
  "element-name.rf",
  "element-name.db",
  "element-name.sg",
  "element-name.bh",
  "element-name.hs",
  "element-name.mt",
  "element-name.ds",
  "element-name.rg",
  "element-name.cn",
  "element-name.nh",
  "element-name.fl",
  "element-name.mc",
  "element-name.lv",
  "element-name.ts",
  "element-name.og",
  "element-anion-name.h",
  "element-anion-name.c",
  "element-anion-name.n",
  "element-anion-name.o",
  "element-anion-name.f",
  "element-anion-name.p",
  "element-anion-name.s",
  "element-anion-name.cl",
  "element-anion-name.br",
  "element-anion-name.i",
  "element-anion-name.at",
  "element-anion-name.ts",
  "ion-name-oxidation-state",
  "chemistry-invalid-symbol",
  "chemistry-invalid-ionic-compound",
  "line-segment-attributes-ignored-with-endpoints",
  "line-segment-attributes-ignored-with-endpoint-and-midpoint",
  "line-segment-midpoint-offset-without-midpoint",
  "line-points-undetermined-dimensions",
  "line-points-too-few-dimensions",
  "line-points-depend-on-variables",
  "line-equation-invalid-format",
  "ray-overprescribed-through",
  "ray-dimension-mismatch",
  "vector-overprescribed-head",
  "vector-dimension-mismatch",
  "attract-to-without-nearest-point",
  "constrain-to-without-nearest-point",
  "constrain-to-interior-without-nearest-point",
  "choice-input-label-position-ignored",
  "choice-input-indices-count-mismatch",
  "pretzel-indices-count-mismatch",
  "shuffle-indices-count-mismatch",
  "indices-ignored-out-of-range",
  "pretzel-indices-repeated",
  "pretzel-circuit-first-index",
  "string-children-need-type",
  "invalid-type-defaulting-to-math",
  "string-not-valid-component-to-arrange",
  "invalid-type-defaulting-to-number",
  "invalid-variable-value",
  "variant-index-must-be-number",
  "variant-index-must-be-integer",
  "side-by-side-absolute-widths",
  "side-by-side-absolute-margins",
  "side-by-side-no-block-child",
  "label-for-ignored-on-graphical",
  "label-for-must-resolve-to-one",
  "label-for-unresolved",
  "label-for-answer-with-authored-inputs",
  "label-for-answer-without-input",
  "label-for-must-reference-input-or-answer",
  "accessibility-short-description-or-decorative",
  "accessibility-video-short-description",
  "accessibility-input-short-description-or-label",
  "accessibility-answer-input-short-description-or-label",
  "accessibility-short-description-contains-math",
  "accessibility-section-title-insufficient-contrast",
  "circle-through-points-non-numerical",
  "circle-too-many-through-points",
  "circle-overprescribed-radius-center-points",
  "circle-center-with-multiple-points",
  "circle-radius-too-small",
  "circle-radius-with-many-points",
  "circle-invalid-center-or-through-points",
  "circle-radius-center-with-multiple-points",
  "circle-change-radius-non-numerical",
  "circle-radius-with-points-non-numerical",
  "circle-change-center-non-numerical",
  "function-domain-insufficient-dimensions",
  "function-domain-invalid-format",
  "function-ignoring-non-numerical",
  "function-ignoring-empty",
  "function-points-too-close",
  "function-iterates-input-output-mismatch",
  "sequence-invalid-length",
  "sequence-invalid-step",
  "sequence-invalid-endpoint-number",
  "sequence-invalid-endpoint-letters",
  "sequence-invalid-endpoint",
  "select-from-sequence-coprime-not-numbers",
  "select-from-sequence-coprime-with-exclude-combinations",
  "target-not-found",
  "target-state-variable-not-found",
  "ode-system-variables-match-independent",
  "ode-system-duplicate-variable-names",
  "ode-system-rhs-function-error",
  "angle-too-many-lines",
  "angle-invalid-through-point",
  "parabola-vertex-too-many-points",
  "parabola-too-many-points",
  "intersection-too-many-items",
  "ionic-compound-not-two-ions",
  "ionic-compound-needs-cation-and-anion",
  "solve-equations-cannot-evaluate",
  "math-operators-operand-number-required",
  "eigen-decomposition-failed",
  "matches-pattern-parameter-not-in-pattern",
  "graph-grid-invalid",
  "field-function-wrong-num-outputs",
  "field-function-attribute-ignored-with-child",
  "field-variables-ignored",
  "prefigure-x-label-position-unsupported",
  "prefigure-y-label-position-unsupported",
  "prefigure-invalid-axis-bounds",
  "prefigure-invalid-width",
  "prefigure-invalid-aspect-ratio",
  "prefigure-grid-spacing-too-fine",
  "prefigure-annotations-not-rendered",
  "multiple-annotations-children",
  "copy-unrecognized-component-type",
  "copy-prop-not-found",
  "collect-no-source",
  "collect-invalid-component-type",
  "reference-index-unavailable",
  "component-action-unavailable",
  "data-frame-inconsistent-row-lengths",
  "data-frame-duplicate-column-names",
  "data-frame-missing-column-name",
  "answer-award-depends-on-own-response",
  "answer-max-num-attempts-in-section-wide-check-work",
  "nested-section-wide-check-work-max-num-attempts",
  "answer-attributes-need-symbolic-equality",
  "answer-invalid-type",
  "module-attribute-child-needs-name",
  "module-attribute-name-already-defined",
  "conditional-content-condition-ignored",
  "slider-markers-type-mismatch",
  "pretzel-problem-needs-statement-and-answer",
  "pretzel-circuit-first-problem-distractor",
  "attribute-invalid-values",
  "attribute-must-be-references",
  "math-input-invalid-function-names",
  "component-type-invalid",
  "attribute-repeated",
  "attribute-invalid-for-component",
  "style-definition-insufficient-contrast",
  "style-definition-dark-mode-text-background-contrast",
  "style-definition-dark-mode-text-canvas-contrast",
  "section-multiple-style-palettes",
  "variant-num-to-select-not-non-negative-integer",
  "variant-num-to-select-not-constant-number",
  "variant-with-replacement-not-constant-boolean",
  "variant-select-weight-disables-unique",
  "variant-coprime-undetermined",
  "variant-attribute-not-constant",
  "variant-attribute-not-number",
  "variant-attribute-wrong-type-for-sequence",
  "variant-length-not-integer",
  "variant-sort-not-implemented",
  "variant-exclude-combinations-not-implemented",
  "variant-math-exclude-not-implemented",
  "variant-non-constant-exclude-not-implemented",
  "prefigure-descendant-unsupported",
  "prefigure-descendant-invalid-geometry",
  "prefigure-curve-label-omitted",
  "prefigure-curve-unsupported-definition-type",
  "prefigure-region-flip-functions-unsupported",
  "prefigure-region-non-formula-child",
  "prefigure-label-position-unsupported",
  "prefigure-fill-style-unsupported",
  "prefigure-line-style-unknown",
  "prefigure-marker-style-mapped-to-diamond",
  "prefigure-marker-style-unsupported",
  "annotation-ref-unresolvable",
  "annotation-ref-multiple-targets",
  "annotation-ref-outside-graph",
  "annotation-ref-unsupported-target",
  "annotation-text-missing",
  "composite-circular-dependency",
  "reference-no-referent",
  "reference-multiple-referents",
  "children-invalid-attribute-format",
  "children-invalid",
  "attribute-value-invalid-using-default",
  "doenetml-version-not-found",
  "parse-invalid-doenetml",
  "parse-tag-missing-close-tag",
  "parse-tag-error",
  "parse-attribute-missing-value",
  "parse-attribute-invalid",
  "parse-attribute-value-invalid",
  "parse-attribute-value-quote-mismatch",
  "parse-open-tag-name-missing",
  "parse-tag-not-closed",
  "parse-self-closing-tag-name-missing",
  "parse-self-closing-tag-not-closed",
  "parse-tag-invalid-attributes",
  "parse-close-tag-name-missing",
  "parse-attribute-value-unquoted",
  "parse-close-tag-without-open-tag",
  "parse-close-tag-mismatched",
  "parser-node-unconvertible",
  "name-attribute-invalid",
  "component-name-invalid-start",
  "answer-video-watched-missing-video",
  "answer-video-watched-video-not-reference",
  "answer-name-not-single-text",
  "external-doenetml-recursion-limit",
  "external-doenetml-unavailable",
  "external-doenetml-type-mismatch",
  "deprecated-attribute-renamed",
  "deprecated-attribute-renamed-conflict",
  "deprecated-attribute-ignored",
  "deprecated-attribute-to-child",
  "deprecated-attribute-value-renamed",
  "pluralize-english-only",
  "schema-element-unrecognized",
  "schema-element-not-allowed-at-root",
  "schema-element-not-allowed-inside",
  "schema-attribute-unrecognized",
  "schema-attribute-value-not-allowed",
  "select-variant-name-option-count-mismatch",
  "select-variant-name-without-options",
  "select-variant-name-not-possible",
  "select-too-few-options",
  "select-from-sequence-too-few-values",
  "select-from-sequence-indices-count-mismatch",
  "select-from-sequence-indices-not-integers",
  "select-from-sequence-index-excluded",
  "select-from-sequence-indices-excluded-combination",
  "select-from-sequence-coprime-not-positive-integers",
  "select-from-sequence-coprime-common-factor",
  "select-from-sequence-coprime-single-number",
  "select-from-sequence-excluded-too-many-combinations",
  "select-from-sequence-coprime-none-found",
  "select-from-sequence-too-few-unique-values",
  "select-prime-numbers-too-few-values",
  "select-prime-numbers-values-count-mismatch",
  "select-prime-numbers-values-not-prime",
  "select-prime-numbers-values-excluded-combination",
  "select-prime-numbers-excluded-too-many-combinations",
  "select-random-combination-fluke",
  "select-random-value-fluke",
  "editor-update-viewer",
  "editor-update-viewer-title",
  "editor-variant",
  "editor-variant-filter",
  "editor-variant-next",
  "editor-variant-previous",
  "editor-accessibility-title",
  "editor-accessibility-label",
  "editor-accessibility-badge",
  "editor-version-title",
  "editor-tab-help",
  "editor-tab-help-short",
  "editor-tab-errors",
  "editor-tab-warnings",
  "editor-tab-info",
  "editor-tab-accessibility",
  "editor-tab-responses",
  "editor-tab-with-count",
  "editor-options",
  "editor-format-as-doenetml",
  "editor-format-as-xml",
  "editor-diagnostic-line",
  "editor-no-errors",
  "editor-no-warnings",
  "editor-no-info",
  "editor-show-info-annotations",
  "editor-show-accessibility-annotations",
  "editor-accessibility-learn-more",
  "editor-accessibility-violations-heading",
  "editor-accessibility-other-heading",
  "editor-none-found",
  "editor-no-responses",
  "editor-response-answer-id",
  "editor-response-response",
  "editor-response-credit",
  "editor-response-submitted",
  "help-placeholder",
  "help-unsupported-ref-chain",
  "help-unresolved-ref",
  "help-learn-about-references",
  "help-reference-page",
  "help-suggestions-header",
  "help-suggestions-footer",
  "help-name-summary",
  "help-ref-is-reference",
  "help-ref-derived-from",
  "help-property-is-reference",
  "help-kind-attribute",
  "help-kind-snippet",
  "help-kind-array-entry",
  "help-default",
  "help-active-default",
  "help-style-number-annotation",
  "help-allowed-values",
  "help-suggested-values",
  "help-inserts",
  "help-coordinates",
  "help-type",
  "help-resolved-style",
  "help-resolved-function-names",
  "help-reset-list",
  "help-added-on-input",
  "help-removed-on-input",
  "help-reset-overrides"
];
export {
  BUNDLED_LOCALES,
  CATALOG_NAMESPACES,
  CHROME_NAMESPACES,
  DEFAULT_LOCALE,
  DEFAULT_LOCALE_DATA,
  DIAGNOSTIC_CODES,
  EN_CATALOGS,
  EN_CATALOG_SOURCE,
  EN_CHROME_TRANSLATOR,
  MATH_NOTATION_LOCALE,
  MESSAGE_KEYS,
  PSEUDO_LOCALE,
  PSEUDO_RTL_BRACKETS,
  PSEUDO_RTL_LOCALE,
  SUPPORTED_LOCALES,
  WORKER_NAMESPACES,
  asFallbackTranslator,
  combineCatalogs,
  createChromeTranslator,
  createDiagnosticFormatter,
  createTranslator,
  createTranslatorFromLocaleData,
  directionOf,
  fetchLocaleLoaders,
  formatDecimalString,
  formatEnglishDiagnostic,
  intlLocale,
  isDiagnosticCode,
  listFormatFor,
  loadLocaleResources,
  loadLocaleResourcesFor,
  localeResourceKey,
  negotiateLocales,
  normalizeLocaleTag,
  pseudoLocalize,
  reachableCatalogLocales,
  resolveDocumentLocale,
  resolveUiLocale,
  setLocaleLoaders,
  stripBidiIsolates
};
//# sourceMappingURL=index.js.map
